/*---------------------------------------------------------------------------------------------------*/
/* Filename: auth.service.ts                                                                         */
/* Description: This file is for giving permission to the user to access certain resource in the app */
/* Created: 13/03/2020                                                                               */
/* Copyright (c) 2023 Lottosocial by Hatchster                                                       */
/*------------------------------------------------ --------------------------------------------------*/
import { HttpClient } from "@angular/common/http";
import { inject, Injectable, Injector, signal } from "@angular/core";
import { AngularFireAuth } from "@angular/fire/compat/auth";
import { AngularFireStorage } from "@angular/fire/compat/storage";
import { collectionData, docData, docSnapshots } from '@angular/fire/firestore';
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute, Data, Router } from "@angular/router";
import { canonicalizeProsubQueryParams } from "./prosub-query-params";
import { Device, DeviceInfo } from "@capacitor/device";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { AuthCredentials } from "@interfaces/auth-credentials.i";
import { CreateUser } from "@interfaces/create-user.i";
import { Language } from "@interfaces/translation-interface";
import { User } from "@interfaces/user.i";
import { FirebaseError } from "firebase/app"; // Import FirebaseError for error type checking

import { Firestore } from "@angular/fire/firestore";
import {
  browserLocalPersistence,
  EmailAuthProvider,
  FacebookAuthProvider,
  getAuth,
  GoogleAuthProvider,
  linkWithCredential,
  linkWithPopup,
  reauthenticateWithCredential,
  setPersistence,
  signInAnonymously,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  unlink,
  updatePassword,
} from "firebase/auth";
import { collection, CollectionReference, doc, DocumentData, DocumentReference, getDoc, getDocs, limit, onSnapshot, orderBy, query, QuerySnapshot, Timestamp, where } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  BehaviorSubject,
  combineLatest,
  defer,
  EMPTY,
  firstValueFrom,
  forkJoin,
  from,
  lastValueFrom,
  Observable,
  of,
  race,
  Subject,
  Subscription,
  throwError,
  timer,
} from "rxjs";
import {
  catchError,
  concatMap,
  delay,
  delayWhen,
  filter,
  finalize,
  first,
  map,
  retry,
  retryWhen,
  scan,
  shareReplay,
  switchMap,
  take,
  takeUntil,
  tap,
  timeout,
  withLatestFrom
} from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AppCheckService } from "../app-check.service";
import { BaseService } from "../app.base.service";
import { CommonVariable, LocalStorageKeys } from "../app.constants";
import { ApiLoaderComponent } from "../components/api-loader/api-loader.component";
import {
  didA2hsLoginContextChange,
  resolveOperatingSystem,
} from "../components/add-home-screen-banner/add-home-screen.helpers";
import { MyToastbarComponent } from "../components/my-toastbar/my-toastbar.component";
import { RequestProfileService } from "../services/RequestBodyService/RequestProfile/request-profile.service";
import { RequestUserService } from "../services/RequestBodyService/RequestUser/request-user.service";
import { ClevertapService } from "../services/clevertap.service";
import { CommonService } from "../services/common/common.service";
import { FirestoreDataService } from "../services/firestore-data.service";
import { GameBalanceService } from "../services/game-balance.service";
import { HomeDataService } from "../services/home-data.service";
import { LoginDataResolverService } from "../services/login-data-resolver.service";
import { MarketingCampaignsService } from "../services/marketing-campaigns.service";
import { NotificationsService } from "../services/notifications.service";
import { OneSignalService } from "../services/one-signal.service";
import { PaymentDataService } from "../services/payment-data.service";
import { SalesJourneyIdentityService } from "../services/sales-journey-identity.service";
import { UserManagementService } from "../services/user-management.service";
import { CatchErrorService } from "./catch-error.service";
import { formatDisplayCurrencyAmount } from "./currency-display.util";
import { LoadingService } from "./loading.service";
import { NetworkStatusService } from "./network-status.service";
import { isMobile, UtilityService } from "./utility.service";
import { AppVersionService } from "../services/appVersions/app-version.service";
import { ToastFacadeService } from "./toast-facade.service";
import { handlePermissionDeniedError, detectPermissionDeniedError } from "./firestore-auth-guard";
import { AuthStateService } from "./auth-state.service";
import { MixpanelService } from "src/app/services/mixpanel.service";
import { TranslateService } from "@ngx-translate/core";
import { ClarityService } from "../services/clarity.service";
declare const GTM_Segment: any;
declare var window;
const zero = 0;
@Injectable({
  providedIn: "root",
})
export class AuthService extends BaseService {
  private firestore = inject(Firestore);
  private angularFireAuth = inject(AngularFireAuth);
  private loadingService = inject(LoadingService);
  private router = inject(Router);
  private fbStorage = inject(AngularFireStorage);
  private snackBar = inject(MatSnackBar);
  private notificationsService = inject(NotificationsService);
  private catchService = inject(CatchErrorService);
  private route = inject(ActivatedRoute);
  private oneSignalService = inject(OneSignalService);
  private clevertapService = inject(ClevertapService);
  private RequestProfileService = inject(RequestProfileService);
  private RequestUserService = inject(RequestUserService);
  private http = inject(HttpClient);
  private firestoreDataServices = inject(FirestoreDataService);
  private userManagementService = inject(UserManagementService);
  private salesJourneyIdentityService = inject(SalesJourneyIdentityService);
  private homeDataService = inject(HomeDataService);
  private paymentDataService = inject(PaymentDataService);
  private gameBalanceService = inject(GameBalanceService);
  private utilityService = inject(UtilityService);
  private marketingCampaignsService = inject(MarketingCampaignsService);
  private networkStatusService = inject(NetworkStatusService);
  private matDialog = inject(MatDialog);
  private commonService = inject(CommonService);

  private mixpanelService = inject(MixpanelService)
  private authStateService = inject(AuthStateService)
  private readonly checkoutFlowUserIdSubject$ = new BehaviorSubject<string>("");
  readonly checkoutFlowUserId$ = this.checkoutFlowUserIdSubject$.asObservable();

  setCheckoutFlowUserId(userId: string): void {
    this.checkoutFlowUserIdSubject$.next(userId);
  }

  warmRegistrationIpCache(): void {
    this.userManagementService.warmRegistrationIpCache();
  }

  public resetRegistrationIdentityCache(): void {
    this.salesJourneyIdentityService.resetRegistrationIdentity();
    this.callSigUpForSalesResponse$?.next(null);
  }

  public captureRegistrationIdentityEpoch(): number {
    return this.salesJourneyIdentityService.captureRegistrationIdentityEpoch();
  }

  public applyRegistrationIdentityResponse(response: unknown, epoch: number): boolean {
    const applied = this.salesJourneyIdentityService.setRegistrationIdentity(response, epoch);
    if (applied) {
      this.callSigUpForSalesResponse$.next(response);
    }
    return applied;
  }

  /**
   * Emits `true` once a confirmed, non-anonymous Firebase user is present and
   * the auth state has been established.  Resets to `false` on logout.
   * Consumers can gate Firestore operations behind this to avoid
   * permission-denied errors caused by the auth/Firestore race on iOS/Safari.
   */
  public authReady$ = new BehaviorSubject<boolean>(false);

  private clarityService = inject(ClarityService);
  push_pref  = signal(true)
  freebieVoucherAssigned: boolean;
  private anonymousLoginInProgress = false;

  updateMainPageSubscription: Subscription;
  isPayPalSignal = signal(false);
  private EXPECTED_OS_VERSION = "17.1";
  tokenRefreshTimeout: ReturnType<typeof setTimeout> | null = null;
  segmentRequestData$ = new BehaviorSubject<any>({});
  userSubs: any[] = [];
  referralData;
  salesLink = new BehaviorSubject(null);
  customTokenInterval: any;
  selectedlanguage$ = new BehaviorSubject(
    (typeof localStorage !== 'undefined' && localStorage !== null && localStorage.getItem("language_code")) || ""
  );
  selectedlanguageObservable$ = this.selectedlanguage$.asObservable();
  gameEvent$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private _showTapAnimation = signal<boolean>(false);
  readonly showTapAnimation = this._showTapAnimation;
  setShowTapAnimation(value: boolean): void {
    this._showTapAnimation.set(value);
  }
  private _showMenuNarratorTip = signal<boolean>(false);
  readonly showMenuNarratorTip = this._showMenuNarratorTip;
  setShowMenuNarratorTip(value: boolean): void {
    this._showMenuNarratorTip.set(value);
  }
  disableNavigation$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  
  // Auth readiness tracking to prevent Firestore ops before token is ready.
  // Public so services can gate Firestore listeners behind token availability.
  public readonly tokenRefreshInProgress$ = new BehaviorSubject<boolean>(false);
  isLanguageChangeTrigger$ = new BehaviorSubject(false);
  appUpdateOnLoginTrigger$ = new BehaviorSubject(false);
  isinboxTrigger$ = new BehaviorSubject("");
  userDisabled$: Data;
  redirectTo: any;
  prosub: any;
  ipBasedCurrency: string;
  preferredLanguage: Language;
  translatedToastMsg: any;
  selectedLanguage: string;
  public userSyndicateRequest$ = new BehaviorSubject(5);
  currencyNodeDvData: any = {};
  callSigUpForSalesResponse$ = new BehaviorSubject(null);
  /** Non-PII timing context for sales new-registration stages (HATDEV-99788). */
  private regFlowTiming: { correlationId: string; prosubId: string; startedAt: number } | null = null;
  latestOngoingSyndicate$ = new BehaviorSubject(null);
  flashSaleEnabled = false;
  private destroy$ = new Subject<void>();
  public userData: Data;
  public onBoardState: string;
  public pwaSegmentStatus: string;
  public userId: string;
  public syndicateLoadedData: Data;
  private activeSyndicateName: string | null = null;
  public ongoingSyndicate = false;
  public checkWinning = false;
  public active_jackpot = false;
  public currencySymbol: string = "£";
  public preferredCurrency: string;
  public userBalance: Data;
  public isBottomSheetOpen = false;
  syndicateData: Data | null = null; // Assuming Data is the type of syndicateData
  userSyndicateList: Data[] = [];
  public CloseText: string;
  public currencySymbols: string = CommonVariable.defaultCurrencySymbol;
  public userMSN$ = new BehaviorSubject<string>("");
  private gamefeedUnsubscribeInbox$: Subject<void> = new Subject<void>();
  public static readonly FIREBASE_REGION = "europe-west3";
  public customCheck: boolean = true;
  public autoUserId: string;
  public fbTokenKey = "fbToken";
  public userIdKey = "userId";
  tokenValidaterSub: Subscription;
  truevoDetails$: BehaviorSubject<Data> = new BehaviorSubject({});
  otpFail$ = new BehaviorSubject(null);
  showTouchAnimation = new BehaviorSubject<boolean>(false);
  localUserID: string;
  timeSinceLastLogin: string;
  private userDetailsSubscription; // Declare a variable to store the subscription
  public templateNameArray: string[] = [];
  private userDisabledUnsubscribe?: () => void;
  private tokenRefreshActive = false;
  private isLocalStorageUnavailable = false; // Flag to track localStorage availability
  sessionCount: number;
  /**
   * Checks if localStorage is available and functional
   * @returns true if localStorage is available, false otherwise
   */
  public isLocalStorageAvailable(): boolean {
    try {
      if (typeof localStorage === 'undefined' || localStorage === null) {
        return false;
      }
      // Test if we can actually use localStorage
      const testKey = '__localStorage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (e) {
      // localStorage is not available or quota exceeded
      return false;
    }
  }

  /**
   * Safely get an item from localStorage
   * @param key - The key to retrieve
   * @param defaultValue - Optional default value to return if key doesn't exist or storage unavailable
   * @returns The stored value or defaultValue
   */
  public safeGetItem(key: string, defaultValue: string = ""): string {
    try {
      if (!this.isLocalStorageAvailable()) {
        return defaultValue;
      }
      const value = localStorage.getItem(key);
      return value !== null ? value : defaultValue;
    } catch (e) {
      return defaultValue;
    }
  }

  /**
   * Safely set an item in localStorage
   * @param key - The key to set
   * @param value - The value to store
   * @returns true if successful, false otherwise
   */
  public safeSetItem(key: string, value: string): boolean {
    try {
      if (!this.isLocalStorageAvailable()) {
        if (!this.isLocalStorageUnavailable) {
          // Only show the warning once
          this.isLocalStorageUnavailable = true;
          this.getConstantsMsg("1083")
            .then((translatedMsg) => {
              const defaultMsg = {
                message: {
                  line_1:
                    "Your browser is preventing this site from saving information. Turn off private browsing or use another browser.",
                  line_2: "",
                },
                style: {
                  icon: "tick circle alert.svg",
                  type: "warning",
                },
              };
              this.showToastMessage(translatedMsg, defaultMsg);
            })
            .catch(() => { });
        }
        return false;
      }
      localStorage.setItem(key, value);
      return true;
    } catch (e) {
      if (!this.isLocalStorageUnavailable) {
        // Only show the warning once
        this.isLocalStorageUnavailable = true;
        this.getConstantsMsg("1083")
          .then((translatedMsg) => {
            const defaultMsg = {
              message: {
                line_1:
                  "Your browser is preventing this site from saving information. Turn off private browsing or use another browser.",
                line_2: "",
              },
              style: {
                icon: "tick circle alert.svg",
                type: "warning",
              },
            };
            this.showToastMessage(translatedMsg, defaultMsg);
          })
          .catch(() => { });
      }
      return false;
    }
  }

  checkUserDisabledOrNot(userID?: string) {
    this.commonService.getUid("authService").pipe(take(1)).subscribe({
      next: (uid) => {
        const userId = userID || uid;
        if (!userId) return;

        try {
          const docRef = doc(this.firestore, `${environment.mirrorPath}disable_users/${userId}`);

          // Store unsubscribe function
          this.userDisabledUnsubscribe = onSnapshot(
            docRef,
            (docSnap) => {
              const data = docSnap.data() as any;
              if (data?.userID === userId) {
                this.logout()
                  .then(() => this.router.navigateByUrl("/welcome"))
                  .catch(() => {
                    // handle error
                  });
              }
            },
            () => {
              // handle error
            }
          );
        } catch {
          // handle error
        }
      },
      error: () => {
        // handle error
      },
    });
  }

  /* This function resets the redirect field of the current user in the database to an empty string. This is used to reset the redirect field after the user has been redirected to the page they were trying to access. */
  resetUserRedirect(pageName: string) {
    this.commonService.getUid("resetUserRedirect").pipe(
      take(1),
      switchMap((userId) => {
        if (!userId) return of(null);

        const path_id = "yzF1Ss5eiMqnh91zQMtv";
        const doc_data = { redirect: pageName };
        const method = "update";

        this.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          method,
        }).subscribe({
          next: (res) => {
            retry({
              count: 3,
              delay: () => timer(500),
            }),
              catchError(() => of(null))
          },
          error: () => {
            // handle error
          }
        });
      }),
      takeUntil(this.destroy$)
    ).subscribe();
  }


  /* Getting the current version of the app from the firestore. */
  appVersion$: Observable<any> = (() => {
    const docRef = doc(this.firestore, environment.pwaVersionCheckPath);
    return docData(docRef).pipe(takeUntil(this.destroy$),
      tap((res: any) => {
        CommonVariable.appVersion = res?.version ?? '';
      }),
      catchError(() => of(null))
    );
  })();

  // This code is used to get the details of the user
  userDetails$: Observable<User> = this.userManagementService.userRequest$.pipe(takeUntil(this.destroy$),
    filter((user) => user !== null),
    switchMap((user: any) => {
      if (!user) return of(null);
       
      const userDocRef: DocumentReference<User> = doc(this.firestore, `users/${user.uid}`) as DocumentReference<User>;

      return this.catchService.isOnline$.pipe(takeUntil(this.destroy$),
        switchMap((isOnline) => {
          // ✅ FIX: Gate the Firestore listener behind customTokenReady$ so it is
          // never opened before the custom Firebase token is confirmed stored.
          // The previous waitForAuthReady approach piped onto an already-started
          // docSnapshots() stream — Firestore evaluated security rules immediately
          // on subscription regardless, causing permission-denied on iOS Safari
          // where the token HTTP round-trip is slower.
          return isOnline
            ? this.authStateService.customTokenReady$.pipe(
                take(1),
                switchMap(() => docSnapshots(userDocRef).pipe(takeUntil(this.destroy$),
                  // ✅ FIX: Handle permission-denied errors with retry logic
                  handlePermissionDeniedError(() => this.refreshAuthToken(), 3, this.tokenRefreshInProgress$),
                  // ✅ FIX: Detect permission errors for logging
                  detectPermissionDeniedError(),
                  this.catchService.retryWhen,
                  this.catchService.catchOfflineError
                ))
              )
            : of(null);
        }),
        filter((res: any) => res !== null && res !== undefined),
        map((res: any) => res?.data()),
        tap((user: any) => {
          if (user?.auto_login_token) {
            // Use safe storage wrappers instead of direct localStorage access
            this.safeSetItem("auto_login_token_validate", user.auto_login_token);
            this.safeSetItem("userNick", user.profile_nickname);
            this.safeSetItem("user_auto_login_token", user.auto_login_token);
            const engagement_promo = user?.engagement_promo ? user?.engagement_promo?.current : {};
            this.safeSetItem("engagement_promo", engagement_promo.promo_name);
            this.safeSetItem("user_state", user.onboard_state);
          }
          // Set currency immediately so sales pages are never flipped by a delayed overwrite.
          if (user) {
            this.safeSetItem("user_currency", user?.preferred_currency ?? CommonVariable.preferredCurrency);
          }
          // --- LANGUAGE SYNC LOGIC ---
          setTimeout(() => {
            // Guard against user being undefined due to race condition
            if (!user) {
              return;
            }
            
            const localSelectedLang = this.selectedlanguage$?.value ?? user?.language_code;
            const fbLanguage = user?.language_code;
            const prevLocalLang = this.safeGetItem("language_code", "");

            let language: string;
            // Prioritize local selection if user just manually selected it (login)
            if (this.isLanguageChangeTrigger$.value && localSelectedLang) {
              language = CommonVariable.allLanguageCodes.includes(localSelectedLang)
                ? localSelectedLang
                : CommonVariable.preferredLanguage;

              // Push local language to Firebase asynchronously
              this.userManagementService.setUserLanguageLogInFb(prevLocalLang, language, "authService");
              // Optional: reset trigger after push
              this.isLanguageChangeTrigger$.next(false);
            } else if (fbLanguage && fbLanguage !== localSelectedLang) {
              // Firebase differs → override local
              language = CommonVariable.allLanguageCodes.includes(fbLanguage)
                ? fbLanguage
                : CommonVariable.preferredLanguage;

              this.selectedlanguage$.next(language);
              this.safeSetItem("language_code", language);
            } else {
              // Otherwise use local
              language = localSelectedLang;
            }

            // Apply language and user state
            user.language_code = language;
            this.safeSetItem("language_code", language);
        }, 5000)
        }),
        shareReplay(1)
      );
    }),
    shareReplay(1)
  );

  userDetailsCheckoutCom$: Observable<User | null> = defer(() =>
    this.checkoutFlowUserId$.pipe(
      filter((userId): userId is string => !!userId),
      switchMap((userId) => this.streamFirestoreUserProfile$(userId)),
    )
  ).pipe(shareReplay(1));

  /** One-shot Firestore profile read for Checkout.com post-payment navigation. */
  private streamFirestoreUserProfile$(userId: string): Observable<User | null> {

    return from(getDoc(doc(this.firestore, `users/${userId}`))).pipe(
      map((snapshot) => (snapshot.exists() ? snapshot.data() as User : null)),
      catchError((error) => {
        this.errorHandlerLogs('streamFirestoreUserProfile$ failed', error);
        return of(null);
      })
    );
  }
  // Get the lottery lines of the user
  // lotteryLInes$ is a observable that is used to get the lottery lines of the user
  // It gets the user from the userRequestForHome$ observable
  // Then it gets the lottery lines of the user and returns the lottery lines of the user
  // If the user is null then it returns null
  lotteryLInes$ = this.userManagementService.userRequestForHome$.pipe(takeUntil(this.destroy$),
    switchMap((user: any) => {
      if (!user?.uid) return of(null); // Check if user or UID is not available

      const linesRef: CollectionReference<DocumentData> = collection(this.firestore, `users/${user?.uid}/lottery_lines`); // Reference lottery_lines collection

      return this.catchService.isOnline$.pipe(takeUntil(this.destroy$),
        switchMap((isOnline) => {
          // ✅ FIX: Gate behind customTokenReady$ so the collectionData listener
          // is never opened before the custom Firebase token is confirmed stored.
          return isOnline
            ? this.authStateService.customTokenReady$.pipe(
                take(1),
                switchMap(() => collectionData(linesRef, { idField: 'id' }).pipe(takeUntil(this.destroy$), // Use collectionData to fetch collection documents
                  this.catchService.retryWhen, // Retry logic for error scenarios
                  this.catchService.catchOfflineError // Handle offline errors
                ))
              )
            : of(null); // Return null if offline
        }),
        map((data: DocumentData[] | null) => {
          // Add defensive check: ensure data is array before processing
          if (!data) {
            return null;
          }
          if (!Array.isArray(data)) {
             this.errorHandlerLogs('lotteryLInes$: Non-array data received, returning null', { dataType: typeof data });
            return null;
          }
          return this.firestoreDataServices.processSnapshotData(data);
        }), // Process the document data safely
        catchError((error) => {
          this.errorHandlerLogs('lotteryLInes$: Error in observable chain', error);
          return of(null); // Return null on error to prevent observable from breaking
        }),
        shareReplay(1) // Cache results
      );
    }),
    shareReplay(1) // Cache results
  );

  // Emits whenever the displayed points balance must be re-fetched after a
  // client-side points mutation (e.g. a successful charity vote) so the cached
  // balance/current read is invalidated without a full page reload.
  balanceRefreshTrigger$ = new BehaviorSubject<void>(undefined);

  /** Latest balance snapshot for UI (Left Menu / Games) after fetch or refresh. */
  readonly currentBalanceSnapshot$ = new BehaviorSubject<Data | null>(null);

  // This code gets the current balance of the user from the database
  // and returns the balance.
  userCurrentBalance$ = combineLatest([
    this.userManagementService.userRequest$,
    this.balanceRefreshTrigger$,
  ]).pipe(
    takeUntil(this.destroy$),
    switchMap(() => {
      return this.commonService.getUid("UserCurrentBalance").pipe(
        switchMap((uid: string | null) => {
          if (!uid) return of({});

          return this.catchService.isOnline$.pipe(
            takeUntil(this.destroy$),
            switchMap((isOnline) => {
              if (!isOnline) return of(null);

              const balanceDocRef = doc(this.firestore, `users/${uid}/balance/current`);
              // ✅ FIX: Gate behind customTokenReady$ so the getDoc call is deferred
              // until the custom Firebase token is confirmed stored.  Without this
              // gate the one-shot getDoc fired immediately on iOS Safari before the
              // token HTTP call completed, causing permission-denied.
              return this.authStateService.customTokenReady$.pipe(
                take(1),
                switchMap(() => from(getDoc(balanceDocRef)).pipe(
                  takeUntil(this.destroy$),
                  // ✅ FIX: Handle permission-denied errors with retry logic
                  handlePermissionDeniedError(() => this.refreshAuthToken(), 3, this.tokenRefreshInProgress$),
                  // ✅ FIX: Detect permission errors for logging
                  detectPermissionDeniedError(),
                  map((snapshot) => {
                    const data = snapshot.data();
                    return data;
                  })
                ))
              );
            }),
            catchError((error) => {
              this.catchService.catchOfflineError(of(error));
              return of(null);
            })
          );
        })
      );
    }),
    tap((balance) => {
      if (balance && typeof balance === "object") {
        this.currentBalanceSnapshot$.next(balance as Data);
      }
    }),
    // Keep the connection hot so Left Menu + Games share the same refresh emissions.
    shareReplay(1)
  );

  /**
   * Re-fetches the canonical member points balance so any Angular-bound points
   * UI updates immediately after a successful points mutation. Used by the Home
   * Scroll charity vote success flow to reflect the post-deduction balance
   * without a manual reload, reusing the shared userCurrentBalance$ source
   * rather than patching displayed values per-component.
   */
  refreshUserCurrentBalance(): void {
    this.balanceRefreshTrigger$.next();
    // Vote CF may commit balance/current slightly after the API success response.
    // A second pass avoids Left Menu / Games keeping a pre-deduction snapshot.
    window.setTimeout(() => this.balanceRefreshTrigger$.next(), 600);
  }


  // This is the code for fetching the data from the Firestore database
  // It will fetch the data of last 10 check winnings
  // It will fetch the data of the user with the id that is stored in the localStorage

  previousCheck$ = this.userManagementService.userRequest$.pipe(takeUntil(this.destroy$),
    filter(() => window.location.href.includes("syndicate")),
    switchMap((user: any) => {
      if (!user || !user.uid) return of(null); // Return null if user or UID is not available

      return this.catchService.isOnline$.pipe(takeUntil(this.destroy$),
        switchMap(async (isOnline) => {
          if (!isOnline) return null; // Return null when offline

          const checkWinningsCollection = collection(this.firestore, `/users/${user.uid}/check_winnings`);
          const checkWinningsQuery = query(
            checkWinningsCollection,
            orderBy("check_winning_date", "desc"),
            limit(10) // Query with ordering and limit
          );

          return await getDocs(checkWinningsQuery); // Fetch QuerySnapshot
        }),
        map((snapshot) => {
          if (snapshot === null) return []; // Handle the offline case explicitly
          return this.firestoreDataServices.snapShotData(snapshot); // Process snapshot data
        }),
        catchError((error) => {
          // permission-denied is expected when auth hasn't fully initialised
          // yet (race condition on iOS/Safari).  Return an empty array so the
          // stream survives and the UI can render a safe empty state.
          if (error?.code === 'permission-denied') {
            console.warn('[previousCheck$] Permission denied - auth not ready, returning empty array');
            return of([]);
          }
          this.catchService.catchOfflineError(of(error));
          throw error;
        }),
        shareReplay(1)
      );
    })
  );

  private tokenLogin = switchMap((loginData) => {
    const { user, customToken, isCurrentJourney } = loginData as Data;
    const isCurrent = () => !isCurrentJourney || isCurrentJourney();
    if (!isCurrent()) return of(null);
    if (!customToken) return of(loginData);
    const storeCustomToken = () => {
      if (typeof localStorage === "object") {
        try {
          localStorage.setItem("fbToken", customToken);
        } catch (e) {
          Storage.prototype._setItem = Storage.prototype.setItem;
          /*  const msg = this.translatedToastMsg?.["1083"]?.[this.selectedLanguage];
           this.authService.showToast(
             msg
               ? msg
               : "Your browser is preventing this site from saving information. Turn off private browsing or use another browser.",
             "warning",
             ""
           ); */
          this.getConstantsMsg("1083")
            .then((translatedMsg) => {
              if (!isCurrent()) return;
              const defaultMsg = {
                message: {
                  line_1:
                    "Your browser is preventing this site from saving information. Turn off private browsing or use another browser.",
                  line_2: "",
                },
                style: {
                  icon: "tick circle alert.svg",
                  type: "warning",
                },
              };
              this.showToastMessage(translatedMsg, defaultMsg);
            })
            .catch((error) => {
              return error;
            });
        }
      }
    };
    // Guarded registration stores its token only after current-journey sign-in succeeds.
    // Other token-login callers retain their existing storage order.
    if (!isCurrentJourney) storeCustomToken();
    const mapLoginResult = (data: Data) => {
      if (!isCurrent()) return null;
      if (isCurrentJourney) storeCustomToken();
      return { ...data, ...{ userDetails: user } };
    };
    if (isMobile.any()) {
      const auth = getAuth();
      const promise = setPersistence(auth, browserLocalPersistence);
      const promiseToObservable = from(promise);
      return promiseToObservable.pipe(takeUntil(this.destroy$),
        switchMap(() => {
          if (!isCurrent()) return of(null);
          return from(this.angularFireAuth.signInWithCustomToken(customToken)).pipe(takeUntil(this.destroy$),
            map(mapLoginResult)
          );
        })
      );
    } else {
      return from(this.angularFireAuth.signInWithCustomToken(customToken)).pipe(takeUntil(this.destroy$),
        map(mapLoginResult)
      );
    }
  });

  getSpotlightStatus(userId: string): Observable<{ id: string; completed: boolean }[]> {
    const docNames = [
      "all_about_games",
      "basics_of_checking_tickets",
      "make_the_most_out_of_your_account",
      "use_of_credit",
      "use_of_points",
    ];

    return this.commonService.getUid("SpotlightService").pipe(
      switchMap((userId) => {
        if (!userId) {
          return of([]); // Return empty array if no user
        }

        // Defense-in-depth gate for user-private spotlight docs (LOTTOSOCIAL-PWA-1MEE)
        return this.authStateService.customTokenReady$.pipe(
          take(1),
          switchMap(() => {
            const observables = docNames.map((docName) => {
              const ref = doc(this.firestore, `users/${userId}/spotlight_carousal/${docName}`);
              return docData(ref, { idField: "id" }).pipe(
                map((data: any) => ({
                  id: docName,
                  status: data?.status,
                  completed: data?.status === "completed",
                }))
              );
            });

            return combineLatest(observables);
          })
        );
      })
    );
  }



  // This function is used to authenticate users via Facebook or Google. The function takes a single argument which is the name of the social account to authenticate with. The function returns an observable of type UserCredential.
  socialConnect(social: string) {
    if (social == "facebook") {
      return this.socialAuthLogin(new FacebookAuthProvider());
    } else {
      return this.socialAuthLogin(new GoogleAuthProvider());
    }
  }

  // This function allows a user to link their account to their social media account.
  socialLink(social: string) {
    let googleProvider = new GoogleAuthProvider();
    let facebookProvider = new FacebookAuthProvider();

    let provider = null;
    if (social == "facebook") {
      provider = facebookProvider;
    } else {
      provider = googleProvider;
    }
    const auth = getAuth();
    return linkWithPopup(auth.currentUser, provider);
  }

  // Unlink a social account from the user account
  socialUnlink(social: string) {
    const auth = getAuth();
    if (social == "facebook") {
      return unlink(auth?.currentUser, "facebook.com");
    } else {
      return unlink(auth?.currentUser, "google.com");
    }
  }

  // Auth logic to run auth providers
  socialAuthLogin(provider) {
    return this.angularFireAuth
      .signInWithPopup(provider)
      .then(() => {
        this.router.navigateByUrl("/mainTabs/tabs/home");
      })
      .catch((error) => {
        return error;
      });
  }

  // This code gets the holding screen data from Firestore
  holdingMessage$ = docData(
    doc(this.firestore, 'holding_screen_content/holding_content')
  ).pipe(
    catchError(() => of(null))
  );

  // const userId = 'user123';
  udid = ['abc123', 'def456', 'ghi789'];
  userIdSignal = signal('');
  constructor(private injector: Injector) {
    super(injector);
    this.init$.subscribe();
    this.sessionCount = 0;

  }

  private get toastFacade(): ToastFacadeService {
    return this.injector.get(ToastFacadeService);
  }


  private init$ = defer(() => {
    this.commonService.getUid("authService").pipe().subscribe({
      next: (res) => {
        this.userId = res;
        this.uploadUserUdid(res, this.udid);
      }
    });

    this.gameBalanceService.scheduleDailyChallenge();
    
    this.userManagementService.selectedCurrency =
      this.userManagementService.selectedCurrency || CommonVariable.preferredCurrency;
    this.freebieVoucherAssigned = false;
    this.currencySymbol = CommonVariable.defaultCurrencySymbol;
    //this.setCahceMetaData();
    this.userManagementService.userDetailsForHome$?.subscribe();
    this.userDetails$?.subscribe();
    this.getCurrencyList$?.subscribe();
    // this.getActiveCurrencyList$?.subscribe();
    this.getCurrencyAndLanguageList$.subscribe();
    // this.getTruevoDetails();
    this.angularFireAuth.onAuthStateChanged((user: any) => {
      if (!user) {
        this.userDisabled$?.unsubscribe();
        // Signal that auth is no longer ready (e.g. after logout)
        this.authReady$.next(false);
      }

      if (!user || user?.isAnonymous ) return;
      // Restored sessions / new tabs never hit sequenceLoginOperations(); keep
      // users/{uid}.current_session_id aligned with tab sessionStorage.SessionId
      // so push dismissal matches createInlineNotificationFn.
      void this.setCurrentSessionId();
      this.checkUserDisabledOrNot(user?.uid);
      this.checkLoginLog().catch(() => {
        // handle error
      });
      // this.updatePromotionalGames();
      this.homeDataService.loadHomeDate$.next(user);
      this.userManagementService.userRequestForHomeTopPage$.next(user);

      this.userManagementService.userRequestForHome$.next(user);
      try {
        this.userIdSignal.set(user ? user?.uid : null);
        this.commonService.getUid('authService').pipe(take(1)).subscribe({
          next: (uid) => { this.localUserID = uid; },
          error: (err) => { return err; }
        });
        this.showMobileUpdate();
        this.fetchLatestOngoingSyndicate();
        this.checkForOnesignalStatus();
        // After mid-session login, bind OneSignal external_id + token row
        this.oneSignalService.reconcileIdentityAndSubscription();
      } catch (e) {
        Storage.prototype._setItem = Storage.prototype.setItem;

        this.getConstantsMsg("1083")
          .then((translatedMsg) => {
            const defaultMsg = {
              message: {
                line_1: "Your browser is preventing this site from saving information. Turn off private browsing or use another browser.",
                line_2: "",
              },
              style: { icon: "tick circle alert.svg", type: "warning" },
            };
            this.showToastMessage(translatedMsg, defaultMsg);
          }).catch((error) => { return error; });
      }

      if (user && user.uid) {
        this.injector.get(AppVersionService).setUserPWACurrentVersion(user.uid);

        this.commonUserCheck(() => {
          this.marketingCampaignsService.addWPUser(user.uid);
          this.setupTokenRefresh(user?.uid);
          this.afterLoginRequest(user?.uid).subscribe();
        });
      }
      this.userManagementService.userRequest$.next(user);
      this.getConstantsMsg$?.subscribe((msg: any) => {
        this.translatedToastMsg = msg?.static_messages_pwa;
      });

      this.userManagementService.userRequest$.next(user);

      // NOTE: authReady$ is intentionally NOT set to true here any more.
      // It is now emitted inside setupTokenRefresh() once the custom Firebase
      // token is confirmed stored — alongside customTokenReady$.next(true) —
      // so that authReady$ accurately reflects "token is in place" rather than
      // "onAuthStateChanged fired".  Setting it here (before the async CF call
      // completes) was the root cause of the iOS Safari permission-denied race.
    });

    this.userManagementService.locations$.subscribe();
    this.templateNameArray = ["popup", "top_box", "bottom_box_with_icon", "full_screen", "takeover", "bottom_box"];
    this.checkAppUpdateOnLogin();
    this.checkFlashSaleEnabled();
    return of(null); // return an observable to complete defer
  });


  async getUid(caller: string): Promise<string | null> {
    try {
      const user = await this.angularFireAuth.currentUser;
      if (!user) {
        return null;
      }
      return user?.uid ?? null;
    } catch (error) {
      return error;
    }
  }

  getUid$(caller: string): Observable<string | null> {
    // Match CommonService.getUid: wait out in-flight token refresh, then wait for
    // customTokenReady$ when a user exists so Firestore callers using getUid$ do
    // not race the iOS/Safari custom-token round-trip (LOTTOSOCIAL-PWA-1MEE).
    // Logged-out callers still resolve null immediately.
    // Fallback: resolve uid after AUTH_GATE_MAX_WAIT_MS if customTokenReady$ never emits.
    // The three bounded waits (token refresh, currentUser, customTokenReady$) can
    // therefore take roughly 3 * AUTH_GATE_MAX_WAIT_MS in the worst case.
    return race(
      this.authStateService.tokenRefreshInProgress$.pipe(
        filter((inProgress) => !inProgress),
        first()
      ),
      timer(AuthService.AUTH_GATE_MAX_WAIT_MS).pipe(
        tap(() => this.reportAuthGateTimeout(caller, "token_refresh"))
      )
    ).pipe(
      switchMap(() =>
        defer(() =>
          from(this.angularFireAuth.currentUser).pipe(
            timeout({
              each: AuthService.AUTH_GATE_MAX_WAIT_MS,
              with: () => {
                this.reportAuthGateTimeout(caller, "auth_init");
                return of(null);
              },
            }),
            switchMap((user) => {
              const uid = user?.uid ?? null;
              if (!uid) {
                return of(null);
              }
              return race(
                this.authStateService.customTokenReady$.pipe(take(1)),
                timer(AuthService.AUTH_GATE_MAX_WAIT_MS).pipe(
                  tap(() => this.reportAuthGateTimeout(caller, "custom_token_ready"))
                )
              ).pipe(map(() => uid));
            }),
            catchError(() => of(null))
          )
        )
      )
    );
  }
  /**
   * Upper bound on a single custom-token refresh attempt. Past this the attempt
   * is abandoned and retried on the normal 5 minute schedule, rather than
   * holding every getUid() caller open.
   */
  private static readonly TOKEN_REFRESH_MAX_MS = 10000;

  /**
   * Hard ceiling on each of the three auth waits in getUid$() (token refresh,
   * Auth init / currentUser, and customTokenReady$). Mirrors
   * CommonService.AUTH_GATE_MAX_WAIT_MS so both entry points behave the same.
   *
   * This is a safety net, not a latency target. Callers that genuinely cannot
   * afford to wait should cap their own wait instead (see partial_Reg), which
   * keeps this ceiling long enough that it rarely hands out a premature null.
   */
  private static readonly AUTH_GATE_MAX_WAIT_MS = 5000;

  /**
   * Fires when one of the auth gates in getUid$() hits its ceiling. Rare by
   * design: a cluster of these is the early warning that a permission-denied
   * regression is coming, and `caller` names the exposed call site.
   */
  private reportAuthGateTimeout(
    caller: string,
    stage: "token_refresh" | "auth_init" | "custom_token_ready"
  ): void {
    try {
      this.errorHandlerLogs("[getUid$] auth gate timed out", {
        caller: caller || "unknown",
        stage,
        waitMs: AuthService.AUTH_GATE_MAX_WAIT_MS,
      });
    } catch {
      // Never let reporting break the auth gate fallback.
    }
  }

  /**
   * Sets up a token refresh mechanism based on token expiration.
   * This replaces the setInterval approach with a more reliable setTimeout.
   */
  private setupTokenRefresh(userId: string): void {
    // Prevent multiple refresh cycles
    if (this.tokenRefreshActive) {
      return;
    }

    this.tokenRefreshActive = true;

    // Clear any existing mechanisms
    this.clearAllTokenRefreshMechanisms();

    const refreshToken = () => {
      // ✅ FIX: Signal that token refresh is in progress to prevent Firestore ops
      // Keep AuthService + AuthStateService subjects in sync so CommonService.getUid
      // (which waits on authStateService.tokenRefreshInProgress$) actually blocks.
      this.tokenRefreshInProgress$.next(true);
      this.authStateService.tokenRefreshInProgress$.next(true);
      
      // Add subscription management
      this.getCustomToken(userId)
        .pipe(
          takeUntil(this.destroy$),
          catchError(err => {
            this.errorHandlerLogs("Token refresh failed:", err);
            // Never fake readiness without Auth. If login/persistence already
            // established a non-anonymous session, unblock listeners and retry
            // the CF refresh so we do not hang the app for 5 minutes.
            // EMPTY stops the chain so switchMap does not finalize a second time.
            this.finishTokenRefreshAttempt(/*signInSucceeded*/ false, userId);
            return EMPTY;
          }),
          switchMap((res: Data) => {
            const customToken = res?.customToken;
            if (!customToken) {
              this.finishTokenRefreshAttempt(/*signInSucceeded*/ false, userId);
              return EMPTY;
            }

            if (typeof localStorage === "object") {
              try {
                localStorage.setItem("fbToken", customToken);
              } catch (e) {
                // iOS Safari Private Browsing may throw; continue with sign-in.
                this.errorHandlerLogs("fbToken localStorage set failed:", e);
              }
            }

            // Critical: only mark customTokenReady$ after Auth accepts the token.
            // Storing in localStorage alone does not authorize Firestore reads.
            const auth = getAuth();
            return from(signInWithCustomToken(auth, customToken)).pipe(
              takeUntil(this.destroy$),
              tap(() => {
                this.finishTokenRefreshAttempt(/*signInSucceeded*/ true, userId);
              }),
              catchError((signInErr) => {
                this.errorHandlerLogs("signInWithCustomToken failed:", signInErr);
                this.finishTokenRefreshAttempt(/*signInSucceeded*/ false, userId);
                return EMPTY;
              })
            );
          }),
          // Bound the complete refresh attempt, including sign-in. A token can
          // arrive successfully while signInWithCustomToken() remains pending.
          timeout({
            each: AuthService.TOKEN_REFRESH_MAX_MS,
            with: () => {
              this.errorHandlerLogs("Token refresh timed out:", {
                timeoutMs: AuthService.TOKEN_REFRESH_MAX_MS,
              });
              this.finishTokenRefreshAttempt(/*signInSucceeded*/ false, userId);
              return EMPTY;
            },
          }),
          // Safety net for unexpected/synchronous errors so guards never stick.
          catchError((err) => {
            this.errorHandlerLogs("Token refresh chain failed:", err);
            this.finishTokenRefreshAttempt(/*signInSucceeded*/ false, userId);
            return EMPTY;
          })
        )
        .subscribe();
    };

    refreshToken();
  }

  /**
   * Clears in-progress flags after a token refresh attempt.
   * On success: always emit customTokenReady$ / authReady$ and schedule the 55m cycle.
   * On failure: emit ready only if a non-anonymous Auth session already exists
   * (login / persistence), then retry; otherwise leave ready unset so gated
   * listeners do not open against an unauthenticated session.
   * Keeps tokenRefreshActive true until the scheduled next/retry callback
   * clears it immediately before setupTokenRefresh(). Retries use the original
   * userId (not getUid) so they cannot deadlock on customTokenReady$.
   */
  private finishTokenRefreshAttempt(signInSucceeded: boolean, userId: string): void {
    this.tokenRefreshInProgress$.next(false);
    this.authStateService.tokenRefreshInProgress$.next(false);

    const existingUser = getAuth()?.currentUser;
    const hasValidAuth = !!(existingUser && !existingUser.isAnonymous);

    if (signInSucceeded || hasValidAuth) {
      this.authStateService.customTokenReady$.next(true);
      this.authReady$.next(true);
    }

    if (signInSucceeded) {
      this.scheduleNextTokenRefresh(userId);
    } else {
      this.scheduleTokenRefreshRetry(userId);
    }
  }

  private clearAllTokenRefreshMechanisms(): void {
    if (this.tokenRefreshTimeout) {
      clearTimeout(this.tokenRefreshTimeout);
      this.tokenRefreshTimeout = null;
    }

    if (this.customTokenInterval) {
      clearInterval(this.customTokenInterval);
      this.customTokenInterval = null;
    }
  }

  private scheduleNextTokenRefresh(userId: string): void {
    const refreshTime = 55 * 60 * 1000; // 55 minutes

    this.clearAllTokenRefreshMechanisms();
    this.tokenRefreshTimeout = setTimeout(() => {
      // isStopped covers Subject.complete(); closed covers unsubscribe.
      if (this.destroy$.closed || this.destroy$.isStopped) {
        this.tokenRefreshActive = false;
        return;
      }
      // Clear guard immediately before setupTokenRefresh reclaim.
      this.tokenRefreshActive = false;
      if (userId) {
        this.setupTokenRefresh(userId);
      }
    }, refreshTime);
  }


  private scheduleTokenRefreshRetry(userId: string): void {
    const retryTime = 5 * 60 * 1000; // 5 minutes

    this.clearAllTokenRefreshMechanisms();
    this.tokenRefreshTimeout = setTimeout(() => {
      if (this.destroy$.closed || this.destroy$.isStopped) {
        this.tokenRefreshActive = false;
        return;
      }
      // Clear guard immediately before setupTokenRefresh reclaim.
      // Use the original userId — do not call getUid (avoids customTokenReady$ deadlock).
      this.tokenRefreshActive = false;
      if (userId) {
        this.setupTokenRefresh(userId);
      }
    }, retryTime);
  }

  // Add a method to check and ensure a valid authentication token exists
  public ensureValidAuth(): Observable<boolean> {
    // ✅ FIX: Skip auth checks while token refresh is in progress
    if (this.tokenRefreshInProgress$.value) {
      return of(false);
    }
    
    return this.getCurrentUser().pipe(takeUntil(this.destroy$),
      switchMap(user => {
        if (!user) {
          // User is not authenticated, return false
          return of(false);
        }

        // User is authenticated, check if token exists
        const token = localStorage.getItem('fbToken');
        if (!token) {
          // No token, try to get a new one
          return (this.getCustomToken(user.uid) ?? of(null)).pipe(takeUntil(this.destroy$),
            map(res => {
              if (res?.customToken) {
                localStorage.setItem('fbToken', res.customToken);
                return true;
              }
              return false;
            }),
            catchError(() => of(false))
          );
        }

        // Token exists, return true
        return of(true);
      }),
      catchError(() => of(false))
    );
  }

  /**
   * Refreshes the auth token explicitly
   * Used by permission-denied retry handler to recover from token issues
   */
  public refreshAuthToken(): Observable<any> {
    return this.commonService.getUid("refreshAuthToken").pipe(
      take(1),
      switchMap((userId) => {
        if (!userId) return of(null);
        return this.getCustomToken(userId) ?? of(null);
      }),
      tap((res) => {
        if (res?.customToken) {
          localStorage.setItem('fbToken', res.customToken);
        }
      }),
      catchError((err) => {
        console.error('[refreshAuthToken] Failed to refresh token:', err);
        return of(null);
      })
    );
  }

  getData() {
    const collectionRef = collection(this.firestore, 'items');
    return collectionData(collectionRef);
  }

  async checkFlashSaleEnabled() {
    try {
      const userId = await firstValueFrom(this.commonService.getUid("checkFlashSaleEnabled"));
      if (!userId) return;

      const remoteConfigRequest = {
        parameter: "flash_sales",
        condition: "flash_sales_condition",
        feature: "flash_sales",
        userId: userId,
      };

      const res = await this.getRemoteConfig(remoteConfigRequest);

      if (res?.data) {
        const remoteConfigResponse = res.data as Data;
        if (remoteConfigResponse?.flash_sales != null) {
          this.flashSaleEnabled = remoteConfigResponse.flash_sales;
        }
      }
    } catch  {
      // handle error
    }
  }

  getConstantsMsg$ = this.getV2CloudFunction("updatefirestoredoc", {
    path_id: "G2hzy28FznIF29mIsjoR",
    filter: ["static_messages_pwa"],
    method: "read",
  }).pipe(
    map((response: any) => {
      // Defensive check: ensure response is valid object
      if (!response || typeof response !== 'object') {
        return {};
      }
      
      // Validate response.data is an object and not a malformed string
      const data = response?.data;
      
      return data || {};
    }),
    catchError(() => {
      // Return empty object as fallback to prevent app crash
      return of({});
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  showMobileUpdate(): void {
    this.userDetails$
      ?.pipe(takeUntil(this.destroy$), first())
      .toPromise()
      .then(async (value: Data) => {
        try {
          // spoof_msn is set on social / passwordless signup payloads (RequestUserService
          // checkSocialSignIn / signInRequestForUsers) when the account has a placeholder
          // mobile. That flag forces mobile verification before continuing — not random checkout MFA.
          if (value?.spoof_msn) {
            const { MobileNumberUpdateComponent } = await import(
              "../components/mobile-number-update/mobile-number-update.component"
            );
            const bottomSheet = this.injector.get(MatBottomSheet);
            const mobileNumberBottomSheet = bottomSheet.open(MobileNumberUpdateComponent, {
              panelClass: "mobile-update-bottomsheet",
              autoFocus: true,
              disableClose: true,
              data: { autoOpen: true, isSpoofMsn: true },
            });
            this.isBottomSheetOpen = true;

            mobileNumberBottomSheet.afterDismissed().subscribe((response: boolean | Data) => {
              if (typeof response == "boolean" && !response) {
                this.showMobileUpdate();
                return;
              }
              this.isBottomSheetOpen = false;
            });
          }
        } catch (error) {
          return error;
        }
      })
      .catch((error) => {
        return error;
      });
  }

  // This method is used for login
  login(credentials: AuthCredentials): Observable<any> {
    // Generate fingerprint if not already available
    if (!CommonVariable.FingerprintJSConst) {
      this.fingerprintJS().then((visitorId: string) => {
        if (visitorId) {
          CommonVariable.FingerprintJSConst = visitorId;
        }
      }).catch(() => {
        // handle error
      });
    }
    this.loadingService.present();
    // const callAuth = this.angularFireFunctions.httpsCallable("login");
    credentials.udId = CommonVariable.UDId;
    credentials.language_code = this.selectedlanguage$?.value;
    credentials.password = credentials?.password;
    credentials.encrypted = false;
    const callAuth = this.getV2CloudFunction("loginv2", credentials);
    return callAuth.pipe(takeUntil(this.destroy$),
      this.tokenLogin,
      catchError((e) => {
        setTimeout(() => {
          this.loadingService.dismiss().catch((err) => {
            return err;
          });
        }, 1200);

        return throwError(e);
      }),
      tap(() => {
        this.updateUserMicroAnimation();
        setTimeout(() => {
          this.loadingService.dismiss().catch((err) => {
            return err;
          });
        }, 1200);
      })
    );
  }

  // this is for logging the id at backend to let it know a user has logged in
  logLogin() {
    try {
      if (window.navigator.onLine) this.getLoginLogApiRequest().subscribe();
    } catch (error) {
      throw new Error(`Error in logLogin function: ${error}`);
    }
  }

  // This method is do all the house keeping after login
  afterLoginRequest(userId: string) {
    return this.waitForDocumentToExist(`users/${userId}`, 5, 300).pipe(takeUntil(this.destroy$),
      switchMap((exists) => {
        if (!exists) {
          return of(null);
        }
        // Non-blocking operations
        this.notificationsService.sendLanguageRequest();
        if (typeof GTM_Segment === "function") {
          try {
            GTM_Segment();
          } catch (err) {
            return err
          }
        }
        if (localStorage.getItem("pwaHouseKeeping") !== "true") {
          try {
            this.setGamePlayRestriction(userId);
            this.processLoginGift(userId);
            this.pwaHouseKepping(userId);
            this.updateGamePromotion(userId);
          } catch (err) {
            return err;
          }
        }
        try {
          this.scratchGameNotificationService(userId);
          this.assignUserGoals(userId);
        } catch (err) {
          return err;
        }
        return this.getV2CloudFunction("afterloginv2", userId).pipe(takeUntil(this.destroy$),
          catchError(() => {
            return of(null); // Allow flow to continue
          }),
          concatMap(() =>
            forkJoin({
              ipInfo: this.userManagementService.getUserCountryBaseOnIP().pipe(takeUntil(this.destroy$),
                catchError(() => {
                  return of({ ip: null });
                })
              ),
              swVersion: this.http.get("/api/service-worker-version", { responseType: "text" }).pipe(takeUntil(this.destroy$),
                retry({ count: 20, delay: 5000 }),
                map((response: string) => {
                  try {
                    const parsed = JSON.parse(response);
                    return parsed;
                  } catch {
                    return { version: environment.appVersion };
                  }
                }),
                catchError(() => {
                  return of({ version: environment.appVersion });
                })
              ),
              fingerPrint: from(this.fingerprintJS()).pipe(
                takeUntil(this.destroy$),
                tap((visitorId: string) => {
                  CommonVariable.FingerprintJSConst = visitorId;
                }),
                catchError((err) => {
                  this.errorHandlerLogs('FingerprintJS Error in afterLoginRequest', err);
                  return of('unknown-fingerprint');
                })
              )
            }).pipe(takeUntil(this.destroy$),
              switchMap(({ ipInfo, swVersion, fingerPrint }) => {
                const acceptedVersion = localStorage.getItem('acceptedAppVersion');
                const payload = {
                  userId,
                  fingerprintId: fingerPrint,
                  userAgent: navigator?.userAgent || null,
                  appVersion: acceptedVersion ?? swVersion.version,
                  userIP: ipInfo?.ip ?? null,
                };
                return this.getV2CloudFunction("updateuserfingerprintv2", payload).pipe(takeUntil(this.destroy$),
                  catchError(() => {
                    return of(null);
                  })
                );
              })
            )
          ),
          catchError((error) => {
            throwError(() => error);
            return of(null);
          })
        );
      })
    );
  }


  waitForDocumentToExist(docPath: string, maxRetries = 10, initialDelayMs = 500): Observable<boolean> {
    return defer(async () => {
      const docRef = doc(this.firestore, docPath);
      const snapshot = await getDoc(docRef);
      return snapshot.exists();
    }).pipe(takeUntil(this.destroy$),
      switchMap((exists) =>
        exists ? of(true) : throwError(() => new Error('Document not found'))
      ),
      retryWhen((errors) =>
        errors.pipe(takeUntil(this.destroy$),
          scan((retryCount, error) => {
            if (retryCount >= maxRetries) throw error;
            return retryCount + 1;
          }, 0),
          delayWhen((retryCount) =>
            timer(Math.min(initialDelayMs * Math.pow(2, retryCount), 30000))
          )
        )
      )
    );
  }

  /**
   * Creates a custom token for the given user id.
   *
   * @param userId The user id to create a custom token for.
   * @returns A promise that resolves to the custom token.
   */
  getCustomToken(userId: string): Observable<any> {
    try {
      // Call the cloud function
      if (userId) {
        return this.getV2CloudFunction("createcustomtokenv2", userId);
      }
      // No userId provided — return safe empty Observable
      return of(null);
    } catch {
      // Return safe empty Observable on any synchronous error
      return of(null);
    }
  }

  // Gets the currently logged in user from firebase
  // Returns a promise that resolves to the user
  async getCurrentLoggedinUser() {
    return await this.angularFireAuth.currentUser;
  }

  /**
   * Update Firebase Auth password for a logged-in Profile change.
   * Uses client SDK reauth + updatePassword. Do not call updateuserpasswordv2 here —
   * that CF is forgot-password only and requires a resetToken.
   */
  updatePasswordOnFirebase(newPassword: string, msn: string, currentPassword: string) {
    const normalizedMsn = String(msn ?? "").trim();
    const normalizedCurrent = String(currentPassword ?? "");
    const normalizedNew = String(newPassword ?? "");
    if (!normalizedMsn || normalizedMsn === "+" || !normalizedCurrent || !normalizedNew) {
      return Promise.reject(new Error("Invalid password update request"));
    }

    const formattedMsn = normalizedMsn.startsWith("+")
      ? normalizedMsn
      : `+${normalizedMsn}`;
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      return Promise.reject(new Error("No authenticated user"));
    }

    const phoneNumberWithoutPlus = formattedMsn.slice(1);
    const syntheticEmail = `${phoneNumberWithoutPlus}@lottosocial.com`;
    const emailForReauth = user.email || syntheticEmail;
    const credential = EmailAuthProvider.credential(emailForReauth, normalizedCurrent);

    return reauthenticateWithCredential(user, credential).then((cred) =>
      updatePassword(cred.user, normalizedNew).then(() => ({ success: true }))
    );
  }
  //This method used for reset password
  resetPasswordOnFirebase(
    newPassword: string,
    msn: string,
    resetToken: string
  ): Promise<Data> {
    const formattedMsn = msn.startsWith('+') ? msn : `+${msn}`;
    const data = {
      password: newPassword,
      resetToken,
    };

    const functions = getFunctions(); // optionally pass your app instance: getFunctions(app)
    const updateUserPassword = httpsCallable(functions, 'updateuserpasswordv2');
    return updateUserPassword(data)
      .then(async (res) => {
        const auth = getAuth(); // optionally getAuth(app)
        const phoneNumberWithoutPlus = formattedMsn.slice(1);
        const email = `${phoneNumberWithoutPlus}@lottosocial.com`;

        try {
          await signInWithEmailAndPassword(auth, email, newPassword);
          return res;
        } catch (err) {
          throw err;
        }
      })
      .catch((error) => {
        throw error;
      });
  }


  // returns api call for logLogin() function
  getLoginLogApiRequest() {
    if (!this.selectedlanguage$?.value) {
      return of(null);
    }
  
    return this.RequestUserService
      .loginLogRequest(this.selectedlanguage$?.value)
      .pipe(
        switchMap((requestBody) => {
          if (!requestBody) {
            return of(null);
          }
  
          return this.getV2CloudFunction("postasynceventsv2", requestBody);
        }),
        catchError(() => {
          return of(null);
        })
      );
  }
  
  

  //This method used for logout
  async logout() {
    // Invalidate registration callbacks before the first asynchronous logout step.
    this.resetRegistrationIdentityCache();
    // Assuming userDetails$ is an Observable emitting user details
    this.userDetailsSubscription = this.userDetails$.pipe(takeUntil(this.destroy$), take(1)).subscribe((user: Data) => {
      this.updateDismissedPushNotification();
      if (user && user?.last_login_datetime && user?.last_login_datetime?.seconds) {
        // Get the last login time from user details
        const lastLoginTime = new Date(user?.last_login_datetime?.seconds * 1000);
        // Get the current time
        const currentTime = new Date();
        // Calculate the session duration in milliseconds
        const sessionDurationMs = currentTime.getTime() - lastLoginTime.getTime();
        // Convert milliseconds to hours, minutes, and seconds
        const hours = Math.floor(sessionDurationMs / (1000 * 60 * 60));
        const minutes = Math.floor((sessionDurationMs % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((sessionDurationMs % (1000 * 60)) / 1000);

        // Format the session duration with units
        this.timeSinceLastLogin = `${hours?.toString()?.padStart(2, "0")}h ${minutes
          ?.toString()
          ?.padStart(2, "0")}min ${seconds?.toString()?.padStart(2, "0")}s`;
      } else {
        // Default value if last login time is not available
        this.timeSinceLastLogin = "00:00:00";
      }
    });
    // Deactivate stored push token + clear OneSignal external_id before sign-out
    // (shared-device safety — HATDEV-100035)
    try {
      await this.oneSignalService.logoutIdentity();
    } catch (error) {
      // Never block logout if OneSignal clear / token deactivate fails
    }
    await this.angularFireAuth
      .signOut()
      .then(() => {
        clearInterval(this.customTokenInterval);
        this.userSubs?.map((sub) => sub?.unsubscribe());
        // Reset auth-ready gate so Firestore operations don't fire on
        // stale credentials before the next login completes
        this.authReady$.next(false);
        this.resetReadAllVouchersSession();
        // This method has been used to clear the user session / cache
        this.clearUserSession();
        this.unsubscribeGameFeedInbox();
        this.userManagementService.userRequest$.next(null);
        this.commonService.getUid("authService-logout").pipe(take(1)).subscribe({
          next: (uid) => {
            if (uid) {
              this.selectedlanguage$.next(CommonVariable?.preferredLanguage);
              // Add defensive check to prevent "Cannot set properties of undefined" error
              if (this.userManagementService?.selectedlanguage$) {
                this.userManagementService.selectedlanguage$.next(CommonVariable?.preferredLanguage);
              }
            }
          },
          error: (err) => {
            return err;
          }
        })
        const userId = this.localUserID;
        if (userId) {
          const body = {
            Session_ID: userId,
            Session_Duration: this.timeSinceLastLogin,
          };
          this.GTMEventPopupInitialization("Session End", body);
        }
        this.clarityService.setGa4UserId(null);
        this.clarityService.clearAuthenticatedClarityContext();
        this.clarityService.ga4Event("logout");
      })
      .catch((err) => err);
  }
  GTMEventPopupInitialization(msg: string, body?: Data) {
    let analytics;
    if (typeof analytics === "undefined") return;

    // Get userId from commonService
    this.commonService.getUid("GTMEventPopupInitialization").pipe(take(1)).subscribe({
      next: (userId) => {
        if (!userId) return;

        analytics.identify(userId);

        analytics.track(msg, {
          ...body,
        });
      },
      error: () => {
        // handle error
      }
    });
  }


  /**
   * This function gets the current user from the authentication service.
   * The current user is defined as the user that is signed in using the current device.
   */
  getCurrentUser(udid?) {
    return this.angularFireAuth?.user;
  }

  /**
   * It takes a userId and a udid and uploads the udid to the userId's collection.
   * </code>
   * @param userId - The user's ID
   * @param udid - string
   */
  uploadUserUdid(userId: string, udid: string[]): void {
    const path_id = "OpqZ4biW6MzqTeQaf5gj";
    const doc_data = { uid: udid };
    const method = "create";

    this.getV2CloudFunction("updatefirestoredoc", {
      path_id,
      doc_data,
      method,
    }).subscribe({
      error: (err) => {
        throw err;
      }
    });
  }

  // This function is used to sign up a new user
  // It takes in a CreateUser object, calls the signup function in the backend, and returns the result
  signup(user: CreateUser) {
    if (this.getReferralData() && this.getReferralData()?.referralCode) {
      user.voucher_code = this.getReferralData()?.referralCode;
    }
    user.language_code = this.selectedlanguage$?.value || "en";
    user.encrypted = false;
    user.password = user?.password;
    const deviceDetails = {
      IP: CommonVariable?.userIP,
      browser: navigator?.userAgent,
      device_type: CommonVariable?.device?.model,
    };
    const userData = {
      ...user,
      device_details: deviceDetails,
    };
    this.loadingService.present();
    // const callAuth = this.angularFireFunctions.httpsCallable("signup");
    const callAuth = this.getV2CloudFunction("signupv2", userData);
    return callAuth.pipe(takeUntil(this.destroy$),
      finalize(() =>
        this.loadingService.dismiss().catch((err) => {
          return err;
        })
      ),
      tap(() => {
        this.updateUserMicroAnimation();
      }),
      catchError((e) => throwError(e))
    );
  }

  // function is used to move document to user collection on user registration or login
  updateUserMicroAnimation() {
    this.commonService.getUid("updateUserMicroAnimation").pipe(
      take(1), // Take the first emitted userId and complete
      switchMap((userId) => {
        if (!userId) return of(null);
        return this.getV2CloudFunction("usermicroanimationv2", { userId });
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => {
        // Optionally handle success here
      },
      error: () => {
        // handle error
      }
    });
  }


  // This method used for get user address based on user IP address
  checkIpAnGetUserAddress(): Observable<string | null> {
    // If we already have location info, return country code immediately
    if (Object.keys(CommonVariable.locationInfo)?.length > 0 && CommonVariable.countryCode != "") {
      const preferredLanguage = CommonVariable.allLanguageCodes.includes(CommonVariable.preferredLanguage as Language)
        ? CommonVariable.preferredLanguage
        : CommonVariable.preferredLanguage;

      // Fetch userId from commonService instead of localStorage
      this.commonService.getUid("checkIpAndGetUserAddress").pipe(take(1)).subscribe((userId) => {
        if (userId) {
          this.selectedlanguage$.next(preferredLanguage);
          // Add defensive check to prevent "Cannot set properties of undefined" error
          if (this.userManagementService?.selectedlanguage$) {
            this.userManagementService.selectedlanguage$.next(preferredLanguage);
          }
        }
      });

      return of(CommonVariable.countryCode);
    }

    // If no location info, get country info based on IP
    return this.userManagementService.getUserCountryBaseOnIP().pipe(
      takeUntil(this.destroy$),
      switchMap((result: any) => {
        this.ipBasedCurrency = result?.currency?.code;
        localStorage.setItem("ip", result?.ip);

        CommonVariable.locationInfo = result;
        CommonVariable.countryCode = result?.country_code;

        return this.utilityService.getLanguageList().pipe(
          takeUntil(this.destroy$),
          take(1),
          tap((res: any) => {
            if (!res?.languages || !result?.location?.languages?.length) return;

            let checkcode = localStorage.getItem("language_code");
            if (!checkcode) {
              const resultCode = result.location.languages[0]?.code ?? CommonVariable.preferredLanguage;
              const code = res.languages.find((f) => f.language_code === resultCode);
              checkcode = CommonVariable.allLanguageCodes.includes(code) ? code : CommonVariable.preferredLanguage;
            }

            // Update user languages if userId exists
            this.commonService.getUid("updateLanguageSelection").pipe(take(1)).subscribe((userId) => {
              if (userId) {
                this.selectedlanguage$.next(checkcode);
                // Add defensive check to prevent "Cannot set properties of undefined" error
                if (this.userManagementService?.selectedlanguage$) {
                  this.userManagementService.selectedlanguage$.next(checkcode);
                }
              }
            });

            setTimeout(() => {
              CommonVariable.preferredLanguage =
                this.preferredLanguage && CommonVariable.allLanguageCodes.includes(this.preferredLanguage)
                  ? this.preferredLanguage
                  : localStorage.getItem(LocalStorageKeys.LANGUAGE_CODE) ?? checkcode;
            }, 500);

            CommonVariable.userIP = result?.ip;
          }),
          map(() => CommonVariable.countryCode)
        );
      })
    );
  }

  // Check user's login log from firestore.
  // Check user's login log from firestore.
  async checkLoginLog(): Promise<void> {
    const auth = getAuth();
    if (!auth?.currentUser || !auth?.currentUser?.uid) return;

    const uid = auth.currentUser.uid;

    // Gate the user-doc read behind customTokenReady$ (bounded) so iOS Safari
    // does not hit the auth/firestore permission-denied race that left last_login_*
    // fields absent when this getDoc ran immediately on onAuthStateChanged.
    await firstValueFrom(
      race(
        this.authStateService.customTokenReady$.pipe(take(1)),
        timer(AuthService.AUTH_GATE_MAX_WAIT_MS)
      )
    );

    const userDocRef = doc(this.firestore, `users/${uid}`);
    let user: any = null;
    try {
      const snapshot = await firstValueFrom(
        from(getDoc(userDocRef)).pipe(
          handlePermissionDeniedError(
            () => this.refreshAuthToken(),
            3,
            this.tokenRefreshInProgress$
          ),
          detectPermissionDeniedError()
        )
      );
      user = snapshot.exists() ? (snapshot.data() as any) : null;
    } catch {
      // Fail-open: still write platform/display_mode/datetime (first-login shape)
      // so iOS does not leave users/{uid} without last_login_* fields.
      user = null;
    }

    const now = new Date();

    // Platform/display_mode must land on the same write as last_login_datetime so
    // update-main-* triggers see them when createInlineNotification ranks A2HS.
    if (!CommonVariable.device?.operatingSystem) {
      try {
        await this.getDeviceInfo();
      } catch {
        // Keep empty platform if device info is unavailable.
      }
    }
    const last_login_platform = resolveOperatingSystem(CommonVariable.device?.operatingSystem);
    const last_login_display_mode = UtilityService.isAppInstalledOnHomePage()
      ? "standalone"
      : "browser";
    // AC4/AC5: platform or display_mode changes must bump last_login_datetime so
    // update-main-* re-runs createInlineNotification (triggers ignore platform fields alone).
    const loginContextChanged = didA2hsLoginContextChange(
      user?.last_login_platform,
      user?.last_login_display_mode,
      last_login_platform,
      last_login_display_mode
    );

    if (user?.last_login_datetime) {
      // Update Last Login Time 
      const ts = user?.last_login_datetime; // Firestore Timestamp object
      const date = new Date(ts.seconds * 1000 + ts.nanoseconds / 1000000);
      const diff = this.utilityService.diffMinutes(date, now);
      if (diff >= 15) {
        this.logLogin();
        const path_id = "yzF1Ss5eiMqnh91zQMtv";
        const doc_data = {
          last_login_datetime: now,
          previous_login_datetime: date,
          language_code: this.selectedlanguage$?.value,
          last_login_platform,
          last_login_display_mode,
        };
        const method = "update";

        await firstValueFrom(
          this.getV2CloudFunction("updatefirestoredoc", { path_id, doc_data, method })
        );
      } else if (loginContextChanged) {
        const path_id = "yzF1Ss5eiMqnh91zQMtv";
        const doc_data = {
          last_login_datetime: now,
          previous_login_datetime: date,
          language_code: this.selectedlanguage$?.value,
          last_login_platform,
          last_login_display_mode,
        };
        const method = "update";

        await firstValueFrom(
          this.getV2CloudFunction("updatefirestoredoc", { path_id, doc_data, method })
        );
      }
    } else {
      const path_id = "yzF1Ss5eiMqnh91zQMtv";
      const doc_data = {
        last_login_datetime: now,
        previous_login_datetime: now,
        language_code: this.selectedlanguage$?.value,
        last_login_platform,
        last_login_display_mode,
      };
      const method = "update";

      await firstValueFrom(
        this.getV2CloudFunction("updatefirestoredoc", { path_id, doc_data, method })
      );
      this.logLogin();
    }
  }


  /**
   * It takes a user object, checks if there's a referral code in the local storage, if there is, it
   * adds it to the user object, then it adds the selected language to the user object, then it calls
   * the signup function on the backend, and returns the result.
   * </code>
   * @param {CreateUser} user - CreateUser
   * @returns The return value is a function that takes a user object and returns an observable.
   */
  signupSalesJourney(user: CreateUser) {
    if (this.getReferralData() && this.getReferralData().referralCode) {
      user.voucher_code = this.getReferralData().referralCode;
    }
    user.language_code = this.selectedlanguage$?.value;
    // const callAuth = this.angularFireFunctions.httpsCallable("signup");
    const callAuth = this.getV2CloudFunction("signupv2", user);
    return callAuth.pipe(takeUntil(this.destroy$), catchError((e) => throwError(e)));
  }

  // This method gets the udid for the user

  getUserUdid(userId: string, udid: string): void {

    this.catchService.isOnline$
      .pipe(takeUntil(this.destroy$),
        switchMap((isOnline) => {
          if (!isOnline) return of(null); // If offline, return null

          const userDocRef = doc(this.firestore, `/users/${userId}/user_id/list`); // Reference Firestore document
          return getDoc(userDocRef).then((snapshot) => snapshot); // Fetch document snapshot
        }),
        catchError((error) => {
          this.catchService.catchOfflineError(of(error)); // Handle offline errors
          throw error;
        }),
        shareReplay(1) // Cache results
      )
      ?.subscribe(
        async (snapshot: any) => {
          const data = snapshot?.data();
          if (!data) {
            this.uploadUserUdid(userId, [udid]); // Upload new data if none exists
            return;
          }

          if (!data.uid?.some((s: string) => s === udid)) {
            data.uid.push(udid); // Add UDID if it does not exist
            const path_id = "OpqZ4biW6MzqTeQaf5gj";
            const doc_data = { uid: data.uid };
            const method = "update";

            this.getV2CloudFunction("updatefirestoredoc", {
              path_id,
              doc_data,
              method,
            }).subscribe({
              error: (error) => {
                return error;
              }
            });
          }
        },
        (err) => {
          throw err; // Handle subscription errors
        }
      );
  }
  // This code is used to update the user udid after the user has logged in
  updateUserUdid(userId: string, userUid: { uid: string }): void {
    const path_id = "OpqZ4biW6MzqTeQaf5gj";
    const doc_data = { uid: userUid?.uid };
    const method = "update";

    this.getV2CloudFunction("updatefirestoredoc", {
      path_id,
      doc_data,
      method,
    }).subscribe({
      error: (error) => {
        return error;
      }
    });
  }
  // This code is used to login with auth credentials.
  async tryLoginWithCred(value: AuthCredentials, isSignup = false) {
      // Ensure fingerprint exists for manual credential-based login
      if (!CommonVariable.FingerprintJSConst && !isSignup) {
        try {
          const visitorId = await this.fingerprintJS();
          if (visitorId) {
            CommonVariable.FingerprintJSConst = visitorId;
          }
        } catch {
          // handle error
        }
      }
    // Set login flow flag to use fallback messages
    this.isInLoginFlow = true;

    return new Promise((resolve, reject) => {
      this.login(value)
        .pipe(takeUntil(this.destroy$), take(1))
        ?.subscribe(
          (user: Data) => {
            if (!user?.user?.uid) {
              this.handleErrorResponse(user);
              reject(user);
              return;
            }

            this.resetTooltipForNewSession();
            // Sequence critical operations to reduce concurrent IndexedDB load
            this.sequenceLoginOperations(user, isSignup)
              .then(() => {
                resolve(user);
                this.isInLoginFlow = false;
              })
              .catch((error) => {
                this.isInLoginFlow = false;
                reject(error);
              });
          },
          (error) => {
            this.handleErrorResponse(error);
            this.isInLoginFlow = false;
            reject(error);
          }
        );
    });
  }

  // Sequence operations during login to reduce concurrent IndexedDB transactions
  private async sequenceLoginOperations(user: Data, isSignup: boolean): Promise<void> {
    try {
      const userId = this.localUserID;

      // Step 1: Critical session initialization
      if (userId) {
        this.clarityService.syncAuthenticatedUser(userId, this.pwaSegmentStatus);
        this.clarityService.setGa4UserId(userId);
        // These operations are lightweight and don't require heavy Firestore transactions
        this.userDetailsForLiveChat();
        this.setCurrentSessionId();
        this.updateDismissedPushNotification();
        const body = { Session_ID: userId };
        this.GTMEventPopupInitialization("Session Start", body);
      }

      // Step 2: Check breakdown visited status
      this.checkBreakDownVisited();

      // Send segment data
      this.segmentRequestData$.next({ email: user?.user?.email, name: user?.user?.displayName });

      // Step 3: Update login status - lightweight operation
      this.userManagementService.updateIsLoggedIn(user?.user);

      // Step 4: Process user security checks with delays to avoid concurrent transactions
      // These are spread out to reduce IndexedDB contention
      await new Promise(resolve => setTimeout(resolve, 500));
      this.callversionCF(user?.user?.uid);

      await new Promise(resolve => setTimeout(resolve, 500));
      this.userManagementService.uploadUserFingerprintInfo(user?.user?.uid, CommonVariable.FingerprintJSConst);

      await new Promise(resolve => setTimeout(resolve, 500));
      this.checkChurnRisk(user?.user?.uid);

      // Step 5: Delay fraud check to avoid IndexedDB contention
      setTimeout(() => {
        this.userManagementService.checkFruadUser(user.user.uid, CommonVariable.FingerprintJSConst);
      }, 2000);

      // Step 6: Handle user navigation based on account state
      await this.handleUserNavigation(user, isSignup);

    } catch (error) {
      throw new Error(`Login sequence failed: ${error.message}`);
    }
  }

  private async handleUserNavigation(user: Data, isSignup: boolean): Promise<void> {
    // For users without details
    if (!user?.userDetails) {
      if (
        isSignup &&
        (!this.marketingCampaignsService.getInvitationData() ||
          !this.marketingCampaignsService.getInvitationData()?.psId)
      ) {
        // Sequence these operations with delays
        this.uploadUserUdid(user?.user?.uid, [CommonVariable.FingerprintJSConst]);

        await new Promise(resolve => setTimeout(resolve, 300));
        this.notificationsService.sendWelcomeEmail();

        await new Promise(resolve => setTimeout(resolve, 300));
        this.homeDataService.newMemberChecklistCard(user?.user?.uid);

        await new Promise(resolve => setTimeout(resolve, 300));
        this.Clevertap_profile_push(user?.user?.phoneNumber);

        this.router.navigateByUrl("/syndicate/fork1");
        return;
      }

      this.getUserUdid(user?.user?.uid, CommonVariable.FingerprintJSConst);

      await new Promise(resolve => setTimeout(resolve, 300));
      this.homeDataService.newMemberChecklistCard(user?.user?.uid);

      this.router.navigateByUrl(this.redirectTo);
      return;
    }

    // For users that need seeding
    if (!user?.userDetails?.seeding) {
      if (!user || !user?.user || !user?.user?.uid) return;

      this.utilityService.seedUserData(user?.user?.uid).subscribe();

      await new Promise(resolve => setTimeout(resolve, 300));
      this.homeDataService.newMemberChecklistCard(user?.user?.uid);

      await new Promise(resolve => setTimeout(resolve, 300));
      this.Clevertap_profile_push(user?.user?.phoneNumber);

      this.router.navigateByUrl(`/holdingScreen?redirectTo=${this.redirectTo}`);
    } else {
      // For users with seeding complete
      await new Promise(resolve => setTimeout(resolve, 300));
      this.homeDataService.newMemberChecklistCard(user?.user?.uid);

      await new Promise(resolve => setTimeout(resolve, 300));
      this.Clevertap_profile_push(user?.user?.phoneNumber);

      if (
        isSignup &&
        (!this.marketingCampaignsService.getInvitationData() ||
          !this.marketingCampaignsService.getInvitationData()?.psId)
      ) {
        this.router.navigateByUrl("/syndicate/fork1");
        return;
      }
      this.router.navigateByUrl(this.redirectTo);
    }
  }


  Clevertap_profile_push(msn: string) {
    const timeDelay = 10000;

    setTimeout(() => {
      this.commonService.getUid("Clevertap_profile_push").pipe(take(1)).subscribe((userId) => {
        if (!userId) return;

        msn = msn.replace("+", "");
        const email = localStorage.getItem("email") ?? "";
        this.clevertapService.sendClevertapData(userId, {
          msn,
          email,
        });
      });
    }, timeDelay);
  }


  handleErrorResponse(error) {
    // Check if error is related to AppCheck
    const isAppCheckError =
      error?.code === 'permission-denied' ||
      error?.message?.toLowerCase().includes('appcheck') ||
      error?.message?.includes('Failed to get document because the client is offline');

    if (isAppCheckError) {
      this.getConstantsMsg("1026")
        .then((translatedMsg) => {
          const defaultMsg = {
            message: {
              line_1: "We couldn't complete the security check. Reload the page and try again.",
              line_2: "",
            },
            style: {
              icon: "tick circle error.svg",
              type: "Error",
            },
          };
          this.showToastMessage(translatedMsg, defaultMsg);
        })
        .catch(() => {
          // handle error
        });

      // Attempt to refresh the AppCheck token in the background
      const appCheckService = this.injector.get(AppCheckService);
      if (appCheckService && appCheckService.getToken) {
        appCheckService.getToken(true).catch(err => {
          // handle error
        });
      }

      this.isInLoginFlow = false;
      return;
    }
    // Check if error is related to offline or IndexedDB issues
    const isOfflineOrStorageIssue =
      !navigator.onLine ||
      (error?.message && (
        error.message.includes("IndexedDB") ||
        error.message.includes("Connection") ||
        error.message.includes("transaction")
      ));

    const tooManyTriesArray = [
      "auth/too-many-requests",
      "autorizzazione/troppe-richieste",
      "autenticación/demasiadas-solicitudes",
      "authentification/trop de demandes",
    ];

    // Set login flow flag to use fallback messages
    this.isInLoginFlow = true;

    // Toast message if user tries to login too many times with wrong password
    if (tooManyTriesArray.some((errorMessage) => error?.message?.includes(errorMessage))) {
      this.getConstantsMsg("1125")
        .then((translatedMsg) => {
          const message = "Too many login attempts. Try again later.";
          const defaultMsg = {
            message: {
              line_1: message,
              line_2: "",
            },
            style: {
              icon: "tick circle error.svg",
              type: "Error",
            },
          };
          this.showToastMessage(translatedMsg, defaultMsg);
        })
        .catch((error) => {
          return error;
        });
      this.isInLoginFlow = false;
      return error;
    }

    const disableMessageTranslations = [
      { lang: "en", translation: "auth/user-disabled" },
      { lang: "es", translation: "autenticación/usuario deshabilitado" },
      { lang: "fr", translation: "authentification/utilisateur désactivé" },
      { lang: "it", translation: "autenticazione/utente disabilitato" },
      { lang: "de", translation: "Authentifizierung/Benutzer deaktiviert" },
    ];
    const disableMessage = disableMessageTranslations.find(
      (disableMessage) => error?.message?.search(disableMessage?.translation) > -1
    );
    if (disableMessage) {
      this.getConstantsMsg("1126")
        .then((translatedMsg) => {
          const defaultMsg = {
            message: {
              line_1:
                "This account is disabled. Contact support@lottosocial.com for help.",
              line_2: "",
            },
            style: {
              icon: "tick circle error.svg",
              type: "Error",
            },
          };
          this.showToastMessage(translatedMsg, defaultMsg);
        })
        .catch((error) => {
          return error;
        });
      this.isInLoginFlow = false;
      return;
    }

    if (error?.message?.search("wrong-password") > -1) {
      this.getConstantsMsg("1113")
        .then((translatedMsg) => {
          const message = "The mobile number or password is incorrect.";
          const defaultMsg = {
            message: {
              line_1: message,
              line_2: "",
            },
            style: {
              icon: "tick circle error.svg",
              type: "Error",
            },
          };
          this.showToastMessage(translatedMsg, defaultMsg);
        })
        .catch((error) => {
          return error;
        });
      this.isInLoginFlow = false;
      return error;
    }

    // For offline or IndexedDB connection issues, use the default message without Firestore
    if (isOfflineOrStorageIssue) {
      const defaultMsg = {
        message: {
          line_1: "We couldn't connect. Check your internet connection and try again.",
          line_2: "",
        },
        style: {
          icon: "tick circle error.svg",
          type: "Error",
        },
      };
      this.showToastMessage({}, defaultMsg);
      this.isInLoginFlow = false;
      return error;
    }

    /* const msg = this.translatedToastMsg?.["1026"]?.[this.selectedlanguage$.value];
    this.showToast(error ? error.message : msg ? msg : "Something went wrong. Please try again", "Error", ""); */
    this.getConstantsMsg("1026")
      .then((translatedMsg) => {
        const defaultMsg = {
          message: {
            line_1: error ? error?.message : "We couldn't log you in. Try again.",
            line_2: "",
          },
          style: {
            icon: "tick circle error.svg",
            type: "Error",
          },
        };
        this.showToastMessage(error ? error?.message : translatedMsg, defaultMsg);
      })
      .catch((error) => {
        return error;
      });
    this.isInLoginFlow = false;
  }

  // This function is used to seed the cloud function with user id.
  seedUserData(userId) {
    // return this.angularFireFunctions.httpsCallable("seed", { timeout: 180000 })(userId);
    return this.getV2CloudFunction("seedv2", userId, { timeout: 180000 });
  }

  // The following function is used to update the user's location details
  updateUserLocationDetails(userDetails: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const date = new Date().toDateString() + " " + new Date().toTimeString(); // Combined date and time as document ID
      userDetails["createdDate"] = new Date(); // Add createdDate field

      this.commonService.getUid("updateUserLocationDetails").pipe(take(1)).subscribe({
        next: (userId) => {
          if (!userId || !userDetails || typeof userDetails !== "object" || Array.isArray(userDetails)) {
            return reject(new Error("Invalid input")); // Handle invalid input
          }

          const path_id = "hS2pb7zofds4dD1OFqkO";
          const doc_data = userDetails;
          const dv = { docId: date };
          const method = "create";

          this.getV2CloudFunction("updatefirestoredoc", {
            path_id,
            doc_data,
            dv,
            method,
          }).subscribe({
            next: () => resolve(true),
            error: (err) => {
              reject(err);
            }
          });
        },
        error: (err) => reject(err),
      });
    });
  }


  // This function used for twilio MSN validation
  public validateTwilio(msn: string) {
    if (!msn) {
      throw new Error(`Error in validateTwilioMSN ${msn}`);
    }
    return this.v2CloudFunctionResponse(this.twilioValidationFunction({ msn: msn }), false);
  }

  /**
   * It takes a document, validates it, and returns a promise.
   * @param document - The document that is being validated.
   * @returns The return value is a promise.
   */
  validateApiPostReq(document: string) {
    return this.requestBodyService.validateApiPostReq(document).pipe(
      switchMap((payload) => {
        if (!payload) {
          return of(null);
        }
        return this.v2CloudFunctionResponse(
          this.callableFunction(payload)
        );
      })
    );
  }


  // This method is used for sales journey login with custom token
  salesJourneyLogin(value: any, isCurrent?: () => boolean) {
    const isActive = isCurrent ?? (() => true);
    var customToken = { customToken: value, user: null, isCurrentJourney: isCurrent };
    return new Promise((resolve, reject) => {
      if (!isActive()) {
        resolve(false);
        return;
      }
      of(customToken)
        .pipe(takeUntil(this.destroy$), this.tokenLogin)
        .pipe(takeUntil(this.destroy$), take(1))
        ?.subscribe(
          (user: any) => {
            if (!isActive()) {
              resolve(false);
              return;
            }
            this.loadingService.dismiss();
            this.segmentRequestData$.next({ email: user?.user?.email, name: user?.user?.displayName });
            if (typeof GTM_Segment === "function" || typeof GTM_Segment !== "undefined") {
              GTM_Segment();
            }
            this.userManagementService.updateIsLoggedIn(user?.user);
            resolve(true);
          },
          (error) => {
            if (!isActive()) {
              resolve(false);
              return;
            }
            this.loadingService.dismiss();
            if (error.message.search("disabled") > -1) {
              /* const msg = this.translatedToastMsg?.["1025"]?.[this.selectedlanguage$.value];
              this.showToast(
                msg
                  ? msg
                  : "Your account has been disabled now, if you see this as message an error, please contact customer support",
                "Error",
                ""
              ); */
              this.getConstantsMsg("1025")
                .then((translatedMsg) => {
                  if (!isActive()) return;
                  const defaultMsg = {
                    message: {
                      line_1:
                        "This account is disabled. Contact support@lottosocial.com for help.",
                      line_2: "",
                    },
                    style: {
                      icon: "tick circle error.svg",
                      type: "Error",
                    },
                  };
                  this.showToastMessage(translatedMsg, defaultMsg);
                })
                .catch((error) => {
                  return error;
                });

              return;
            }
            /*  const msg = this.translatedToastMsg?.["1026"]?.[this.selectedlanguage$.value];
             this.showToast(msg ? msg : "Something went wrong. Please try again", "Error", ""); */
            this.getConstantsMsg("1026")
              .then((translatedMsg) => {
                  if (!isActive()) return;
                const defaultMsg = {
                  message: {
                    line_1: "We couldn't log you in. Try again.",
                    line_2: "",
                  },
                  style: {
                    icon: "tick circle error.svg",
                    type: "Error",
                  },
                };
                this.showToastMessage(translatedMsg, defaultMsg);
              })
              .catch((error) => {
                return error;
              });
            reject(new Error('Operation failed'));
          }
        );
    });
  }

  /**
   * Keep anonymousId available while Firebase promotes the temporary user.
   * The 4C guard uses this ID while the DFCI custom-token login is pending.
   */
  async completeAnonymousSalesJourneyLogin(
    tempUser: any,
    customToken: string,
    registrationIdentityEpoch: number = this.captureRegistrationIdentityEpoch()
  ): Promise<boolean> {
    const anonymousId = tempUser?.user?.uid;
    const isCurrent = () => this.captureRegistrationIdentityEpoch() === registrationIdentityEpoch;
    if (!isCurrent()) return false;
    try {
      await tempUser?.user?.delete?.().catch(() => undefined);
      if (!isCurrent()) return false;
      const loggedIn = await this.salesJourneyLogin(customToken, isCurrent);
      return loggedIn !== false && isCurrent();
    } finally {
      // Never clear a newer journey's anonymous identity, including after an await.
      if (isCurrent() && anonymousId && localStorage.getItem("anonymousId") === anonymousId) {
        localStorage.removeItem("anonymousId");
      }
    }
  }

  // This function returns the referral data from the service.
  getReferralData() {
    return this.referralData;
  }

  setCahceMetaData() {
    const getImageURL = httpsCallable(this.functions, "getImageURL");

    getImageURL(1)
      .then((result: any) => {
        const data = result.data;
        data.map((i) => {
          setTimeout(() => {
            const path = i?.split("/o/")[1]?.split("?")[0].replace(/%2F/g, "/");
            if (path) {
              this.fbStorage
                .ref(path)
                .updateMetadata({ cacheControl: "private, max-age=15552000" })
                .pipe(takeUntil(this.destroy$), delay(1000))
                .subscribe();
            }
          }, 3000);
        });
      })
      .catch(() => {
        // handle error
      });
  }
  /**
   * It returns an observable that emits the onboarding status of the user and the user details.
   * @returns The return value is an observable that emits an array of two items. The first item is the
   * data from the firestore document. The second item is the userDetailsForHome$ observable.
   */
  getOnboardingStatus(): Observable<[any, any]> {
    return new Observable<[any, any]>((subscriber) => {
      this.commonService.getUid("getOnboardingStatus").pipe(take(1)).subscribe({
        next: (userId) => {
          if (!userId) {
            subscriber.next([null, null]);
            subscriber.complete();
            return;
          }

          const path = `users/${userId}/OnBoarding/list`;
          const docRef = doc(this.firestore, path);

          // Defense-in-depth gate (LOTTOSOCIAL-PWA-1MEE); getUid already waits.
          this.authStateService.customTokenReady$.pipe(take(1)).subscribe({
            next: () => {
              const unsubscribe = onSnapshot(
                docRef,
                {
                  next: (snapshot) => {
                    const data = snapshot?.data() || null;

                    // Get user details once
                    this.userManagementService.userDetailsForHome$.pipe(take(1)).subscribe({
                      next: (user) => {
                        subscriber.next([data, user]);
                      },
                      error: (err) => subscriber.error(err)
                    });
                  },
                  error: (err) => subscriber.error(err)
                }
              );

              // Clean up Firestore listener when unsubscribed
              subscriber.add(() => unsubscribe());
            },
            error: (err) => subscriber.error(err),
          });
        },
        error: (err) => subscriber.error(err),
      });
    }).pipe(
      takeUntil(this.destroy$),
      shareReplay(1)
    );
  }



  // This is a function that calls a callable function to reset a user's password.
  // The function takes in the new password and creates a request body using the requestBodyService.
  // It then calls the callable function and returns the result.
  resetPassword(newPassword) {
    return this.RequestUserService.resetPassword(newPassword).pipe(takeUntil(this.destroy$),
      switchMap((finalPayload) => {
        return this.v2CloudFunctionResponse(this.callableFunction(finalPayload))
      })
    );
  }

  // Updating the password of the user
  updatePassword(password) {
    return this.RequestUserService.updatePassword(password).pipe(
      switchMap((reqBody) =>
        this.v2CloudFunctionResponse(this.callableFunction(reqBody))
      )
    );
  }

  /**
   * This function is used for updating the custom msn
   * @param {Object} msn - The msn to update
   * @returns {Promise} Promise that will resolve when the update is complete
   */
  updateCustomeMsn(msn) {
    return this.v2CloudFunctionResponse(this.callableFunction(this.RequestUserService.updateCustomeMsn(msn)));
  }

  // This function is used to login with facebook account. It first initializes the auth object and then creates a provider object.
  // It then sets the language code to English and the display to popup. Finally, it calls the AuthLogin() function with the provider object as the argument.
  facebookAuth() {
    try {
      const auth = getAuth();
      let provider = new FacebookAuthProvider();
      auth.languageCode = CommonVariable?.preferredLanguage;
      provider.setCustomParameters({
        display: "popup",
      });
      return this.AuthLogin(provider);
    } catch (error) {
      throw new Error(error);
    }
  }

  // This function is used to login in the user via the GoogleAuthProvider
  // The user is redirected to a Google login page to authenticate
  // The user is then redirected back to the application
  // The user is then authenticated and signed in
  googleAuth() {
    let provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      display: "popup",
    });
    return this.AuthLogin(provider);
  }

  // this function is used to login using google provider
  AuthLogin(provider) {
    const auth = getAuth();
    signInWithPopup(auth, provider).catch((error) => {
      return error;
    });
  }

  // This function gets all events and returns them as an observable. It uses the idField option to change the id field from the default to "id".
  getEventsForTester(): Promise<any[]> {
    const eventsCollection = collection(this.firestore, "event_lookup"); // Reference to the Firestore collection

    return getDocs(eventsCollection).then((snapshot) => {
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    });
  }

  /**
   * It returns a promise that resolves to a document from a collection.
   * </code>
   * @param {string} id - string - the id of the prosub
   * @returns A promise that resolves to a DocumentSnapshot.
   */
  getLSWProsubDetails(id: string) {
    if (!id) return of(null); // Return observable

    return this.catchService.isOnline$.pipe(
      switchMap((isOnline) => {
        if (!isOnline) {
          return of(null);
        }

        const path_id = "ERzajuwYcQgv2qQ1tA9q";
        const dv = { docId: id };
        const method = "read";
        return this.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          dv,
          method,
        }).pipe(
          map((snapshot: any) => {
            return snapshot?.data || null; // return plain object
          }),
          this.catchService.retryWhen,
          this.catchService.catchOfflineError
        );
      }),
      this.catchService.retryWhen,
      this.catchService.catchOfflineError,
      shareReplay(1)
    );
  }

  /**
   * It takes a phone number as an argument and returns a promise that resolves to the result of the
   * function call.
   * @param userPhone - The phone number of the user.
   * @returns a promise.
   */
  validateuserOtpfb(userPhone) {
    // return this.angularFireFunctions.httpsCallable("validate")({ phoneNumber: userPhone });
    return this.getV2CloudFunction("validatev2", { phoneNumber: userPhone });
  }

  /**
   * "This function is called when a user signs up for the first time. It creates a new user in the
   * database and sends a verification code to the user's phone number."
   * </code>
   * @param userPhone - The phone number of the user
   * @param user_id - This is the user id of the user who is logged in.
   * @returns A promise.
   */
  createUserfb(userPhone: string, user_id: string) {
    return httpsCallable(this.functions, "createUser")({ phoneNumber: userPhone, user_id });
  }
  // Check user in SQL database
  checkuserExitIndb(userPhone) {
    // return this.angularFireFunctions.httpsCallable("checkUserInSql")({ phoneNumber: userPhone });
    return this.getV2CloudFunction("checkuserinsqlv2", { phoneNumber: userPhone });
  }
  /**
   * It takes a phone number as an argument and returns a promise that resolves to a password.
   * @param userPhone - The phone number of the user
   * @returns a promise.
   */userPassword(userPhone: string) {
    return httpsCallable(this.functions, "userPasswordIndb")({ phoneNumber: userPhone });
  }

  async getDvResolvedJson(dvObject: Data) {
    try {
      if (dvObject?.myString) {
        const referenceKeys = Object.keys(dvObject.myString).filter(
          key => dvObject.myString[key] instanceof DocumentReference
        );

        referenceKeys.forEach(key => {
          const ref = dvObject.myString[key];
          dvObject.myString[key] = ref.path;
        });
      }
      const functions = getFunctions();
      functions.region = AuthService.FIREBASE_REGION;
      const dvstringresolver = httpsCallable(functions, "dvstringresolver");
      return await dvstringresolver(dvObject);
    } catch (error) {
      return dvObject;
    }
  }
  //This function is used to call housekeeping CF we move further
  pwaHouseKepping(userId: string) {
    //V2
    this.getV2CloudFunction("housekeepingv2", userId)
      ?.pipe(takeUntil(this.destroy$), catchError((err) => throwError(() => err)))
      ?.subscribe(() => {
        localStorage.setItem("pwaHouseKeeping", "true");
      });
  }

  /**
   * It returns the data of a document from a collection in Firestore.
   * </code>
   * @param {string} offerId - string - the id of the offer
   * @returns The return value is an Observable of the document data.
   */
 getOfferDetail(offerId: string, idType?: string) {
    if (idType === "hofferid") {
      const offersCollection = collection(this.firestore, `/general_modules/offer_listing_v1/offers`); // Reference to the collection
      const offersQuery = query(offersCollection, where("hofferid", "==", offerId)); // Query with condition
 
      return of(offersQuery).pipe(takeUntil(this.destroy$),
        switchMap(() => getDocs(offersQuery)), // Fetch query results
        map((snapshot) =>
          snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))[0] // Take the first result
        ),
        shareReplay(1) // Cache results
      );
    }
 
    const offerDocRef = doc(this.firestore, `/general_modules/offer_listing_v1/offers/${offerId}`); // Reference to specific document
 
    return of(offerDocRef).pipe(takeUntil(this.destroy$),
      switchMap(() => getDoc(offerDocRef)), // Fetch the document
      map((snapshot) => snapshot.data()), // Extract document data
      shareReplay(1) // Cache results
    );
  }
  // Skip overlapping / repeat readvoucherv2 in the same session.
  private readAllVouchersInFlight = false;
  private readAllVouchersCompleted = false;
  private readAllVouchersSession = 0;
  private readAllVouchersSub?: Subscription;

  private resetReadAllVouchersSession(): void {
    this.readAllVouchersSession += 1;
    this.readAllVouchersSub?.unsubscribe();
    this.readAllVouchersSub = undefined;
    this.readAllVouchersInFlight = false;
    this.readAllVouchersCompleted = false;
  }

  // This function will be called when the user wants to read all vouchers
  // that are associated with their user ID. It will call the readVoucher
  // function from the backend.
  readAllVouchers() {
    if (this.readAllVouchersInFlight || this.readAllVouchersCompleted) {
      return;
    }
    this.readAllVouchersInFlight = true;
    const session = this.readAllVouchersSession;
    this.readAllVouchersSub = this.commonService.getUid("readAllVouchers").pipe(
      take(1),
      filter((userId): userId is string => !!userId), // Only proceed if userId exists
      switchMap((userId) => {
        const call = this.getV2CloudFunction("readvoucherv2", { userId });
        return call ? call : EMPTY;
      }),
      catchError((err) => {
        console.error('[readAllVouchers] Error reading vouchers:', err);
        return EMPTY;
      }),
      finalize(() => {
        if (session === this.readAllVouchersSession) {
          this.readAllVouchersInFlight = false;
        }
      })
    ).subscribe({
      next: (data) => {
        if (session !== this.readAllVouchersSession) {
          return;
        }
        if (data != null) {
          this.readAllVouchersCompleted = true;
        }
      },
    });
  }


  /**
   * Get user info card list
   * @param userId User id
   * @returns User info card list
   */
  getUserInfoCardList(userId: string) {
    const docRef = doc(this.firestore, `user_info_card_list/${userId}`);
    return docData(docRef).pipe(takeUntil(this.destroy$),
      map((res: any) => res),
      shareReplay(1)
    );
  }

  // This function gets the device info from the device
  private async getDeviceInfo() {
    CommonVariable.device = await Device.getInfo();
  }

  /**
   * It returns a promise that resolves to an object with two properties: message and style.
   * @param msgCode - The code of the message you want to get.
   * @returns {
   *   message: "",
   *   style: "success"
   * }
   */
  // Fallback messages map for critical operations when Firestore is unavailable
  private fallbackMessages = {
    "1026": {
      message: {
        line_1: "We couldn't log you in. Try again.",
        line_2: "",
      },
      style: {
        icon: "tick circle error.svg",
        type: "Error",
      }
    },
    "1083": {
      message: {
        line_1: "Your browser is preventing this site from saving information. Turn off private browsing or use another browser.",
        line_2: "",
      },
      style: {
        icon: "tick circle alert.svg",
        type: "warning",
      }
    },
    "1113": {
      message: {
        line_1: "The mobile number or password is incorrect.",
        line_2: "",
      },
      style: {
        icon: "tick circle error.svg",
        type: "Error",
      }
    },
    "1125": {
      message: {
        line_1: "Too many login attempts. Try again later.",
        line_2: "",
      },
      style: {
        icon: "tick circle error.svg",
        type: "Error",
      }
    },
    "1126": {
      message: {
        line_1: "This account is disabled. Contact support@lottosocial.com for help.",
        line_2: "",
      },
      style: {
        icon: "tick circle error.svg",
        type: "Error",
      }
    }
  };

  // Check if we're in login flow to use fallback messages
  private isInLoginFlow = false;

  private getFallbackMessage(msgCode: string): Data {
    return this.fallbackMessages[msgCode] || null;
  }

  public async getConstantsMsg(msgCode: any): Promise<any> {
    try {
      /* 1. Offline / login-flow short-circuit ------------------------------ */
      if (!navigator.onLine || this.isInLoginFlow) {
        return this.getFallbackMessage(msgCode) ?? null;
      }

      /* 2. Fetch the document with the modular SDK ------------------------- */
      const ref = doc(this.firestore, `Global_lookup/toast_message/list/${msgCode}`);
      const snap = await getDoc(ref);

      if (!snap.exists()) return null;

      const msgData = snap.data() as {
        message?: Record<string, string>;
        style?: Record<string, unknown>;
      };

      if (!msgData) return null;

      /* 3. Language resolution -------------------------------------------- */
      const userLang = this.selectedlanguage$?.value;
      const fallbackLang = CommonVariable?.preferredLanguage;

      const toastMsg =
        msgData.message?.[userLang] ??
        msgData.message?.[fallbackLang] ??
        '';

      /* 4. Build the response object -------------------------------------- */
      return {
        message: toastMsg,
        style: msgData.style ?? {}
      };
    } catch {      // graceful degradation – fall back to cached/static copy
      return this.getFallbackMessage(msgCode) ?? null;
    }
  }


  /**
   * It takes a translated message and a default message, and then it shows a toast message with the
   * translated message if it exists, otherwise it shows the default message.
   * </code>
   * @param translatedMsg - The translated message object
   * @param defaultMsg - This is the default message that will be shown if the translation is not
   * found.
   */
  public showToastMessage(translatedMsg, defaultMsg): void {
    this.toastFacade.showInline({
      message: translatedMsg?.message ?? defaultMsg?.message,
      style: {
        icon: translatedMsg?.style?.icon ?? defaultMsg?.style?.icon,
        type: translatedMsg?.style?.type ?? defaultMsg?.style?.type,
        duration_ms: translatedMsg?.style?.duration_ms ?? translatedMsg?.style?.duration ?? defaultMsg?.style?.duration_ms ?? 5000,
      },
    });
  }

  /**
   * It opens a snackbar with a custom component, and passes data to that component.
   * </code>
   * @param message - The message you want to display in the toast.
   * @param snackType - This is the type of snackbar you want to show. It can be success, error,
   * warning, info.
   * @param description - This is the message that will be displayed in the toast.
   */
  showToast(message, snackType, description) {
    this.snackBar.openFromComponent(MyToastbarComponent, {
      duration: 5000,
      horizontalPosition: "center",
      verticalPosition: "top",
      panelClass: "toast-for-syndicate",
      data: { message: message, state: snackType, description: description },
    });
  }

  /* Getting the currency data from the firestore and converting it to JSON. */
  getCurrencyList$ = from(
    getDoc(doc(this.firestore, '/general_modules/currency'))
  ).pipe(takeUntil(this.destroy$),
    map((docSnapshot: Data) => {
      if (docSnapshot.exists()) {
        const currencyList: any = docSnapshot.data();
        return {
          details: currencyList?.details?.filter((currency: any) => currency.status === true),
        };
      }
      return {};
    }),
    catchError(() => {
      return of({});
    }),
    shareReplay(1)
  );

  // This function is used to get all DVs from the string
  getDVFromString(myString) {
    return new Promise((resolve, reject) => {
      try {
        if (myString) {
          let myStringLen = myString?.length;
          let start = 0;
          let end = 0;
          let start1 = 0;
          let end1 = 0;
          let start2 = 0;
          let end2 = 0;
          const dvReplaceObj: any = [];
          do {
            start = myString?.indexOf("[currency_beautify:", end);
            start1 = myString?.indexOf("[currency_node:", end1);
            start2 = myString?.indexOf("[currency_actual:", end2);
            if (start !== -1) {
              end = myString?.indexOf("]", start);

              let dvToReplace = myString?.substring(start, end + 1);
              dvReplaceObj.push(dvToReplace);
            }
            if (start1 !== -1) {
              end1 = myString?.indexOf("]", start1);
              let dvToReplace = myString?.substring(start1, end1 + 1);
              dvReplaceObj.push(dvToReplace);
            }
            if (start2 !== -1) {
              end2 = myString?.indexOf("]", start2);
              let dvToReplace = myString?.substring(start2, end2 + 1);
              dvReplaceObj.push(dvToReplace);
            }
          } while (start !== -1 || start1 !== -1 || start2 !== -1);
          let loopCount: number = 0;
          if (dvReplaceObj?.length && Array.isArray(dvReplaceObj)) {
            var dvLength = dvReplaceObj?.length;
            dvReplaceObj.forEach((dvReplaceElement, index) => {
              this.getDVCurrency(dvReplaceElement, myString, index)
                .then((dvReplaceElementResp) => {
                  myString = myString?.replace(dvReplaceElement, dvReplaceElementResp);
                  loopCount++;
                  if (loopCount >= dvLength) {
                    resolve(myString);
                  }
                })
                .catch((err) => {
                  reject(err);
                });
            });
          } else {
            resolve(myString);
          }
        } else {
          resolve(myString);
        }
      } catch (err) {
        reject(err);
      }
    });
  }

  // This method is used to get all DVs from currency for conversion
  getDVCurrency(dvData: any, inputText: any, index: number) {
    const isEmpty = (val: any) =>
      val === undefined || val === null || (typeof val === "object" && Object.keys(val)?.length === 0) || !val?.length;
    return new Promise((resolve, reject) => {
      try {
        if (!isEmpty(dvData) && !isEmpty(inputText)) {
          dvData = dvData.replace(",", "");
          let formatedDVData = dvData.replace("[", "").replace("]", "");
          let result = formatedDVData?.split(":");
          let dvKey1 = result[0];
          let dvKey2 = dvKey1 === "currency_beautify" ? Number(result[1])?.toFixed(2) : result[1];
          let preferredCurrency: string = CommonVariable.preferredCurrency;
          let currencySymbol: string = "£";

          this.userDetails$.pipe(takeUntil(this.destroy$), take(1)).subscribe(
            (user: Data) => {
              preferredCurrency = this.isPayPalSignal() ? CommonVariable.preferredCurrency :
                (user?.preferred_currency !== ""
                  ? user?.preferred_currency
                  : this.userManagementService.selectedCurrency);
              this.preferredLanguage = user?.preferred_language ?? CommonVariable.preferredLanguage;
              CommonVariable.preferredLanguage = user?.preferred_language;
              this.getCurrencyList$?.subscribe((data: Data) => {
                const CurrencyDetails = data?.details?.find((x: any) => x?.currency_code === preferredCurrency);
                currencySymbol = CurrencyDetails?.currency_symbol ? CurrencyDetails?.currency_symbol : "£";

                if (dvKey1 === "currency_beautify") {
                  let getBeautifyObj;
                  // if (Number(dvKey2) !== 0 && Number(dvKey2) <= 100) {
                  this.paymentDataService
                    .getBeautifiedCurrency(dvKey2, preferredCurrency)
                    .then((beautifiedAmt) => {
                      if (beautifiedAmt) {
                        getBeautifyObj = currencySymbol + beautifiedAmt;
                      } else {
                        getBeautifyObj = currencySymbol + "0";
                      }
                      resolve(getBeautifyObj);
                    })
                    .catch((err) => {
                      reject(err);
                    });
                  /*  } else {
                   getBeautifyObj = currencySymbol + "0";
                   resolve(getBeautifyObj);
                 } */
                } else if (dvKey1 === "currency_actual") {
                  let getActualRateObj;
                  if (Number(dvKey2) !== 0) {
                    this.paymentDataService
                      .getBeautifiedCurrency(dvKey2, preferredCurrency, dvKey1)
                      ?.then((beautifiedAmt: any) => {
                        if (beautifiedAmt) {
                          if (preferredCurrency === "GBP") {
                            const parsedAmount = Number(String(beautifiedAmt).replace(/,/g, ""));
                            const safeAmount = Number.isFinite(parsedAmount) && parsedAmount > 0 ? parsedAmount : 0;
                            const roundedAmount = Math.round((safeAmount + Number.EPSILON) * 100) / 100;
                            if (roundedAmount >= 0.01 && roundedAmount < 1) {
                              getActualRateObj = `${Math.round(roundedAmount * 100)}p`;
                            } else {
                              getActualRateObj = `${currencySymbol}${formatDisplayCurrencyAmount(roundedAmount)}`;
                            }
                          } else {
                            const getActualAmt = Number(beautifiedAmt).toLocaleString("en-US", {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 2,
                            });
                            getActualRateObj = currencySymbol + getActualAmt;
                          }
                        } else {
                          getActualRateObj = currencySymbol + "0";
                        }
                        resolve(getActualRateObj);
                      })
                      .catch((err) => {
                        reject(err);
                      });
                  } else {
                    getActualRateObj = currencySymbol + "0";
                    resolve(getActualRateObj);
                  }
                } else if (dvKey1 === "currency_node") {
                  let getNodeObj;
                  let currencyVariable = "int_" + dvKey2;
                  let currencyNodeAmt;
                  if (index === 0 && result[1] == "gift") {
                    currencyNodeAmt = this.currencyNodeDvData?.[currencyVariable]?.[preferredCurrency] + "0";
                  } else {
                    currencyNodeAmt = this.currencyNodeDvData?.[currencyVariable]?.[preferredCurrency];
                  }
                  if (currencyNodeAmt >= 0.01 && currencyNodeAmt <= 0.09) {
                    currencyNodeAmt = Number(currencyNodeAmt)?.toFixed(2);
                  } else if (currencyNodeAmt >= 0.1 && currencyNodeAmt <= 2.9 && result[1] !== "gift") {
                    currencyNodeAmt = Number(currencyNodeAmt);
                  } else {
                    result[1] !== "gift" ? currencyNodeAmt : "";
                  }
                  // Drop trailing ".00" for whole, non-gift node values; preserve gift handling
                  // and the existing low-range fractional formatting above.
                  if (result[1] !== "gift" && currencyNodeAmt !== "" && currencyNodeAmt != null) {
                    const numericNodeAmt = Number(currencyNodeAmt);
                    if (Number.isInteger(numericNodeAmt)) {
                      currencyNodeAmt = String(numericNodeAmt);
                    }
                  }
                  getNodeObj = currencySymbol + currencyNodeAmt;

                  resolve(getNodeObj);
                }
              });
            },
            (err) => {
              reject(err);
            }
          );
        } else {
          reject(new Error('Required condition not met'));
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  // method to create a new document in specified path and return the id of new document
  private async addDocumentForAsyncModuleCall(): Promise<string> {
    const userId = await this.commonService.getUid("addDocumentForAsyncModuleCall").pipe(take(1)).toPromise();

    if (!userId) {
      throw new Error("User ID is not available");
    }

    try {
      const path_id = "uk3EJWK1yDt2hols9n6E";
      const doc_data = {
        expected_time_sec: 60,
        timeout_msg: false,
        user_outcome_message: "",
        late_response_inbox_msg: false,
      };
      const method = "create";
      const response: any = await lastValueFrom(
        this.getV2CloudFunction("updatefirestoredoc", { path_id, doc_data, method })
      );
      const docId = response?.path?.substring(response.path.lastIndexOf("/") + 1) || null;

      this.matDialog.open(ApiLoaderComponent, {
        panelClass: "api-loader-dialog-size",
        backdropClass: "decline-back-drop-color",
        data: docId,
      });

      return docId;
    } catch (error) {
      throw error;
    }
  }



  /**
   * It's a function that calls a cloud function that calls a third party API.
   * </code>
   * @param cerditAmount - The amount of credit you want to add to the user's account.
   * @returns The return value is a promise.
   */
  creditAutoRefresh(creditAmount) {
    return defer(() => this.addDocumentForAsyncModuleCall()).pipe(
      takeUntil(this.destroy$),
      switchMap((asyncModuleCallDocId) =>
        this.requestBodyService?.refreshToken(creditAmount, asyncModuleCallDocId).pipe(
          switchMap((body) => {
            if (!body) {
              return of(null);
            }
            return this.getV2CloudFunction("autocreditrefreshapiv2", body);
          })
        )
      ),
      catchError((error) => {
        throw error;
      })
    );
  }


  /**
   * It's a function that calls a cloud function for refresh token
   * @param cerditAmount - number
   * @returns The return value is a promise.
   */
  creditAutoRefreshOff(creditAmount) {
    return from(this.addDocumentForAsyncModuleCall()).pipe(
      takeUntil(this.destroy$),
      switchMap((asyncModuleCallDocId) =>
        this.requestBodyService?.refreshTokenOff(creditAmount, asyncModuleCallDocId).pipe(
          switchMap((body) => {
            if (!body) {
              return of(null);
            }
            return this.getV2CloudFunction("autocreditrefreshapiv2", body);
          })
        )
      ),
      catchError((error) => {
        throw error;
      })
    );
  }


  /**
   * "This function is used to turn on the credit refresh notification for the user."
   * </code>
   * @returns The return value is a promise.
   */
turnOnCreditRefresh(): void {
  this.commonService.getUid("turnOnCreditRefresh").pipe(
    take(1),
    tap((uid) => {
      if (!uid) throw new Error("User ID is not available");
    }),
    switchMap((uid) =>
      from(getDoc(doc(this.firestore, `/users/${uid}/in_app_notification/status`))).pipe(
        map((snapshot) => ({ uid, data: snapshot.data() as Data }))
      )
    ),
    switchMap(({ uid, data }) => {
      // 1. Read promo_details
      const promoDetails$ = this.getV2CloudFunction("updatefirestoredoc", {
        path_id: "1cblQl68QltRugb1YDAY",
        method: "read",
      }).pipe(
        map((snapshot: Data) => snapshot?.promo_details || []),
        catchError(() => {
          return of([]);
        }),
        switchMap((promoDetails: Data[]) => {
          // 2. Update promo_details
          const updatedPromoDetails = promoDetails.map(promo =>
            promo.promo_name === "activate_auto_topup"
              ? { ...promo, status: true }
              : promo
          );
          return this.getV2CloudFunction("updatefirestoredoc", {
            path_id: "1cblQl68QltRugb1YDAY",
            doc_data: { promo_details: updatedPromoDetails },
            method: "update",
          }).pipe(
            catchError(() => {
              return of(null);
            })
          );
        })
      );

      // 3. Update credit_refresh
      const doc_data = data?.out_of_points_promotion
        ? {
            out_of_points_promotion: { updatedOn: new Date() },
            credit_refresh: { status: true, low_trigger_threshold: 1, updatedOn: new Date() },
          }
        : {
            credit_refresh: { status: true, low_trigger_threshold: 1, updatedOn: new Date() },
          };

      const creditRefresh$ = this.getV2CloudFunction("updatefirestoredoc", {
        path_id: "KdpTQsnyjKvgqvjItVvT",
        doc_data,
        method: "update",
      }).pipe(
        catchError(() => {
          return of(null);
        })
      );

      // 4. Run both updates in parallel and wait for both to complete
      return forkJoin([promoDetails$, creditRefresh$]).pipe(
        tap(() => {
          this.clarityService.event("auto_topup_activated");
        })
      );
    }),
    catchError(() => {
      return of(null);
    })
  ).subscribe();
}


  /**
   * I'm trying to send a message to a user's inbox using the Firebase Cloud Functions SDK
   * @returns a promise.
   */
  sendCreditRefreshInboxMessage(): Observable<any> {
      return of({
      status: 'deprecated',
      message: 'creditRefreshInbox is no longer active'
    });
  }


  /** True when Turnstile/credential payload is safe to reuse or treat as a completed decision. */
  private isSignupValidationResult(data: Data): boolean {
    const hasToken = typeof data?.customToken === "string" && data.customToken.trim() !== "";
    const hasDecision = typeof data?.customerFound === "boolean" &&
      typeof data?.credentialMatched === "boolean" &&
      (data.customerFound || !data.credentialMatched);
    return !!data && typeof data === "object" && !Array.isArray(data) &&
      data.success !== false && !data.error && (hasToken || hasDecision);
  }

  /**
   * It takes a user object, calls a function on the server, and if the server returns a customToken,
   * it logs the user in.
   * If the server returns a customToken, the user is logged in.
   * If the server returns
   * @param {CreateUser} user - CreateUser
   * @param {Data} [cp_request] - optional checkout/purchase request
   * @param {Data} [signupValidationData] - optional same-submission Turnstile validation result
   */
  validateSignupPartialReg(
    user: CreateUser,
    cp_request?: Data,
    signupValidationData?: Data,
    registrationIdentityEpoch?: number
  ) {
    if (registrationIdentityEpoch === undefined) {
      this.resetRegistrationIdentityCache();
    }
    const expectedEpoch = registrationIdentityEpoch ?? this.captureRegistrationIdentityEpoch();
    const isCurrent = () => this.captureRegistrationIdentityEpoch() === expectedEpoch;

  return new Promise((resolve, reject) => {
    const ignoreStale = () => {
      if (isCurrent()) return false;
      resolve(null);
      return true;
    };
    user.language_code = this.selectedlanguage$?.value;
    user.preferred_currency = this.userManagementService?.selectedCurrency ?? "GBP";

    const encryptedPasswordUser = {
      ...user,
      password: user?.password,
      encrypted: false,
    };
    user.encrypted = false;

    // Reuse only this submission's completed server validation. Older backends
    // omit the marker and retain the standalone validation path.
    const callAuth = signupValidationData?.signup_validation_complete === true
      ? of(signupValidationData)
      : this.getV2CloudFunction("validatesalesignupv2", encryptedPasswordUser);
    return callAuth
      .pipe(
        takeUntil(this.destroy$),
        switchMap((data: any) =>
            from((async () => {
          if (ignoreStale()) return false;
          if (signupValidationData?.signup_validation_complete === true && !this.isSignupValidationResult(data)) {
            throw new Error("We couldn't validate your account. Please try again.");
          }
          // ✅ Existing Firebase user
          if (data?.customToken) {
            const sjLogin = await this.salesJourneyLogin(data?.customToken, isCurrent);
            if (ignoreStale()) return false;
            const loginService = this.injector.get(LoginDataResolverService);
            loginService.insertCustomerPurchaseAndCreateCheckout(cp_request);
            resolve({ sjLogin, existingCustomerInFb: true });
            return true;
          }

          // ✅ SQL user exists, wrong password
          if (!data?.credentialMatched && data?.customerFound) {
            resolve(data);
            return true;
          }
          // ✅ Anonymous login
          const tempUser = await this.loginAnonymously();
          if (ignoreStale()) return false;
          localStorage.setItem("anonymousId", tempUser?.user?.uid);

          this.createInstantUserInSql(user, tempUser?.user?.uid).subscribe(() => {
            if (ignoreStale()) return;
            // SQL-only users request checkout after their identity promotion below.
            if (!data.customerFound) {
              const loginService = this.injector.get(LoginDataResolverService);
              loginService.insertCustomerPurchaseAndCreateCheckout(cp_request);
            }
          });

          // ✅ NEW CUSTOMER SIGNUP FLOW
          // Do not resolve until minimum readiness (SQL signup SUCCESS + customer_id).
          // Early resolve left newRegistrationFlow racing a 10s/45s wait while signup was still in flight.
          if (!data.customerFound) {
            this.reportRegistrationTiming("callSigUpForSales");
            this.callSigUpForSales(user, tempUser?.user?.uid).subscribe(
              (res) => {
                if (ignoreStale() || !this.applyRegistrationIdentityResponse(res, expectedEpoch)) return;
                this.reportRegistrationTiming("callSigUpForSalesResponse$", {
                  status: res?.response?.status,
                  hasCustomerId: !!res?.response?.customer_id,
                });
                if (res?.response?.status === "SUCCESS" && res?.response?.customer_id) {
                  // Snapshot before resolve — validateAndRegisterUser finalize may clear regFlowTiming
                  // while createFbUser / login still emit stage logs.
                  const timingSnapshot = this.regFlowTiming;
                  // Minimum readiness for new_registration handoff — UI can proceed; createFbUser continues.
                  resolve(tempUser);
                  this.reportRegistrationTiming("createFbUser", undefined, timingSnapshot);
                  this.createFbUser(user, res?.response).subscribe(
                    (createFbUserRes: Data) => {
                      if (ignoreStale()) return;
                      this.reportRegistrationTiming("createFbUser_complete", {
                        success: !!createFbUserRes?.success,
                        hasCustomToken: !!createFbUserRes?.customToken,
                      }, timingSnapshot);
                      if (createFbUserRes?.success && createFbUserRes?.customToken) {
                        this.completeAnonymousSalesJourneyLogin(tempUser, createFbUserRes?.customToken, expectedEpoch).catch((error) => {
                          if (ignoreStale()) return;
                          this.reportRegistrationTiming("completeAnonymousSalesJourneyLogin_failed", {
                            message: "Your account was created, but we couldn't log you in. Log in to continue.",
                            error,
                          }, timingSnapshot);
                        });
                      } else {
                        this.reportRegistrationTiming("createFbUser_unsuccessful", {
                          message: createFbUserRes?.message,
                        }, timingSnapshot);
                      }
                    },
                    (err) => {
                      if (ignoreStale()) return;
                      this.createFbUserErrorHandling();
                      this.reportRegistrationTiming("createFbUser_error", { error: err }, timingSnapshot);
                    }
                  );
                } else {
                  this.reportRegistrationTiming("callSigUpForSales_failed", {
                    message: res?.message || res?.response?.message,
                  });
                  reject({
                    message: res?.message || res?.response?.message || "Registration failed. Please try again.",
                  });
                }
              },
              (err) => {
                if (ignoreStale() || !this.applyRegistrationIdentityResponse(err, expectedEpoch)) return;
                this.reportRegistrationTiming("callSigUpForSales_error", { error: err });
                reject(err);
              }
            );

            return true;
          }

          // ✅ SYNC FLOW (Existing SQL user + password matched)
          if (data?.credentialMatched && data?.customerFound) {
            this.callSyncForSales(user, tempUser?.user?.uid).subscribe(
              (res) => {
                if (ignoreStale()) return;
                if (res?.response?.status === "SUCCESS" && res?.response?.customer_id) {
                  this.createFbUser(user, res?.response).subscribe(
                    (createFbUserRes: Data) => {
                      if (ignoreStale()) return;
                      if (createFbUserRes?.success && createFbUserRes?.customToken) {
                        this.completeAnonymousSalesJourneyLogin(tempUser, createFbUserRes?.customToken, expectedEpoch)
                          .then((completed) => {
                            if (!completed || ignoreStale()) return;
                            const loginService = this.injector.get(LoginDataResolverService);
                            loginService.insertCustomerPurchaseAndCreateCheckout(cp_request);
                          })
                          .catch((error) => {
                            if (ignoreStale()) return;
                            reject({ message: "Your account was created, but we couldn't finish setup. Log in again to continue.", originalError: error });
                          });
                      } else {
                        reject({ message: createFbUserRes?.message });
                      }
                    },
                    (err) => {
                      if (ignoreStale()) return;
                      this.createFbUserErrorHandling();
                      reject(err);
                    }
                  );
                }
              },
              (err) => {
                if (ignoreStale()) return;
                reject(err);
              }
            );

            resolve(tempUser);
            return true;
          }

          return true;
          })())
        ),
        catchError((e) => {
          if (ignoreStale()) return EMPTY;
          reject(e);
          return throwError(e);
        })
      )
      .subscribe(
        () => {},
        (err) => {
          if (ignoreStale()) return;
          reject(err);
        }
      );
  });
}


  /**
     * The function `validateAndRegisterUser` validates user signup data and determines the type of
     * registration based on the validation results.
     */
  /**
     * The function `validateAndRegisterUser` validates user signup data and determines the type of
     * registration based on the validation results.
     */
validateAndRegisterUser(user: CreateUser, cp_request: Data): Observable<Data> {
  return defer(() => {
  // Every new or repeat-user submission supersedes prior background registration work.
  this.resetRegistrationIdentityCache();
  const registrationIdentityEpoch = this.captureRegistrationIdentityEpoch();
  const isCurrent = () => this.captureRegistrationIdentityEpoch() === registrationIdentityEpoch;
  const isTurnstileEnabled = !!(user.turnstileToken && environment.turnstile?.enabled);
  const proceedWithRegistration = (signupValidationData?: Data): Observable<Data> => {
    return this.networkStatusService.getNetworkQuality().pipe(
      takeUntil(this.destroy$),
      take(1),
      switchMap(quality => {
        if (!isCurrent()) return EMPTY;
        if (quality === "offline") {
          return throwError(() => new Error("You're offline. Your registration has been saved and will continue when you're back online."));
        }
        const partialStart = Date.now();
        const prosubId = String(
          (user as Data)?.prosub_id ?? cp_request?.prosub_id ?? localStorage.getItem("prosub_id") ?? ""
        );
        this.regFlowTiming = {
          correlationId: `reg-${partialStart}-${Math.random().toString(36).slice(2, 10)}`,
          prosubId,
          startedAt: partialStart,
        };
        this.reportRegistrationTiming("validateAndRegisterUser");

        return from(this.validateSignupPartialReg(user, cp_request, signupValidationData, registrationIdentityEpoch)).pipe(
          takeUntil(this.destroy$),
          timeout({
            each: 45000,
            with: () => {
              this.reportRegistrationTiming("validateAndRegisterUser_timeout", {
                timeoutStage: "validateAndRegisterUser",
                waitMs: 45000,
              });
              return throwError(() => new Error("Registration is taking longer than expected. Check your connection and try again."));
            },
          }),
          catchError(error => {
            if (!isCurrent()) return EMPTY;
            this.errorHandlerLogs("Error during user validation/registration", error);
            this.reportRegistrationTiming("validateAndRegisterUser_error", { error });

            if (error?.message?.includes("already registered")) {
              return throwError(() => ({
                type: "already_registered",
                message: error.message,
              }));
            }

            return throwError(() => error);
          })
        );
      }),

      switchMap((validateSignupRes: Data) => {
        if (!isCurrent()) return EMPTY;

        if (validateSignupRes?.sjLogin && validateSignupRes?.existingCustomerInFb) {
          this.reportRegistrationTiming("validateAndRegisterUser_complete", { resultType: "existing_user" });
          return of({ type: "existing_user", validateSignupRes });
        }

        if (validateSignupRes?.customerFound && !validateSignupRes.credentialMatched) {
          this.reportRegistrationTiming("validateAndRegisterUser_complete", { resultType: "wrong_password" });
          return of({ type: "wrong_password", validateSignupRes });
        }

        this.reportRegistrationTiming("validateAndRegisterUser_complete", { resultType: "new_registration" });
        return of({ type: "new_registration", validateSignupRes });
      }),
      catchError(error => {
        return throwError(() => error);
      }),
      finalize(() => {
        if (isCurrent()) this.regFlowTiming = null;
      })
    );
  };

  // ✅ TURNSTILE path
  if (isTurnstileEnabled) {

    return this.userManagementService.getUserCountryBaseOnIP().pipe(
      take(1),
      switchMap((ipData: any) => {
        if (!isCurrent()) return EMPTY;
        const userIP = localStorage.getItem("ip") || ipData?.ip || "UNKNOWN_IP";
        user = { ...user, userIP };
        const payload = {
          ...user,
          ...cp_request,
        };


        const turnstileStart = Date.now();

        return this.getV2CloudFunction("validateandregisterwithturnstile", payload).pipe(
          switchMap((signupValidationData) => {
            if (!isCurrent()) return EMPTY;
            // Both legacy and marked servers must return a valid credential result.
            // The marker controls reuse only; it does not relax response validation.
            if (!this.isSignupValidationResult(signupValidationData)) {
              return throwError(() => new Error("We couldn't complete the security check. Try again."));
            }
            return proceedWithRegistration(
              signupValidationData.signup_validation_complete === true ? signupValidationData : undefined
            );
          }),
          catchError(error => {
            if (!isCurrent()) return EMPTY;
            return throwError(() => ({
              message: error?.message || "We couldn't complete the security check. Try again.",
            }));
          })
        );
      }),
      catchError(error => {
        if (!isCurrent()) return EMPTY;
        return throwError(() => ({
          message: error?.message || "We couldn't complete the security check. Try again.",
        }));
      })
    );
  }
  return proceedWithRegistration();
  });
}

  /**
   * It takes in a user object and a uid string, and returns a promise.
   * </code>
   * @param {CreateUser} user - CreateUser = {
   * @param {string} uid - string =&gt; this is the user id of the user that is logged in.
   * @returns The return value is a promise.
   */
callSigUpForSales(user: CreateUser, uid: string) {
  if (!user || !uid) {
    return throwError(() => new Error("Invalid user or uid"));
  }

  const request = this.RequestUserService.signupRequestForSales(user, uid);
  return this.getV2CloudFunction("callpartialregapiv2", request)
    .pipe(
      catchError(err => {
        return throwError(() => err);
      })
    );
}


  createInstantUserInSql(user: CreateUser, uid: string) {
    if (!user || !uid) return;
    user.language_code = this.selectedlanguage$?.value;
    const request = this.RequestUserService.signupRequestInstant(user, uid);
    // const callAuth = this.angularFireFunctions.httpsCallable("api");
    // return callAuth(request);
    return this.getV2CloudFunction("apiv2", request);
  }
  /**
   * It takes in a user object and a uid, and returns a promise that resolves to a response object.
   * @param {CreateUser} user - CreateUser = {
   * @param {string} uid - string =&gt; this is the user id of the user that is logged in.
   * @returns The return value is a promise.
   */
callSyncForSales(user: CreateUser, uid: string): Observable<any> {
  if (!user || !uid) {
    return throwError(() => new Error("Invalid user or uid"));
  }

  try {
    user.language_code = this.selectedlanguage$?.value;
    CommonVariable.preferredLanguage = user?.language_code;
    CommonVariable.preferredCurrency = this.userManagementService.selectedCurrency
      ? this.userManagementService.selectedCurrency
      : user?.preferred_currency;

    const request = this.RequestUserService.signupRequestForSales(user, uid);
    return this.getV2CloudFunction("callPartialRegAPI", request).pipe(
      catchError(err => {
        return throwError(() => err);
      })
    );
  } catch (err) {
    return throwError(() => err);
  }
}



  /**
   * It takes a user object and a createUserResponse object, and returns a callAuth object.
   * </code>
   * @param {CreateUser} user - CreateUser - this is the user object that is passed in from the front
   * end.
   * @param createUserResponse - This is the response from the createUser function.
   * @returns The return value is a promise that resolves to the result of the function call.
   */
  createFbUser(user: CreateUser, createUserResponse, existingUser?: boolean) {
    // Prefer real MSN, then SQL spoof MSN, then social anonymous uid (same value
    // checkSocialSignIn sends as mobile). createfirebaseuserv2 requires phoneNumber.
    const phoneNumber =
      user?.phoneNumber ||
      createUserResponse?.spoof_generated_msn ||
      this.resolveSocialSpoofPhoneNumber(user);

    const request = {
      customer_id: createUserResponse?.customer_id,
      user_id: createUserResponse?.user_id,
      password: user?.password,
      phoneNumber,
      firstName: user?.firstName,
      lastName: user?.lastName,
      user_exists: existingUser ?? false,
    };

    const data = {
      userDetails: request,
    };
    return this.getV2CloudFunction("createfirebaseuserv2", data);
    // const callAuth = this.angularFireFunctions.httpsCallable("createFirebaseUser");
    // return callAuth(request);
  }

  /**
   * Social signup/login uses the anonymous Firebase uid as the spoof MSN when
   * calling partial-reg SQL APIs. Reuse that for createfirebaseuserv2 so social
   * flows are not rejected with "Missing fields" (phoneNumber null).
   */
  private resolveSocialSpoofPhoneNumber(user: CreateUser): string | null {
    if (!user?.social_id || !user?.connect_type) {
      return null;
    }
    try {
      return localStorage.getItem("anonymousId");
    } catch {
      return null;
    }
  }

  /**
   * If the user has an anonymous account, convert it to a real account using the email and password
   * they just entered.
   * @param msn - the user's MSN
   * @param password - The password of the anonymous account.
   * @returns The user object.
   */
  convertAnonymousAccountToReal(msn, password) {
    if (!msn || !password) return;
    const email = msn?.split("+")[1] + "@lottosocial.com";
    const credential = EmailAuthProvider.credential(email, password);
    const auth = getAuth();
    linkWithCredential(auth.currentUser, credential)
      .then((usercred) => {
        const user = usercred?.user;
      })
      .catch((error) => {
        return error;
      });
  }
  /**
   * This function returns a promise that resolves to the result of calling the signInAnonymously
   * function, which is passed the result of calling the getAuth function.
   * @returns a promise.
   */
  async loginAnonymously() {
    try {
      if (this.anonymousLoginInProgress) {
        throw new Error('Anonymous login already in progress');
      }
      this.anonymousLoginInProgress = true;
      await new Promise(resolve => setTimeout(resolve, 300));
      const auth = getAuth();
      const result = await signInAnonymously(auth);
      await new Promise(resolve => setTimeout(resolve, 300));
      return result ?? null;
    } catch (error) {
      return error;
    } finally {
      this.anonymousLoginInProgress = false;
    }
  }


  // This method is used to auto login the user
  async autoLogin(
    token,
    params,
    isActive: () => boolean = () => true,
    cancel$: Observable<void> = this.destroy$,
    // Optional: clear caller deadlines once Firebase sign-in has succeeded so recovery UI cannot race navigation.
    onAuthenticated?: () => void,
  ) {
    if (!isActive()) return;
    const auth = getAuth();

     if (this.tokenValidaterSub) {
      this.tokenValidaterSub.unsubscribe();
      this.tokenValidaterSub = null;
    }

    this.tokenValidaterSub = this.getV2CloudFunction("tokenvalidater", { token, ...params }).pipe(takeUntil(cancel$)).subscribe({
      next: (response) => {
        if (!isActive()) return;
        const { status, token, token_refresh } = response || {};
        if (token_refresh) {
          localStorage.setItem("fbToken", token_refresh);
        }
        if (status && token) {
          signInWithCustomToken(auth, token)
            .then((userCredential) => {
              if (!isActive()) return;
              onAuthenticated?.();
              const user = userCredential?.user;
              this.autoUserId = user?.uid || null;
              this.segmentRequestData$.next({ email: user?.email, name: user?.displayName });
              if (typeof GTM_Segment === "function" || typeof GTM_Segment !== "undefined") {
                GTM_Segment();
              }
              this.userManagementService.updateIsLoggedIn(user);
              const continueAutoLogin = () => {
                if (!isActive()) return;
                const currentUrl = this.router.url;
                const urlTree = this.router.parseUrl(currentUrl);
                const newQueryParams = canonicalizeProsubQueryParams({
                  ...(urlTree.queryParams || {}),
                  ...(params || {}),
                });
                delete newQueryParams["token"];
                delete newQueryParams["t"];
                delete newQueryParams["T"];
                const prosub_id = newQueryParams.prosub_id;
                const redirect_to = params?.redirect_to ?? newQueryParams.redirect_to;
                const goTo = (path) => {
                  this.redirectTo = path;
                  if (this.redirectTo == this.router.url.split("?")[0]) {
                    this.router.navigateByUrl("/", { skipLocationChange: true }).then(() => {
                      if (!isActive()) return;
                      this.router.navigate([this.redirectTo], { queryParams: newQueryParams });
                    });
                  } else {
                    this.router.navigate([this.redirectTo], { queryParams: newQueryParams });
                  }
                };
                if (redirect_to) {
                  goTo(redirect_to);
                  return;
                }
                if (!prosub_id) {
                  goTo("/mainTabs/tabs/home");
                  return;
                }
                firstValueFrom(
                  this.getV2CloudFunction("updatefirestoredoc", {
                    path_id: "ERzajuwYcQgv2qQ1tA9q",
                    dv: { docId: String(prosub_id) },
                    method: "read",
                  }).pipe(
                    takeUntil(cancel$),
                    timeout(5000)
                  )
                )
                  .then((snapshot: Data) => {
                    if (!isActive()) return;
                    const details = snapshot?.data ?? snapshot;
                    const optionalPage = details?.optional_page ? String(details.optional_page).split("?")[0] : "";
                    goTo(optionalPage || "/partial-registration");
                  })
                  .catch(() => {
                    if (isActive()) goTo("/partial-registration");
                  });
              };
              const compatSignIn = this.angularFireAuth?.signInWithCustomToken?.(token);
              if (compatSignIn && typeof compatSignIn.then === "function") {
                compatSignIn.then(() => continueAutoLogin()).catch(() => continueAutoLogin());
              } else {
                continueAutoLogin();
              }
            })
            .catch(() => {
              if (isActive()) this.redicectToLogin(params);
            });
        } else {
          this.redicectToLogin(params);
        }
      },
      error: () => {
        if (isActive()) this.redicectToLogin(params);
      },
    });
  }

  redicectToLogin(params) {
    this.redirectTo = "/login";
    this.router.navigate([this.redirectTo], { queryParams: params });
  }

  // This method is used to call scratchGameNotification CF with user details after use login
  scratchGameNotificationService(userId) {
    try {
      this.getV2CloudFunction("scratchgamenotificationv2", { userId: userId })
        .pipe(takeUntil(this.destroy$),
          catchError(() => {
            return of({
              success: false,
              code: "Error in scratchGameNotificationService",
              message: "Error in scratchGameNotificationService",
            });
          })
        )
        .subscribe({
          next: (response) => {
            if (response && response.success) {
            } else {
              return of({ success: false, code: "Cloud function call failed", message: "Cloud function call failed" });
            }
          },
          error: (error) => {
            return of({
              success: false,
              code: "Subscription error in scratchGameNotificationService",
              message: "Subscription error in scratchGameNotificationService",
            });
          },
        });
    } catch (error) {
      return of({
        success: false,
        code: "Unexpected error in scratchGameNotificationService",
        message: "Unexpected error in scratchGameNotificationService",
      });
    }
  }

  /**
   * This function is called when a user clicks a button on the front end, and it calls a function in
   * the cloud functions that updates the user's game promotion status.
   * @param {string} userId - string
   */
  updateGamePromotion(userId: string) {
    this.getV2CloudFunction("updategamepromotionv2", userId)
      .pipe(takeUntil(this.destroy$), catchError((err) => throwError(() => err)))
      .subscribe();
    // const updateGamePromotion = this.angularFireFunctions.httpsCallable("updateGamePromotion");
    // updateGamePromotion(userId)?.subscribe();
  }
  setGamePlayRestriction(userId: string) {
    this.getV2CloudFunction("setgameplayrestrictionv2", userId)
      .pipe(takeUntil(this.destroy$), catchError((err) => throwError(() => err)))
      .subscribe();
  }
  processLoginGift(userId: string) {
    try {
      // const processLoginGift = this.angularFireFunctions.httpsCallable("processLoginGift");
      // processLoginGift(userId)?.subscribe();
      this.getV2CloudFunction("processlogingiftv2", userId)
        .pipe(takeUntil(this.destroy$), catchError((err) => throwError(() => err)))
        .subscribe();
    } catch (error) {
      throw new Error("Error in processLoginGift");
    }
  }

  // This function is used to update the user's leaderboard
  updateUserLeaderBoard() {
    const updateUserLeaderBoard = this.getV2HttpsCallableFunction("peepsoleaderboard");
    updateUserLeaderBoard().catch((error) => {
      throw new Error(`Error in updateUserLotteryDiscovery: ${error.message}`);
    });
  }
  updateUserLotteryDiscovery() {
    const updateUserLotteryDiscovery = this.getV2HttpsCallableFunction("updateuserlotterydiscoveryv2");
    updateUserLotteryDiscovery().catch((error) => {
      throw new Error(`Error in updateUserLotteryDiscovery: ${error.message}`);
    });
  }

  /**
   * This function shows a toast message to the user, if the user is anonymous, and the user is trying
   * to register.
   */
  showAnonymousUserMessage(message: string = "") {
    const defaultMsg = {
      message: {
        line_1: message
          ? message
          : "We couldn't complete your registration. Try again or contact support if the problem continues.",
        line_2: "",
      },
      style: {
        type: "Error",
        icon: "tick circle error.svg",
      },
    };
    this.showToastMessage({}, defaultMsg);
  }
  /**
   * It takes a visitorId as a parameter, then it returns the fingerprint-users document with the
   * matching visitorId
   * @param {string} visitorId - string
   * @returns The data from the document.
   */
  getFingerprintId(visitorId: string): Observable<any> {
    const docRef = doc(this.firestore, `${environment.mirrorPath}fingerprint-users/${visitorId}`);
    return docData(docRef); // Automatically updates on snapshot change
  }
  /**
   * This function returns a document from the Firestore database, and then maps the data to a
   * variable.
   * @returns The data from the document.
   */
  getTestMsn(): Observable<any> {
    // const docRef = doc(this.firestore, 'test-msn/sIiUDPjdyuN7VSS6jbKp');
    return of(''); // returns an observable that emits real-time updates
  }

  //this method is to hide coach mark popup when user visit on first time
  hideCoachMarkPopup(): void {
    this.commonService.getUid("hideCoachMarkPopup").pipe(take(1)).subscribe({
      next: (userId) => {
        if (!userId) {
          return " No userId found";
        }
        this.getV2CloudFunction("updatefirestoredoc", {
          path_id: "aiUM9R8c5J9Zai5FQiOh",
          doc_data: { coachmark: { credit_topup: true } },
          method: "update",
        }).pipe(take(1)).subscribe({
          error: (err) => {
            return err;
          }
        });
      },
      error: (err) => {
        return err;
      }
    });
  }



  //This method is to update the user vip status
  updateInlineNotificationStatus(settingObject: Record<string, boolean>): Observable<void> {
    return this.commonService.getUid("updateInlineNotificationStatus").pipe(
      take(1),
      switchMap(async (userId) => {
        if (!userId) {
          throw new Error("User ID is not available");
        }
        const path_id = "MvKG66uONeNYblkZ2w2N";
        const doc_data = settingObject;
        const method = "update";

        this.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          method,
        }).subscribe({
          error: (err) => {
            throw new Error(`Error in updateInlineNotificationStatus: ${err}`); // Handle and propagate error
          }
        });
      })
    )
  }


  // Get data from Angular firestore
  getAngularFireStore(url: string, method: string) {
    return this.catchService.isOnline$.pipe(takeUntil(this.destroy$),
      switchMap((isOnline) => {
        if (!isOnline) return of(null);

        const docRef: DocumentReference = doc(this.firestore, url);
        const needsAuthGate = typeof url === "string" && url.includes("users/");

        const runOp = () => {
          if (method === "get") {
            return from(getDoc(docRef));
          }

          if (method === "snapshotChanges") {
            return new Observable((observer) => {
              const unsubscribe = onSnapshot(docRef, {
                next: (snapshot) => observer.next(snapshot),
                error: (err) => observer.error(err),
              });

              // Cleanup when unsubscribed
              return () => unsubscribe();
            }).pipe(takeUntil(this.destroy$),
              this.catchService.retryWhen,
              this.catchService.catchOfflineError
            );
          }

          return of(null);
        };

        // Gate user-private paths only (LOTTOSOCIAL-PWA-1MEE); public paths unchanged
        return needsAuthGate
          ? this.authStateService.customTokenReady$.pipe(take(1), switchMap(() => runOp()))
          : runOp();
      }),
      catchError((error) => {
        // Never return/throw a bare Error into RxJS innerFrom — that produces
        // "You provided an invalid object where a stream was expected" (LOTTOSOCIAL-PWA-1M60).
        const isPermissionDenied =
          (error instanceof FirebaseError && error.code === 'permission-denied') ||
          (typeof error?.message === 'string' &&
            error.message.toLowerCase().includes('missing or insufficient permissions'));
        if (isPermissionDenied) {
          return of(null);
        }
        return throwError(() => error);
      }),
      shareReplay(1)
    );
  }



  private getCachedData(key: string): any {
    try {
      const cached = localStorage.getItem(`firestore_cache_${key}`);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        // Cache valid for 1 hour
        if (Date.now() - timestamp < 3600000) {
          return data;
        }
        localStorage.removeItem(`firestore_cache_${key}`);
      }
      return null;
    } catch (e) {
      return null;
    }
  }

  private setCachedData(key: string, data: any): void {
    try {
      if (data) {
        const cacheData = {
          data,
          timestamp: Date.now()
        };
        localStorage.setItem(`firestore_cache_${key}`, JSON.stringify(cacheData));
      }
    } catch  {
      // handle error
    }
  }
  // Offline queue
  private offlineQueue: Array<{
    name: string;
    payload: Data | string;
    options: Data;
    resolver: { resolve: Function; reject: Function };
  }> = [];

  public getV2CloudFunction(CloudFunctionName: string, payload: Data | string, options: Data = {}): Observable<any> {
    // Internal transport controls never enter Firebase's options or the API body.
    // An uncertain write response must be observed through its receipt, not replayed.
    const { retryAttempts, beforeInvoke, ...callableOptions } = options;
    const singleAttempt = retryAttempts === 0;
    if (!navigator.onLine && singleAttempt) {
      return throwError(() => Object.assign(new Error('Write was not invoked while offline'), {
        code: 'CALLABLE_NOT_INVOKED', reason: 'offline',
      }));
    }
    const isTurnstileRegistration = CloudFunctionName === "validateandregisterwithturnstile" ||
      CloudFunctionName === "validateandregisterwithturnstilev3";
    if (!navigator.onLine && isTurnstileRegistration) {
      // A queued submission can retain an expired, single-use token. The form
      // must regain control and acquire a fresh token on the next submission.
      return throwError(() => new Error("You're offline. Reconnect and submit again to repeat the security check."));
    }
    if (!navigator.onLine) {
      return new Observable(observer => {
        const resolver = {
          resolve: (result: Data) => {
            observer.next(result);
            observer.complete();
          },
          reject: (error: Data) => {
            observer.error(error);
          }
        };
        this.offlineQueue.push({ name: CloudFunctionName, payload, options, resolver });
        // Process offline queue when online
        window.addEventListener('online', () => {
          this.processOfflineQueue();
        }, { once: true });
      });
    }

    try {
      if (!navigator.onLine) {
        return throwError(() => new Error('OFFLINE_ERROR'));
      }

      const region = AuthService.FIREBASE_REGION || 'us-central1';
      const callable = httpsCallable(getFunctions(undefined, region), CloudFunctionName, callableOptions);

      // CRITICAL FIX: Wrap the entire Observable chain to ensure it never emits undefined
      // This prevents "You provided 'undefined' where a stream was expected" errors
      return defer(() => {
        if (singleAttempt && !navigator.onLine) {
          return throwError(() => Object.assign(new Error('Write was not invoked while offline'), {
            code: 'CALLABLE_NOT_INVOKED', reason: 'offline',
          }));
        }
        if (typeof beforeInvoke === 'function' && beforeInvoke() === false) {
          return throwError(() => Object.assign(new Error('Callable owner changed before invocation'), {
            code: 'CALLABLE_NOT_INVOKED', reason: 'owner_changed',
          }));
        }
        // Defensive check: Ensure callable and payload are valid before invoking
        if (!callable || typeof callable !== 'function') {
          return of({}); // Return empty object Observable instead of throwing
        }

        // Wrap the callable invocation to catch any synchronous errors
        try {
          const result = callable(payload);
          // Ensure result is a valid Promise before converting to Observable
          if (!result || typeof result.then !== 'function') {
            return of({});
          }
          return from(result);
        } catch (error) {
          return singleAttempt ? throwError(() => error) : of({});
        }
      }).pipe(
        takeUntil(this.destroy$),
        tap(),

        map((res: Data) => {
          // Validate the response structure before processing
          if (!res || typeof res !== 'object') {
            // Return empty object instead of throwing to prevent stream errors
            return {};
          }

          // Validate that res.data exists and is not a string starting with '/' or '<'
          const data = res?.data;
          
          // CRITICAL FIX: Never return undefined - return empty object instead
          // This prevents "You provided 'undefined' where a stream was expected" errors
          // when downstream RxJS operators expect a valid value
          if (data === undefined || data === null) {
            return {};
          }
          
          return data;
        }),

        retryWhen((errors) =>
          errors.pipe(takeUntil(this.destroy$),
            tap((error) => {
              const msg = error?.message?.toLowerCase?.() || '';
              const isNetworkError = msg.includes('offline') || msg.includes('network');

              if (isNetworkError && !navigator.onLine) {
              }
            }),

            scan((retryCount, error) => {
              // Turnstile tokens are single-use. An uncertain response cannot
              // safely be retried with the same token; let the form request a new one.
              if (singleAttempt || CloudFunctionName === "validateandregisterwithturnstile") {
                throw error;
              }
              if (retryCount >= 3) {
                throw error;
              }

              return retryCount + 1;
            }, 0),
            delayWhen(retryCount => timer(Math.min(Math.pow(2, retryCount) * 1000, 30000))) // Exponential backoff capped at 30s
          )
        ),
        catchError(error => {
          if (singleAttempt || CloudFunctionName === "validateandregisterwithturnstile") {
            return throwError(() => error);
          }
          // CRITICAL FIX: Always return an Observable, never undefined
          // Return empty object Observable instead of throwing to prevent undefined propagation
          if (error?.message === 'OFFLINE_ERROR') {
            return of({}); // Return empty object instead of throwing
          }

          if (error?.message === 'UNAUTHENTICATED_ERROR') {
            return of({}); // Return empty object instead of throwing
          }

          return of({}); // Return empty object instead of throwing to prevent undefined errors
        })
      );
    } catch (error) {
      // CRITICAL FIX: Return Observable with empty object instead of throwing
      return singleAttempt ? throwError(() => error) : of({});
    }
  }

  //Offline queue
  private processOfflineQueue() {
    if (!navigator.onLine) return;

    while (this.offlineQueue.length) {
      const { name, payload, options, resolver } = this.offlineQueue.shift()!;
      this.getV2CloudFunction(name, payload, options).subscribe({
        next: (result) => resolver.resolve(result),
        error: (error) => resolver.reject(error)
      });
    }
  }

  /** Helper to detect Firebase auth errors */
  private isAuthError(error: any): boolean {
    if (!error) return false;

    const firebaseErrorCodes = [
      'auth/invalid-user-token',
      'auth/user-token-expired',
      'auth/user-disabled',
      'auth/unauthenticated'
    ];

    return firebaseErrorCodes.some(code => error.code === code);
  }

  // Initializes Firebase Functions, sets the region, and returns a callable reference to a specific function.
  getCallable(functionName: string) {
    const functions = getFunctions();
    functions.region = AuthService.FIREBASE_REGION;
    return httpsCallable(functions, functionName);
  }
  //This function is used to call getremoteconfig CF
  async getRemoteConfig(remoteConfigRequest: Data) {
    const getRemoteConfig = this.getCallable("getremoteconfig");
    // Validate input
    if (!remoteConfigRequest || typeof remoteConfigRequest !== "object") {
      throw new Error("Invalid request data provided for remote config.");
    }
    try {
      const response = await getRemoteConfig(remoteConfigRequest);
      // Ensure the response is valid
      if (!response || typeof response !== "object") {
        throw new Error("Received an invalid response from the remote config call.");
      }
      return response;
    } catch (error: any) {
      if (error instanceof FirebaseError) {
        // Handle FirebaseError explicitly
        return error.message, error.code;
      } else {
        // Handle other types of errors
        return error;
      }
    }
  }


  public v2CloudFunctionResponse(CFCall, checkUserAuth: boolean = true) {
    try {
      if (checkUserAuth) {
        const auth = getAuth();
        const user = auth?.currentUser;
        if (!user?.uid) {
          return of({});
        }
      }

      return from(CFCall).pipe(takeUntil(this.destroy$),
        map((res: Data) => {
          return res?.data;
        }),
        catchError(() => {
          return of({});
        })
      );
    } catch (error) {
      return of({});
    }
  }

  public unsubscribeGameFeedInbox() {
    this.gamefeedUnsubscribeInbox$?.next();
    this.gamefeedUnsubscribeInbox$?.complete();
  }

  public customOfferCreditCheck(userid, award_id) {
    try {
      return this.getV2CloudFunction("customerofferpagev2", {
        userID: userid,
        award_id: award_id,
      });
      // const creditBackCall = this.angularFireFunctions.httpsCallable("custmoerOfferPage");
      // return creditBackCall({
      //   userID: userid,
      //   award_id: award_id,
      // });
    } catch (error) {
      return error;
    }
  }

  public getGlobalPartnerOffers(type: string): Observable<any> {
    try {
      const docRef = doc(this.firestore, `Global_lookup/partners/list/${type}`);
      return docData(docRef).pipe(takeUntil(this.destroy$),
        catchError((err) => throwError(() => err)),
        shareReplay(1)
      );
    } catch (error) {
      return throwError(() => error);
    }
  }

  // This method is used to set isVisited to true
  checkBreakDownVisited() {
    combineLatest([
      this.userDetails$.pipe(take(1)),
      this.previousCheck$.pipe(take(1)),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([userDetails, data]: [Data, Data]) => {
        const checkWinnings = data?.length;
        const isBreakdownVisited = userDetails?.is_breakdown_visited;

        if (isBreakdownVisited == null || isBreakdownVisited == undefined || isBreakdownVisited == false) {
          this.breakDownVisited(checkWinnings > 0);
        }
      });
  }
  // This method is used to set isVisited to true
  breakDownVisited(isVisited: boolean): void {
    this.commonService.getUid("breakDownVisited").pipe(
      take(1),
      switchMap((userId) => {
        if (!userId) {
          return of(null); // Skip update if no userId
        }

        const path_id = "yzF1Ss5eiMqnh91zQMtv";
        const doc_data = { is_breakdown_visited: isVisited };
        const method = "update";

        this.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          method,
        }).subscribe({
          error: () => {
            // handle error
         }
        });
      })
    ).subscribe({
      error: () => {
        // handle err
      },
    });
  }


  // This method is used updateAddToHomeCardStatus
  updateAddToHomeCardStatus(dataToBeUpdated: Record<string, any>): void {
    this.commonService.getUid("updateAddToHomeCardStatus").pipe(
      take(1),
      switchMap((userId) => {
        if (!userId) {
          return of(null);
        }
        return this.getV2CloudFunction("updatefirestoredoc", {
          path_id: "RdSJ8Cffwlw0MZ9VJ8yZ",
          doc_data: dataToBeUpdated,
          method: "update",
        });
      }),
      catchError((err) => {
        return of(null);
      })
    ).subscribe();
  }


  async resetPasswordAndRedirectToNextPage(
    userMsn: string,
    userOldPassword: string,
    page: string,
    resetToken: string
  ): Promise<void> {
    if (!userMsn || !userOldPassword || !resetToken || !window.navigator.onLine) {
      return;
    }

    this.loadingService.present();

    try {
      await this.resetPasswordOnFirebase(userOldPassword, userMsn, resetToken);

      const res: Data = await firstValueFrom(this.resetPassword(userOldPassword));

      if (res?.status === "success") {
        setTimeout(() => {
          this.loadingService.dismiss();
          const redirectTo = this.redirectTo || "/mainTabs/tabs/home";

          if (page === "partial-reg") {
            this.redirectTo = "free-reg-step4c";
            const params = this.route.snapshot.queryParams;
            this.router.navigate([this.redirectTo], { queryParams: params });
          } else {
            this.router.navigateByUrl(redirectTo);
          }
        }, 2000);
      } else {
        this.handlePasswordResetError();
      }
    } catch (err) {
      this.handlePasswordResetError();
    }
  }


  getV2HttpsCallableFunction(CFName: string) {
    const functions = getFunctions();
    functions.region = AuthService.FIREBASE_REGION;
    return httpsCallable(functions, CFName);
  }

  private getDataFromLocalStorageOrCookie(keyName: string) {
    return localStorage.getItem(keyName)
      ? localStorage.getItem(keyName)
      : this.utilityService.getCookie(keyName)
        ? this.utilityService.getCookie(keyName)
        : "";
  }

  private handlePasswordResetError(): void {
    this.loadingService.dismiss();

    this.getConstantsMsg("1026")
      .then((translatedMsg) => {
        const defaultMsg = {
          message: {
            line_1: "We couldn't reset your password. Try again.",
            line_2: "",
          },
          style: {
            type: "Error",
            icon: "tick circle error.svg",
          },
        };
        this.showToastMessage(translatedMsg, defaultMsg);
      })
      .catch((error) => {
        return error;
      });
  }
  // This obserable used to get Card Data from stored card document

  $CardData = this.catchService.isOnline$.pipe(
    takeUntil(this.destroy$),
    switchMap((isOnline) => {
      if (!isOnline) return of(null); // Handle offline case

      // Get userId from commonService as an Observable
      return this.commonService.getUid("getStoredCards").pipe(
        switchMap((userId) => {
          if (!userId) return of(null);

          const storedCardCollection = collection(this.firestore, `/users/${userId}/stored_card`);
          const storedCardQuery = query(storedCardCollection, where("status", "==", true));

          // Convert Promise from getDocs to Observable
          return from(getDocs(storedCardQuery)).pipe(
            map((snapshot) =>
              snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
            ),
            catchError((error) => {
              this.catchService.catchOfflineError(of(error));
              throw error;
            })
          );
        })
      );
    }),
    map((cards: any[]) => {
      if (!cards) return [];
      // Filter unique cards
      const uniqArray = cards.filter((card, index, arr) =>
        arr.findIndex((t) => t.id === card.id) === index
      );
      return uniqArray;
    }),
    shareReplay(1) // Cache results
  );

  trackInlineNotifications(data: any, isClicked: any, isClosed?: any): void {
    try {
      if (data) {
        const languageCode = this.selectedlanguage$.value;
        const messageId = "vip_status";
        let title = "Inline Message Viewed";
        let body = {
          title: data?.content?.[languageCode || "en"]?.title,
          id: data?.messageId || messageId,
        };
        if (isClicked) {
          title = "Inline Message Clicked";
          body["destinationUrl"] = isClosed ? "close" : data?.action?.details;
        }
        if (typeof (window as any)?.analytics !== "undefined") {
          (window as any).analytics.track(title, { ...body });
        }
        this.clevertapService?.trackInlineNotifications(title, body);
      }
    } catch (error) {
      return error;
    }
  }



  // This method is used to get user's device info on login and saves it to session storage
  async captureDeviceInfo(): Promise<void> {
    const versionString = CommonVariable.device.osVersion;
    const majorVersion = parseInt(versionString, 10);
    const operatingSys = CommonVariable.device.operatingSystem;
    let osFormattedString;
    if (operatingSys == "ios") {
      osFormattedString = `${operatingSys} ${majorVersion}`;
    } else {
      osFormattedString = versionString;
    }
    const classifyBatteryHealth = (batteryLevel: number): string => {
      if (batteryLevel >= 80) {
        return "High";
      } else if (batteryLevel >= 45) {
        return "Medium";
      } else if (batteryLevel >= 30) {
        return "Low";
      } else {
        return "Very Low";
      }
    };
    const batteryInfo = async (): Promise<string> => {
      if (!Device) {
        return "N/A";
      }
      try {
        const batteryInfo = await Device.getBatteryInfo();
        const batteryLevel = batteryInfo.batteryLevel * 100;
        const batteryHealthCategory = classifyBatteryHealth(batteryLevel);
        return batteryHealthCategory;
      } catch (error) {
        return "N/A";
      }
    };
    const deviceInfo = {
      userAgent: navigator.userAgent || "N/A",
      deviceMemory: window?.navigator?.deviceMemory || "N/A",
      batteryHealth: await batteryInfo(),
      hardwareConcurrency: navigator.hardwareConcurrency || "N/A",
      browserVersion: this.getBrowserVersion(),
      connectionType: this.getConnectionType(),
      osVersion: osFormattedString,
    };
    var existingDeviceInfo = sessionStorage.getItem("deviceInfo");

    if (existingDeviceInfo) {
      sessionStorage.removeItem("deviceInfo");
    }
    sessionStorage.setItem("deviceInfo", JSON.stringify(deviceInfo));
  }

  getConnectionType(): string {
    const connection = window.navigator.connection;
    return connection?.effectiveType || "N/A";
  }

  getBrowserVersion(): string {
    const userAgent = navigator.appVersion;
    const browserVersionMatch = userAgent.match(/(chrome|firefox|safari|opera|edge|msie|trident)\/?\s*(\d+)/i);
    if (browserVersionMatch && browserVersionMatch?.length >= 3) {
      const browserName = browserVersionMatch[1];
      let version;
      if (browserName == "Safari") {
        version = this.extractBrowserVersion(navigator.userAgent);
      } else {
        version = browserVersionMatch[2];
      }
      return `${browserName} ${version}`;
    } else {
      return "Unknown";
    }
  }

  extractBrowserVersion(userAgent: string): string {
    const matches = userAgent.match(/Version\/(\d+(\.\d+)+)/);
    if (matches && matches?.length > 1) {
      return matches[1];
    }
    return "Unknown";
  }

  public commonUserCheck(callback: () => void): void {
    this.angularFireAuth.currentUser
      .then((user) => {
        if (!user) {
          let token = localStorage.getItem("fbToken");
          if (token) {
            this.commonManualLogin(token, callback);
          } else {
            const interval = setInterval(() => {
              let token = localStorage.getItem("fbToken");
              if (token) {
                clearInterval(interval);
                this.commonManualLogin(token, callback);
              }
            }, 1000);
          }
        } else {
          callback();
        }
      })
      .catch((err) => {
        return err;
      });
  }

  private commonManualLogin(token: string, callback: () => void): void {
    const auth = getAuth();

    if (this.tokenValidaterSub) {
        this.tokenValidaterSub.unsubscribe();
        this.tokenValidaterSub = null;
      }

    this.tokenValidaterSub = this.getV2CloudFunction("tokenvalidater", { token }).subscribe({
      next: async (res) => {
        const { status, token, token_refresh } = res;

        if (status && token) {
          if (token_refresh) {
            localStorage.setItem("fbToken", token); // Refresh the token
          }



          try {
            await signInWithCustomToken(auth, token); // Sign in using the token
            const currentUser = await auth.currentUser;

            if (currentUser) {
              callback(); // Execute callback if authenticated
            } else {
              const defaultMsg = {
                message: {
                  line_1: "Something went wrong!",
                  line_2: "",
                },
                style: {
                  icon: "tick circle error.svg",
                  type: CommonVariable.error,
                },
              };
              this.showToastMessage({}, defaultMsg); // Display error message
            }
          } catch (error) {
            const logData = {
              creation_date: new Date(),
              local_fb_token: localStorage.getItem(this.fbTokenKey) || "Missing Token",
              error,
              page: "auth.service.ts",
              userId: localStorage.getItem(this.userIdKey) || "Missing User ID",
            };

            const path_id = "hbqZtgNd5muQnA8eI3xe";
            const doc_data = logData;
            const method = "create";

            await this.getV2CloudFunction("updatefirestoredoc", {
              path_id,
              doc_data,
              method,
            }).subscribe({
              error: () => {
                return false;
              }
            });
          }
        }
      },
      error: (err) => {
        return err; // Handle subscription errors
      },
    });
  }

  private checkForOnesignalStatus(): void {
    this.commonService.getUid("checkForOnesignalStatus").pipe(take(1)).subscribe({
      next: (userId) => {
        if (!userId) return;

        const notificationDocRef = doc(
          this.firestore,
          `/users/${userId}/onesignal-push-notifications/details`
        );
        getDoc(notificationDocRef)
          .then((snapshot) => {
            const data = snapshot?.data() as any;
            if (!data || !data?.token_details) return;

            const tokenArray = data.token_details;
            const currentDeviceId =
              CommonVariable.FingerprintJSConst || localStorage.getItem("fingerSalt");

            const isMatch = tokenArray.some(
              (obj) => obj.onesignal_id === currentDeviceId
            );

            this.oneSignalService.showOneSignalNotificationUI$.next(!isMatch);
          })
          .catch((error) => {
            throwError(() => error);
          });
      },
      error: () => {
        // handle error
      }
    });
  }


  //This method is to call assign user goals cloud function on user login.
  assignUserGoals(userId: string) {
    try {
      const functions = getFunctions();
      functions.region = AuthService.FIREBASE_REGION;
      httpsCallable(functions, "assignusergoalsv2")({ uid: userId });
    } catch (error) {
      throw new Error(error);
    }
  }

  // Function to fetch currency and language based on country code from Firebase document [ we already move]
  getCurrencyAndLanguageList$ = docData(
    doc(this.firestore, 'general_modules/countries')
  ).pipe(takeUntil(this.destroy$),
    map((data: any) => JSON.parse(JSON.stringify(data))),
    catchError(() =>
      of({
        GB: {
          currency: 'GBP',
          language: 'en',
        },
      })
    ),
    shareReplay(1)
  );

  sendVersionMessage(userId: string) {
    const functions = getFunctions();
    functions.region = AuthService.FIREBASE_REGION;
    const inboxMsg = httpsCallable(functions, "sendversionmessagev2");
    inboxMsg(userId).catch((error) => {
      return error;
    });
  }

  getOSVersion() {
    const versionString = CommonVariable.device.osVersion;
    const operatingSys = CommonVariable.device.operatingSystem;
    if (operatingSys == "ios") {
      return versionString;
    }
  }

  callversionCF(userId: string) {
    try {
      const osversion = this.getOSVersion();
      if (osversion === this.EXPECTED_OS_VERSION) {
        setTimeout(() => {
          this.sendVersionMessage(userId);
        }, 3000);
      }
    } catch (error) {
      return "An error ocurred while checking OS version" + error.message;
    }
  }

  // This function is used to get latest ongoing syndicate to take payment holiday
  async getLatestOngoingSyndicate(): Promise<any> {
    try {
      const userId = await this.commonService.getUid("getLatestOngoingSyndicate").pipe(take(1)).toPromise();
      if (!userId) return null;

      // Reference the collection
      const ticketsCollection = collection(this.firestore, `/users/${userId}/managed_tickets`);

      // Define the query with conditions
      const ticketsQuery = query(
        ticketsCollection,
        where("syndicate_type", "==", "Ongoing"),
        orderBy("creation_date", "desc"),
        limit(1)
      );

      // Fetch the documents matching the query
      const querySnapshot = await getDocs(ticketsQuery);

      if (!querySnapshot.empty) {
        return querySnapshot.docs[0].data(); // Return the latest syndicate
      }

      return null; // No ongoing syndicate found
    } catch {
      return null; // Handle errors and return null
    }
  }


  // This function is used to send latestSyndicate data to observable
  async fetchLatestOngoingSyndicate() {
    try {
      const latestSyndicate = await this.getLatestOngoingSyndicate();
      this.latestOngoingSyndicate$.next(latestSyndicate);
    } catch (error) {
      return error;
    }
  }

  // To call the issuechurninboxv2 CF
  checkChurnRisk(userId: string) {
    return this.getV2CloudFunction("issuechurninboxv2", userId);
  }

  private createFbUserErrorHandling(): void {
    const proSub = localStorage.getItem("prosub_id");
    this.logout().catch((error) => error);
    this.callSigUpForSalesResponse$?.next(null);
    if (proSub === null) return;
    this.router.navigateByUrl(`partial-registration?prosub_id=${proSub}`);
    const messages = {
      en: "We couldn't create your account. Check your details and try again.",
      de: "We couldn't create your account. Check your details and try again.",
      fr: "Nous n'avons pas pu créer votre compte. Vérifiez vos informations et réessayez.",
      it: "Non è stato possibile creare il tuo account. Controlla i tuoi dati e riprova.",
      es: "No pudimos crear tu cuenta. Comprueba tus datos e inténtalo de nuevo.",
      pt: "Não foi possível criar a sua conta. Verifique os seus dados e tente novamente.",
    };
    this.showAnonymousUserMessage(messages[this.selectedlanguage$?.value]);
  }
  // This code is used to get the inbox messages from firestore at a time.
  userInbox = this.homeDataService.homePageLoaded$.pipe(takeUntil(this.destroy$),
    filter((f: boolean) => f),
    withLatestFrom(this.userManagementService.userRequest$),
    switchMap(([, user]: [boolean, any]) => {
      if (user && user.uid) {
        return this.catchService.isOnline$.pipe(takeUntil(this.destroy$),
          switchMap((isOnline) => {
            if (!isOnline) return of([]); // Handle offline scenario
            const inboxCollection = collection(this.firestore, `/users/${user.uid}/inbox`); // Reference collection
            const inboxQuery = query(
              inboxCollection,
              where("hide", "==", false),
              orderBy("creation_date", "desc") // Query to filter and sort inbox
            );

            return getDocs(inboxQuery).then((snapshot) => {
              const inboxMessages = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
              })); // Map query results to array
              return inboxMessages; // Return mapped data
            });
          }),
          catchError((error) => {
            this.catchService.catchOfflineError(of(error)); // Handle errors
            throw error;
          }),
          shareReplay(1) // Cache results
        );
      }
      return of([]); // Return empty array if user or uid is not available
    }),
    map((inboxMessages: any[]) => {
      const uniqueInboxMessages = inboxMessages.filter((message, index, arr) =>
        arr.findIndex((msg) => msg.id === message.id) === index
      ); // Ensure unique messages by id
      return uniqueInboxMessages; // Return filtered array
    })
  );

  app_notification$ = this.userManagementService.userRequest$?.pipe(
    takeUntil(this.destroy$),
    switchMap((user: any) => {
      if (!user) {
        return of(false);
      }
      return this.commonService.getUid('AppNotification').pipe(
        switchMap((userId: string | null) => {

          if (!userId) {
            return of(false);
          }
          const docRef = doc(this.firestore, `users/${userId}/in_app_notification/status`);

          // Defense-in-depth: defer docData until custom token is ready
          // (LOTTOSOCIAL-PWA-1MEE). getUid already waits; take(1) is instant if ready.
          return this.authStateService.customTokenReady$.pipe(
            take(1),
            switchMap(() => docData(docRef).pipe(
              map((notification: any) => {
                return notification?.winning ?? false;
              }),
              catchError((error) => {
                if ((error as { code?: string })?.code === "permission-denied") {
                  return of(false);
                }
                return throwError(() => error);
              })
            ))
          );
        })
      );
    }),
    shareReplay(1) // ✅ Ensures multiple subscribers share the same data stream
  );

  userid: string;
  public async userDetailsForLiveChat(): Promise<Data> {
    try {
      // Wait for custom token readiness before any user-private Firestore read.
      // helpSupport calls this on enter; without the gate, balance docData races
      // the iOS/Safari token round-trip (LOTTOSOCIAL-PWA-1MEE).
      // 8s fallback matches getUid so live chat never hangs permanently.
      await firstValueFrom(
        race(
          this.authStateService.customTokenReady$.pipe(take(1)),
          timer(8000)
        )
      );

      // ✅ Get UID first
      const uid = await this.commonService.getUid("authService").pipe(first()).toPromise();

      // Do not create Firebase listeners without a valid authenticated user ID
      if (!uid) {
        return null;
      }

      if (!navigator.onLine) {
        return {
          name: "Guest User",
          customer_id: uid || "",
          device: "Web",
          title: "Guest",
          email: "",
          language: this.selectedlanguage$?.value || "en",
          service_age: "Unknown",
          bonus_credit_balance: "0",
          cash_balance: "0",
          previous_login: "Unknown",
        };
      }

      const docRef = doc(this.firestore, `users/${uid}/balance/current`);
      this.authStateService.customTokenReady$.pipe(
        take(1),
        switchMap(() => docData(docRef))
      ).subscribe((balance: any) => {
        this.userBalance = balance;
      });

      this.userSyndicate$.subscribe(
        (syndicateData) => {
          if (syndicateData) {
            return (this.userSyndicateList = syndicateData);
          }
        },
        (error) => {
          return error;
        }
      );

      // Initialize userData object
      this.userData = {
        title: "",
        name: "",
        email: "",
        customer_id: "",
        language: "",
        service_age: "",
        previous_login: "",
        bonus_credit_balance: "",
        reward_points_balance: "",
        cash_balance: "",
        lottery_participation: "",
        active_ongoing_syndicate: "",
        active_jackpot_trigger: "",
        pending_cashout: "",
        outstanding_check_winning: "",
        username: "",
        firstName: "",
        lastName: "",
        Registered_Since: "",
        phone: "",
        device: "",
        restriction: "",
        restriction_ttl: "",
      };

      const userData = await this.userDetails$?.pipe(takeUntil(this.destroy$), first()).toPromise();
      if (!userData) {
        return;
      }

      this.checkWinning = await new Promise<boolean>((resolve, reject) => {
        this.app_notification$.subscribe(
          (data: boolean) => resolve(data),
          (error) => reject(error)
        );
      });

      if (this.userSyndicateList && this.userSyndicateList?.length > 0) {
        const syndicateLod = this.userSyndicateList;
        const activeSyndicate = syndicateLod?.find((syndicate) => syndicate?.syndicate_status === "active");
        const OngoingSyndicate = syndicateLod?.find((syndicate) => syndicate?.syndicate_type === "Ongoing");

        this.ongoingSyndicate = activeSyndicate !== undefined && OngoingSyndicate !== undefined;
        this.syndicateLoadedData = activeSyndicate || null;
        this.activeSyndicateName = this.syndicateLoadedData ? this.syndicateLoadedData?.syndicate_name : null;
      }

      let device: DeviceInfo;
      if (Device) {
        try {
          device = await Device?.getInfo();
        } catch (error) {
          return error;
        }
      }

      if (userData) {
        this.preferredCurrency = userData?.preferred_currency ?? CommonVariable.preferredCurrency;

        this.getCurrencyList$.subscribe((res: Data) => {
          const CurrencyDetails = res?.details?.find((x: Data) => x?.currency_code === this.preferredCurrency);
          this.currencySymbol = CurrencyDetails?.currency_symbol ?? CommonVariable.defaultCurrencySymbol;
        });

        this.userData.title = userData?.title || "Mr";
        this.userData.email = userData?.email || "";
        this.userData.customer_id = uid || "";
        this.userData.bonus_credit_balance = this.currencySymbol + (this.userBalance?.credit || zero);
        this.userData.reward_points_balance = this.userBalance?.points || zero;
        this.userData.cash_balance = this.currencySymbol + (this.userBalance?.winning_balance || zero);
        this.userData.lottery_participation = this.activeSyndicateName;
        this.userData.active_ongoing_syndicate = this.ongoingSyndicate || false;
        this.userData.pending_cashout =
          this.userBalance?.withdraw_amount !== undefined && this.userBalance?.withdraw_amount !== null;
        this.userData.outstanding_check_winning = this.checkWinning ?? false;
        this.userData.username = uid || "";
        this.userData.firstName = userData?.name || "";
        this.userData.lastName = userData?.surname || "";
        this.userData.device = device?.platform || "Web";
        this.onBoardState = userData?.onboard_state || "null";
        this.pwaSegmentStatus = userData?.pwa_segment_status || "null";
        this.clarityService.syncAuthenticatedUser(uid, userData?.pwa_segment_status);
        this.userData.language = userData?.language_code || "en";
        this.userData.restriction = userData?.restriction ?? null;
        this.userData.restriction_ttl = userData?.restriction_ttl ? userData?.restriction_ttl?.toDate() : null;

        // ✅ Use correct uid for jackpot triggers
        const syndicateJackpotTriggers$ = this.getSyndicateJackpotTriggers(uid);
        syndicateJackpotTriggers$
          .pipe(
            takeUntil(this.destroy$),
            map((triggers: Data) => {
              const activeJackpotTrigger = triggers?.find((trigger: Data) => trigger?.status === true);
              this.active_jackpot = activeJackpotTrigger !== undefined;
              return this.active_jackpot?.toString();
            })
          )
          .subscribe(
            (activeJackpotValue) => (this.userData.active_jackpot_trigger = activeJackpotValue),
            (error) => error
          );

        let previous_login = "just now";
        if (userData?.previousLoginDatetime) {
          previous_login = this.calculateRegisteredTime(userData?.timestamp?.toDate());
        }
        this.userData.previous_login = previous_login;

        let protectedMsn = "";
        const phone = `+${userData.msn}`;
        if (phone !== "") {
          for (let digits = 0; digits < phone?.length - 3; digits++) protectedMsn += "*";
          for (let digits = phone?.length - 3; digits < phone?.length; digits++) protectedMsn += phone[digits];
        }
        this.userData.phone = protectedMsn;

        if (this.userData?.firstName && this.userData?.lastName) {
          this.userData.name = `${this.userData?.firstName} ${this.userData?.lastName}`;
        } else if (this.userData.firstName) {
          this.userData.name = this.userData?.firstName;
        } else if (this.userData.lastName) {
          this.userData.name = this.userData?.lastName;
        }
      }

      let Registered_Since = "just now";
      if (userData?.timestamp) {
        Registered_Since = this.calculateRegisteredTime(userData?.timestamp?.toDate());
      }
      this.userData.Registered_Since = Registered_Since;
      this.userData.service_age = Registered_Since;

      for (const key in this.userData) {
        const value = this.userData[key];
        if (value === undefined || value === "") delete this.userData[key];
      }

      this.getChatDetails().subscribe({
        next: (res: Data) => {
          if (!res || !Array.isArray(res) || res.length === 0) {
            // Handle empty or null response
            this.userData["Contacted support in last 7 days"] = "N/A";
            this.setChatlioDetails("N/A");
            return;
          }
          
          const chatArray = Array.isArray(res) ? res : [];
          const userChatData = chatArray.filter((item: Data) => item?.user?.name === this.userData?.firstName);
          const chatID = userChatData[0]?.id ?? "";
          const livechatInitTime = userData?.last_support_contacted
            ? userData.last_support_contacted.toDate()
            : "N/A";

          const createdAtMilliseconds = new Date(livechatInitTime).getTime();
          const sevenDaysAgoTimestamp = Date.now() - 7 * 24 * 60 * 60 * 1000;
          const contactedSupportField =
            createdAtMilliseconds >= sevenDaysAgoTimestamp
              ? new Date(createdAtMilliseconds).toLocaleString()
              : "N/A";

          this.userData["Contacted support in last 7 days"] = contactedSupportField;

          this.getChatDetailsChatID(chatID)?.subscribe({
            next: (res: any) => {
              if (!res || !res.messages) {
                this.setChatlioDetails("N/A");
                return;
              }
              const filteredValues =
                res?.messages?.flatMap((item) => (item?.responses?.[0]?.value ? [item.responses[0].value] : []))?.join(", ") ||
                "N/A";
              this.setChatlioDetails(filteredValues);
            },
            error: () => this.setChatlioDetails("N/A")
          });
        },
        error: (error) => {
          this.errorHandlerLogs('Error in getChatDetails subscription:', error);
          this.userData["Contacted support in last 7 days"] = "N/A";
          this.setChatlioDetails("N/A");
        }
      });

      return this.userData;
    } catch (error) {
      return error;
    }
  }


  getSyndicateJackpotTriggers(userId: string) {

    return this.catchService.isOnline$.pipe(takeUntil(this.destroy$),
      switchMap((isOnline) => {
        if (!isOnline) {
          return of(null); // Return null if offline
        }

        const jackpotTriggerCollection = collection(this.firestore, `/users/${userId}/jackpot_trigger`); // Reference collection
        return getDocs(jackpotTriggerCollection); // Fetch QuerySnapshot<DocumentData>
      }),
      map((snapshot: QuerySnapshot<DocumentData>) => {
        if (!snapshot) {
          return []; // Handle null snapshot
        }
        return this.firestoreDataServices.snapShotData(snapshot); // Pass QuerySnapshot directly to snapShotData
      }),
      shareReplay(1) // Cache results
    );
  }



  userSyndicate$ = this.userSyndicateRequest$.pipe(
    takeUntil(this.destroy$),
    switchMap((pageSize: number) =>
      this.commonService.getUid("authService").pipe(
        switchMap((userId) => {
          if (!userId || userId.length <= 5) {
            return of(null); // Return null if userId is invalid
          }

          return this.catchService.isOnline$.pipe(
            takeUntil(this.destroy$),
            switchMap(async (isOnline) => {
              if (!isOnline) {
                return []; // Return empty array if offline
              }

              const syndicateCollection = collection(
                this.firestore,
                `/users/${userId}/userSyndicate_list`
              );

              const syndicateQuery = query(
                syndicateCollection,
                where("hide", "==", false),
                orderBy("creation_date", "desc"),
                limit(pageSize)
              );

              const snapshot = await getDocs(syndicateQuery);
              const actions = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
              }));
              return actions;
            }),
            shareReplay(1)
          );
        })
      )
    ),
    shareReplay(1)
  );


  private calculateRegisteredTime(date) {
    const currentDate = new Date().getTime(); // Get current time in milliseconds
    const timestampDate = date.getTime(); // Convert the provided date to milliseconds

    const timeDifference = currentDate - timestampDate;

    const secondsInMinute = 60;
    const secondsInHour = secondsInMinute * 60;
    const secondsInDay = secondsInHour * 24;
    const secondsInMonth = secondsInDay * 30;
    const secondsInYear = secondsInDay * 365;

    const years = Math.floor(timeDifference / (1000 * secondsInYear));
    const months = Math.floor((timeDifference % (1000 * secondsInYear)) / (1000 * secondsInMonth));
    const days = Math.floor((timeDifference % (1000 * secondsInMonth)) / (1000 * secondsInDay));
    const hours = Math.floor((timeDifference % (1000 * secondsInDay)) / (1000 * secondsInHour));
    const minutes = Math.floor((timeDifference % (1000 * secondsInHour)) / (1000 * secondsInMinute));
    const seconds = Math.floor((timeDifference % (1000 * secondsInMinute)) / 1000);

    let result = "";

    if (years > 0) {
      result += years + (years === 1 ? " year " : " years ");
    }
    if (months > 0) {
      result += months + (months === 1 ? " month " : " months ");
    }
    if (days > 0) {
      result += days + (days === 1 ? " day " : " days ");
    }
    if (years === 0 && months === 0 && days === 0) {
      if (hours > 0) {
        result += hours + (hours === 1 ? " hour " : " hours ");
      }
      if (minutes > 0) {
        result += minutes + (minutes === 1 ? " minute " : " minutes ");
      }
      if (seconds > 0) {
        result += seconds + (seconds === 1 ? " second " : " seconds ");
      }
    }

    if (result === "") {
      result = "just now";
    } else {
      result += "ago";
    }

    return result;
  }

  ngOnDestroy() {
    // Signal all managed subscriptions to complete
    this.destroy$.next();
    this.destroy$.complete();

    // Clean up specific subscriptions
    if (this.userDetailsSubscription) {
      this.userDetailsSubscription.unsubscribe();
    }
    if (this.tokenValidaterSub) {
      this.tokenValidaterSub.unsubscribe();
    }
    if (this.updateMainPageSubscription) {
      this.updateMainPageSubscription.unsubscribe();
    }

    // Clean up token refresh mechanisms
    this.clearAllTokenRefreshMechanisms();
    this.tokenRefreshActive = false;

    // Clean up user disabled listener
    if (this.userDisabledUnsubscribe) {
      this.userDisabledUnsubscribe();
    }
  }

  // This method has been usde to clear user session
  public async clearUserSession(): Promise<void> {
    this.resetRegistrationIdentityCache();
    try {
      // ✅ Clear specific keys from sessionStorage
      Object.keys(sessionStorage).forEach(key => {
        try {
          sessionStorage.removeItem(key);
        } catch (e) {
          return e;
        }
      });

      // ✅ Clear specific keys from localStorage
      Object.keys(localStorage).forEach(key => {
        try {
          if (key !== 'acceptedAppVersion') {
            localStorage.removeItem(key);
          }
        } catch (e) {
          return e;
        }

      });

      // ✅ Clear in-memory caches (custom)
      this.clearCaches();

      // ✅ Unregister service workers safely
      await this.unregisterServiceWorkers();
    } catch (error) {
      return error;
    }
  }

  // This method has been usde to clear cache
  private async clearCaches(): Promise<void> {
    if (!("caches" in window)) {
      return;
    }

    try {
      const keys = await caches.keys();
      await Promise.all(
        keys.map(async (key) => {
          try {
            await caches.delete(key);
          } catch (err) {
            return err;
          }
        })
      );
    } catch (error) {
      return error;
    }
  }

  // This method has been usde to unregisterServiceWorkers
  private async unregisterServiceWorkers() {
    if ("serviceWorker" in navigator) {
      try {
        const registrations = await navigator?.serviceWorker?.getRegistrations();
        await Promise.all(
          registrations.map(
            (registration) => registration.unregister(),
            (err) => {
              return err;
            }
          )
        );
      } catch (error) {
        return error;
      }
    }
  }

  // update user grade based on sms verification status
  updateUserGrade(grade: string) {
    try {
      const userId = this.commonService.getUid("updateUserGrade");
      if (!userId) {
        throw new Error("User ID not found");
      }

      const payload = {
        user_id: userId,
        user_grade: grade,
      };

      return this.getV2CloudFunction("updateusergradev2", payload);
    } catch (error) {
      return error;
    }
  }

// This FN hasbeen used to check fingerprientJS
async fingerprintJS() {
  try {
    // Check if we already have a stored fingerprint from a previous manual login
    let visitorId = localStorage.getItem("fingerSalt");
    if (!visitorId) {
      // Generate new fingerprint only if not exists
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      visitorId = result.visitorId;
      CommonVariable.FingerprintJSResult = result;
      // Store for future use and as fallback
      localStorage.setItem("fingerSalt", visitorId);
    }

    return visitorId;
  } catch (error) {
    this.errorHandlerLogs('Fingerprint generation failed:', error);
    // Generate a fallback ID if FingerprintJS fails
    const fallbackId = `fallback-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem("fingerSalt", fallbackId);
    return fallbackId;
  }
}

  /**
   * The function `updateUserSocialTokenDetails` updates a user's social token details by making a
   * request to a user service and a cloud function.
   */
  updateUserSocialTokenDetails(user: Data, unFilteredData: Data) {
    const request = this.RequestUserService.updateUserSocialTokenDetails(user, unFilteredData);
    return this.getV2CloudFunction("postasynceventsv2", request);
  }

  // assign 2 free powerball lines upon activating push notifications
  freePowerballPushNotification() {
    try {
      this?.getV2CloudFunction(
        "postasynceventsv2",
        this.RequestProfileService?.freePowerballPushNotification()
      ).subscribe(null, (err) => {
        return err;
      });
    } catch (error) {
      return error;
    }
  }
  updateMainPages(payload: Data) {
    // Add retry attempt counter to payload if not present
    const payloadWithRetry = {
      ...payload,
      retryAttempt: (payload.retryAttempt || 0) + 1
    };

    // Check max retries
    if (payloadWithRetry.retryAttempt > 2) {
      return of({ success: false, message: 'Failed to update, using cached data' });
    }

    return this.getV2CloudFunction("updatemainpages", payloadWithRetry);
  }


  /**
   * Fetches chat details through secure Cloud Function proxy
   * @returns Observable of chat data
   */
  getChatDetails(): Observable<Data> {
    const currentTime = Number(Date.now());
    // Initialize Cloud Functions with correct region
    const functions = getFunctions(undefined, 'europe-west3');
    const getChatDetailsFunction = httpsCallable(functions, 'getchatdetails');
    return from(getChatDetailsFunction({
      timestamp: currentTime
    })).pipe(
      map((result: Data) => {
        if (result.data?.success && result.data?.data) {
          return result.data.data;
        }
        throw new Error('Invalid response from chat service');
      }),
      retry({
        count: 2,
        delay: 1000,
        resetOnSuccess: true,
      }),
      catchError((error) => {
        // Log error without sensitive information
        const errorMessage = this.getErrorMessage(error);
        return of([]); // Return empty array as Observable
      })
    );
  }

  /**
   * Extracts error message from Cloud Function error
   */
  private getErrorMessage(error: Data): string {
    if (error?.code === 'resource-exhausted') {
      return 'Rate limit exceeded';
    }
    if (error?.code === 'deadline-exceeded') {
      return 'Request timeout';
    }
    if (error?.code === 'unauthenticated') {
      return 'Authentication failed';
    }
    return error?.message || 'Unknown error';
  }


  /**
  * Fetches specific chat details through secure Cloud Function proxy
  * @param chatID The chat ID to fetch
  * @returns Observable of chat data
  */
  getChatDetailsChatID(chatID: string): Observable<any> {
    if (!chatID) {
      return of(null); // Return Observable with null instead of throwing error
    }
    const functions = getFunctions(undefined, 'europe-west3');
    const getChatDetailsFunction = httpsCallable(functions, 'getchatdetailsbyid');

    return from(getChatDetailsFunction({
      chatId: chatID
    })).pipe(
      map((result: Data) => result.data),
      catchError((error) => {
        return of(null); // Return Observable with null
      })
    );
  }

  async setLastLiveChatTemp(currentTime: Date): Promise<void> {
    try {
      const userId = await this.commonService.getUid("setLastLiveChatTemp");
      if (!userId) return;

      const liveChatInitTime = Timestamp.fromDate(currentTime);

      const path_id = "yzF1Ss5eiMqnh91zQMtv";
      const doc_data = { last_support_contacted: liveChatInitTime };
      const method = "update";

      await this.getV2CloudFunction("updatefirestoredoc", {
        path_id,
        doc_data,
        method,
      }).subscribe();
    } catch {
      // handle error
    }
  }

  // This method has been used to set Chatlio Details in local storage
  setChatlioDetails(issues: string) {
    const d = {
      userData: { ...this.userData, issue: issues },
      onBoardState: this.onBoardState,
      pwaSegmentStatus: this.pwaSegmentStatus,
    };
    localStorage.setItem("chatlioDetails", JSON.stringify(d));
  }

  public getUserPwaVersionData(userId: string) {
    return this.catchService.isOnline$.pipe(takeUntil(this.destroy$),
      switchMap((isOnline) => {
        if (!isOnline) return of(null); // Return null if offline

        const userDocRef = doc(this.firestore, `users/${userId}`); // Document reference
        return getDoc(userDocRef).then((snapshot) => of(snapshot.data())); // Fetch document snapshot
      }),
      catchError((error) => {
        this.catchService.catchOfflineError(of(error)); // Handle errors
        throw error;
      }),
      shareReplay(1), // Cache results
      map((data: any) => data) // Map snapshot data directly
    );
  }

  private checkAppUpdateOnLogin() {
    this.userManagementService.userRequestForHome$?.subscribe({
      next: (user) => {
        if (!user) {
          return;
        }
        const userId = user?.uid;
        const fingerprintId = CommonVariable.FingerprintJSConst
          ? CommonVariable.FingerprintJSConst
          : localStorage.getItem("fingerSalt");
        if (user && user?.uid) {
          const versionCheckPayload = {
            userId,
            fingerprintId,
          };
          this.getV2CloudFunction("appversioncheckv2", versionCheckPayload)?.subscribe({
            next: () => {
              this.getUserPwaVersionData(userId)?.subscribe({
                next: (pwaVersionData) => {
                  if (pwaVersionData?.sw_update_required) {
                    this.appUpdateOnLoginTrigger$.next(true);
                  }
                },
                error: (error) => {
                  return error;
                },
              });
            },
            error: (err) => {
              return err;
            },
          });
        }
      },
      error: (err) => {
        return err;
      },
    });
  }

  public async updateUserFingerprintRequest(appVersionId?: string) {
    try {
      const userId = await this.commonService.getUid("updateUserFingerprintRequest");
      if (!userId) {
        return;
      } 

      const ipInfo: Data = await firstValueFrom(this.userManagementService.getUserCountryBaseOnIP());
      const token = localStorage.getItem("user_auto_login_token");
      const fingerPrint = token ? null : await this.fingerprintJS();
      const payload = {
        userId: userId,
        fingerprintId: fingerPrint,
        userAgent: navigator?.userAgent,
        appVersion: appVersionId ?? null,
        userIP: ipInfo?.ip ?? null,
      };

      this.getV2CloudFunction("updateuserfingerprintv2", payload).subscribe();
    } catch {
      // handle error
    }
  }


  getIconUrl(iconUrl: string | undefined): string {
    const basePath = "assets/icon";
    if (!iconUrl) {
      return `${basePath}/bell_icon_with_red_dot.png`;
    }

    if (iconUrl.startsWith("http") || iconUrl.startsWith("assets/images")) {
      return iconUrl;
    }

    if (!iconUrl.startsWith(basePath)) {
      return `${basePath}${iconUrl}`;
    }

    return iconUrl;
  }


  callSigInSocial(socialData: Data) {
    try {
      if (!socialData) return;
      const request = this.RequestUserService.signInRequestForUsers(socialData);
      return this.getV2CloudFunction("callpartialregapiv2", request);
    } catch (err) {
      return err;
    }
  }

  socialLogin(value: Data, connectType?: string) {
    var customToken = { customToken: value, user: null };
    const providerLabel =
      connectType === "google.com"
        ? "Google"
        : connectType === "facebook.com"
          ? "Facebook"
          : connectType || "social";
    return new Promise((resolve, reject) => {
      of(customToken)
        .pipe(takeUntil(this.destroy$), this.tokenLogin)
        .pipe(takeUntil(this.destroy$), take(1))
        ?.subscribe(
          () => {
            this.loadingService.dismiss();
            resolve(true);
          },
          () => {
            this.loadingService.dismiss();
            this.getConstantsMsg("1152")
              .then((translatedMsg) => {
                let translatedMessage = translatedMsg?.message;
                if (translatedMessage?.line_1?.includes("{provider}")) {
                  translatedMessage = {
                    ...translatedMessage,
                    line_1: translatedMessage.line_1.replace(/\{provider\}/g, providerLabel),
                  };
                }
                const defaultMsg = {
                  message: {
                    line_1: `We couldn't log you in with ${providerLabel}. Try again.`,
                    line_2: "",
                  },
                  style: {
                    icon: "tick circle error.svg",
                    type: "Error",
                  },
                };
                this.showToastMessage(
                  translatedMessage ? { ...translatedMsg, message: translatedMessage } : translatedMsg,
                  defaultMsg
                );
              })
              .catch((error) => {
                return error;
              });
            reject(new Error('Operation failed'));
          }
        );
    });
  }

  getfeaturePopupDetails(name: string, preferredLanguage?: string) {
    try {
      if (!name) {
        return EMPTY;
      }
      return this.commonService.getUid("getfeaturePopupDetails").pipe(
        filter((userId) => !!userId),
        take(1),
        switchMap((userId) => {
          const language =
            preferredLanguage ||
            localStorage.getItem("language_code") ||
            "en";
          return this.getV2CloudFunction("getfeaturepopup", {
            userID: userId,
            preferredLanguage: language,
            featureName: name,
          });
        }),
        catchError(() => EMPTY)
      );
    } catch (err) {
      return EMPTY;
    }
  }

  //update log feature feedback
  public async updatelogfeaturefeedback(feedbackData: Data) {
    try {
      const userId = await firstValueFrom(this.commonService.getUid("updatelogfeaturefeedback"));
      if (!userId) {
        return;
      }

      const sessionId = sessionStorage.getItem("SessionId");
      const userAgent = navigator?.userAgent;

      const request = {
        userID: userId,
        featureName: feedbackData?.featureName,
        questionText: feedbackData?.questionText,
        feedbackType: feedbackData?.feedbackType,
        sessionid: sessionId,
        pageurl: window.location.href,
        useragent: userAgent,
      };

      this.getV2CloudFunction("logfeaturefeedback", request).subscribe({
        next: (res) => {
          const message = res?.message;
        },
        error: (error) => {
          this.errorHandlerLogs("[updateLogFeatureFeedback] Failed to log feature feedback:", error);
        }
      });
    } catch (error) {
      this.errorHandlerLogs("[updateLogFeatureFeedback] Setup failed while logging feature feedback:", error);
    }
  }

  //get Push Notification data
  getPushNotificationData() {
    return this.catchService.isOnline$.pipe(
      takeUntil(this.destroy$),
      switchMap((isOnline) => {
        if (!isOnline) {
          return EMPTY;
        }

        // Get userId from commonService as an Observable
        return this.commonService.getUid("getPushNotificationData").pipe(
          switchMap((userId) => {
            if (!userId) return EMPTY;

            const docRef = doc(this.firestore, `users/${userId}/inline_notification/push_notification`);
            // Defense-in-depth gate (LOTTOSOCIAL-PWA-1MEE)
            return this.authStateService.customTokenReady$.pipe(
              take(1),
              switchMap(() => docData(docRef).pipe(
                takeUntil(this.destroy$),
                this.catchService.retryWhen,
                this.catchService.catchOfflineError
              ))
            );
          })
        );
      }),
      shareReplay(1)
    );
  }

  /**
   * Select primary vs alternate push device arrays from the push_notification document.
   */
  getPushDeviceArrays(doc: Data = {}): {
    activatedKey: "activated_devices" | "alt_activated_devices";
    dismissedKey: "dismissed_devices" | "alt_dismissed_devices";
    activatedDevices: Data[];
    dismissedDevices: Data[];
  } {
    const pushPref =
      doc?.push_pref !== undefined ? doc.push_pref !== false : this.push_pref() !== false;
    if (pushPref) {
      return {
        activatedKey: "activated_devices",
        dismissedKey: "dismissed_devices",
        activatedDevices: doc?.activated_devices ?? [],
        dismissedDevices: doc?.dismissed_devices ?? [],
      };
    }
    return {
      activatedKey: "alt_activated_devices",
      dismissedKey: "alt_dismissed_devices",
      activatedDevices: doc?.alt_activated_devices ?? [],
      dismissedDevices: doc?.alt_dismissed_devices ?? [],
    };
  }

  /**
   * Resolve push_pref from the push_notification document (not the in-memory default).
   * Keeps authService.push_pref in sync when the doc is available.
   */
  private async resolvePushNotificationDoc(): Promise<Data | null> {
    try {
      const pushDoc = await firstValueFrom(
        this.getPushNotificationData().pipe(
          take(1),
          timeout({ each: 3000 }),
          catchError(() => of(null as Data | null))
        )
      ) as Data | null;
      if (pushDoc?.push_pref !== undefined) {
        this.push_pref.set(pushDoc.push_pref ?? true);
      }
      return pushDoc ?? null;
    } catch {
      return null;
    }
  }

  async updatePushNotificationData(
    oldActivatedData: any,
    updatedDismissedData: Data,
    updatedActivatedData: Data | null,
    notificationID: string
  ) {
    try {
      const userId = await firstValueFrom(
        this.commonService.getUid("updatePushNotificationData").pipe(take(1))
      );
      if (!userId) {
        return;
      }

      // Backend rebuild matches dismissal by users/{uid}.current_session_id.
      // Sync before writing so restored tabs / late SessionId creation cannot miss.
      if (notificationID === "push_notification" && updatedDismissedData?.dismissed === true) {
        await this.setCurrentSessionId();
      }

      const pushDoc = await this.resolvePushNotificationDoc();
      const { activatedKey, dismissedKey } = this.getPushDeviceArrays(pushDoc ?? {});

      const dismissedDevicesData = [updatedDismissedData];

      // Dismiss must only touch dismissed arrays — never write activated:false
      const payload: Data = notificationID === "push_notification"
        ? { [dismissedKey]: dismissedDevicesData }
        : { dismissed: updatedDismissedData.dismissed };

      if (updatedActivatedData) {
        payload[activatedKey] = this.updateDeviceArray(
          oldActivatedData,
          updatedActivatedData,
          "fingerprintId"
        );
      }

      const path_id = "4TvDXD3XcUOtKEevVFFH";
      const doc_data = payload;
      const method = "update";

      this.getV2CloudFunction("updatefirestoredoc", {
        path_id,
        doc_data,
        method,
      }).subscribe({
        error: () => {
          // handle error
        }
      });
    } catch  {
      // handle error
    }
  }

  // Filtter the fringerint data according to fringerprintID
  updateDeviceArray<T extends Record<string, Data>>(
    oldData: T[] = [], updatedData: T, matchKey: keyof T): T[] {
    const exists = oldData.some(item => item[matchKey] === updatedData[matchKey]);
    if (exists) {
      return oldData.map(item =>
        item[matchKey] === updatedData[matchKey] ? { ...item, ...updatedData } : item
      );
    }
    return [...oldData, updatedData];
  }
  // This method is used to set Current Session Id
  async setCurrentSessionId(): Promise<void> {
    try {
      const sessionId = sessionStorage.getItem("SessionId");
      if (!sessionId) return;

      const userId = await firstValueFrom(
        this.commonService.getUid("setCurrentSessionId").pipe(take(1))
      );
      if (!userId) {
        return;
      }

      if (!CommonVariable.device?.operatingSystem) {
        try {
          await this.getDeviceInfo();
        } catch {
          // Keep empty platform if device info is unavailable.
        }
      }

      const last_login_platform = resolveOperatingSystem(CommonVariable.device?.operatingSystem);
      const last_login_display_mode = UtilityService.isAppInstalledOnHomePage()
        ? "standalone"
        : "browser";

      let bumpLoginDatetime = false;
      let previousLoginDatetime: Date | undefined;
      try {
        const userSnap = await getDoc(doc(this.firestore, `users/${userId}`));
        const userData = userSnap.exists() ? (userSnap.data() as Data) : null;
        bumpLoginDatetime = didA2hsLoginContextChange(
          userData?.last_login_platform,
          userData?.last_login_display_mode,
          last_login_platform,
          last_login_display_mode
        );
        if (bumpLoginDatetime && userData?.last_login_datetime) {
          const ts = userData.last_login_datetime;
          previousLoginDatetime =
            typeof ts?.toDate === "function"
              ? ts.toDate()
              : typeof ts?.seconds === "number"
                ? new Date(ts.seconds * 1000 + (ts.nanoseconds || 0) / 1000000)
                : undefined;
        }
      } catch {
        // If the user doc cannot be read, still write session + platform fields.
      }

      const path_id = "yzF1Ss5eiMqnh91zQMtv";
      const doc_data: Data = {
        current_session_id: sessionId,
        last_login_platform,
        last_login_display_mode,
        ...(bumpLoginDatetime
          ? {
              last_login_datetime: new Date(),
              ...(previousLoginDatetime ? { previous_login_datetime: previousLoginDatetime } : {}),
              language_code: this.selectedlanguage$?.value,
            }
          : {}),
      };
      const method = "update";

      await firstValueFrom(
        this.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          method,
        }).pipe(
          take(1),
          timeout({ each: 5000 }),
          catchError(() => of(null))
        )
      );
    } catch  {
      // handle error
    }
  }


  // This method is used to set Dismissed Push Notification
  async updateDismissedPushNotification(): Promise<void> {
    try {
      const userId = await firstValueFrom(
        this.commonService.getUid("updateDismissedPushNotification").pipe(take(1))
      );
      const sessionId = sessionStorage.getItem("SessionId");

      if (!userId || !sessionId) {
        return;
      }

      const pushDoc = await this.resolvePushNotificationDoc();
      const { dismissedKey } = this.getPushDeviceArrays(pushDoc ?? {});

      const payload = {
        dismissed: false,
        session_id: sessionId,
      };

      const dataArray = [payload];

      const path_id = "4TvDXD3XcUOtKEevVFFH";
      const doc_data = { [dismissedKey]: dataArray };
      const method = "update";

      this.getV2CloudFunction("updatefirestoredoc", {
        path_id,
        doc_data,
        method,
      }).subscribe();
    } catch  {
      // handle error
    }
  }

  // Save user IP
  saveIpIfNotExists(userIP: string, ipData: any): Observable<any> {
    if (!userIP) return of(null);

    const docRef = doc(this.firestore, `ip_address_details/${userIP}`);

    return from(getDoc(docRef)).pipe(
      takeUntil(this.destroy$),
      switchMap((docSnap) => {
        if (docSnap.exists()) {
          return of(docSnap.data());
        } else {
          const newData = { ...ipData, createdAt: new Date() };
          const path_id = "IJmAsqQCEr0D8WnuSnYy";
          const dv = { docId: userIP };
          const doc_data = newData;
          const method = "update";

          return this.getV2CloudFunction("updatefirestoredoc", {
            path_id,
            dv,
            doc_data,
            method,
          }).pipe(
            map(() => newData), // return newData when CF succeeds
            catchError((err) => {
              return throwError(() => err);
            })
          );
        }
      })
    );
  }
  // This method is used to update post_request_repeat_sec

  postRequestRepeatSecUpdate() {
    return this.getUid$("AccountRepositoryService").pipe(
      switchMap((userId) => {
        if (!userId) return of(null);

        const postReqRepSec = { post_request_repeat_sec: 5 };

        return this.getV2CloudFunction("updatefirestoredoc", {
          path_id: "NRBYKuqQ00ig7zMiMCNq",
          doc_data: postReqRepSec,
          method: "update",
        }).pipe(
          catchError(() => {
            return of(null);
          })
        );
      })
    );
  }

  // This method is used to log error messages
  errorHandlerLogs(message: string, error?: Data): { message: string; timestamp: string; error: Data } {
    const log = {
      message,
      timestamp: new Date().toISOString(),
      error: error ? this.formatError(error) : null,
    };
    return log;


  }

  /**
   * Whitelist-only fields for registration stage timing (no raw error objects / PII payloads).
   */
  private sanitizeRegistrationTimingExtra(extra?: Data): Record<string, unknown> | undefined {
    if (!extra || typeof extra !== "object") {
      return undefined;
    }
    const safe: Record<string, unknown> = {};
    const allowKeys = [
      "status",
      "hasCustomerId",
      "success",
      "hasCustomToken",
      "timeoutStage",
      "waitMs",
      "resultType",
      "message",
      "errorCode",
      "errorName",
      "httpStatus",
    ] as const;
    for (const key of allowKeys) {
      const val = (extra as Data)[key];
      if (val === undefined || val === null) {
        continue;
      }
      if (typeof val === "string") {
        safe[key] = val.slice(0, 200);
      } else if (typeof val === "number" || typeof val === "boolean") {
        safe[key] = val;
      }
    }
    const err = (extra as Data).error ?? (extra as Data).originalError;
    if (err !== undefined && err !== null) {
      if (err instanceof Error) {
        safe.errorName = err.name;
        const code = (err as Data)?.code;
        if (typeof code === "string" || typeof code === "number") {
          safe.errorCode = code;
        }
        if (typeof err.message === "string" && safe.message === undefined) {
          safe.message = err.message.slice(0, 200);
        }
      } else if (typeof err === "object") {
        const e = err as Data;
        if (typeof e.code === "string" || typeof e.code === "number") {
          safe.errorCode = e.code;
        }
        if (typeof e.name === "string") {
          safe.errorName = e.name;
        }
        if (typeof e.status === "number") {
          safe.httpStatus = e.status;
        }
        if (typeof e.message === "string" && safe.message === undefined) {
          safe.message = e.message.slice(0, 200);
        }
      } else if (typeof err === "string" && safe.message === undefined) {
        safe.message = err.slice(0, 200);
      }
    }
    return Object.keys(safe).length ? safe : undefined;
  }

  /**
   * Structured stage timing for sales new-registration (no PII).
   * Uses errorHandlerLogs — same pattern as reportAuthGateTimeout.
   * Pass timingCtx when logging after validateAndRegisterUser finalize may have cleared regFlowTiming.
   */
  private reportRegistrationTiming(
    stage: string,
    extra?: Data,
    timingCtx?: { correlationId: string; prosubId: string; startedAt: number } | null
  ): void {
    try {
      const ctx = timingCtx !== undefined ? timingCtx : this.regFlowTiming;
      const sanitized = this.sanitizeRegistrationTimingExtra(extra);
      this.errorHandlerLogs("[registration] stage timing", {
        stage,
        correlationId: ctx?.correlationId ?? "unknown",
        prosub_id: ctx?.prosubId ?? "",
        elapsedMs: ctx ? Date.now() - ctx.startedAt : undefined,
        ...(sanitized ? { extra: sanitized } : {}),
      });
    } catch {
      // Never let timing break registration.
    }
  }

  // This method is used to format error messages
  private formatError(error: Data): Data {
    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
        stack: error.stack,
      };
    }
    return error;
  }

  // Auth service
  async displayPromo(actionType: string, promoName: string) {
    try {
      if (!promoName) return;

      // Convert Observable to Promise
      const userId = await firstValueFrom(this.commonService.getUid("displayPromo"));
      if (!userId) {
        return;
      }

      switch (actionType) {
        case 'seen':
          this.promotionCfCall(userId, promoName, 'logpromoseenv2');
          break;
        case 'click':
          this.promotionCfCall(userId, promoName, 'logpromoclickv2');
          break;
        case 'conversion':
          this.promotionCfCall(userId, promoName, 'logpromoconversionv2');
          break;
        default:
          break;
      }
    } catch  {
      // handle error
    }
  }



  promotionCfCall(userId: string, promoName: string, cfName: string) {
    this.getV2CloudFunction(cfName, { userId: userId, promoName: promoName }).subscribe();
  }

  private get translate(): TranslateService {
    return this.injector.get(TranslateService);
  }

  async trackFeatureIfNotExists(feature: string) {
    const userId = await firstValueFrom(this.commonService.getUid("updateFeatureNode"));
      if (!userId) {
        return;
      }
  
    const path_id = "yzF1Ss5eiMqnh91zQMtv";
  
    this.getV2CloudFunction("updatefirestoredoc", {
      path_id,
      doc_data: {
        feature_used: {
          open_inbox: {
            last_used: new Date(),
          },
        },
      },
      method: "update"
    }).subscribe({
      next: (res) => {
        console.warn(`${feature} tracked successfully`, res);
        this.errorHandlerLogs(`${feature} tracked successfully`, res)
      },
      error: (error) => {
        this.errorHandlerLogs(`Error tracking ${feature}:`, error)
      }
    });
  }

  getTooltipData() {
    return this.commonService.getUid('getTooltipData').pipe(
  
      switchMap((userId) => {
  
        if (!userId) {
          return of(null);
        }
  
        const docRef = doc(
          this.firestore,
          `users/${userId}/in_app_notification/status`
        );
  
        return from(getDoc(docRef)).pipe(
  
          map(snapshot => {
            if (!snapshot.exists()) {
              return null;
            }
  
            const data: Data = snapshot.data();
            return data?.checklist_tooltip || null;
          }),
  
          catchError((err) => {
            this.errorHandlerLogs('getTooltipData error', err);
            return of(null);
          })
  
        );
      }),
  
      catchError((err) => {
        this.errorHandlerLogs('getTooltipData UID error', err);
        return of(null);
      })
    );
  }

  updateTooltip(docData: Data) {

    return this.getV2CloudFunction("updatefirestoredoc", {
      path_id: "KdpTQsnyjKvgqvjItVvT",
      doc_data: {
        checklist_tooltip: docData
      },
      method: "update"
    }).pipe(
  
      tap(() => {
        this.errorHandlerLogs('[updateTooltip] Tooltip updated successfully');
      }),
  
      catchError((err) => {
        this.errorHandlerLogs('updateTooltip error', err);
        return EMPTY;
      })
  
    );
  }

  updateChecklistTooltip(checklistArr: Data[], userId?) {
    const checklistStartTime =  Date.now();
    if (!checklistArr?.length) {
      return;
    }
  
    try {
  
      const completed = checklistArr.filter(i => i?.status).length;
      const remaining = checklistArr.length - completed;
  
      const message = remaining == 0 ? 0 : `${remaining} ${this.translate.instant('tabTranslate.remaining')}`;
      if(remaining == 0) {
        this.checkListEvent(
          'checklist_completed',
          userId,
          checklistStartTime,
          completed
        );
      } else {
        this.checkListEvent(
          'checklist_progress_updated',
          userId,
          checklistStartTime,
          completed
        );
        this.checkListEvent(
          'checklist_step_completed',
          userId,
          checklistStartTime,
          completed
        );
      }
      this.updateTooltip({ tooltip: message }).subscribe({
        error: (err) => {
          this.errorHandlerLogs('updateChecklistTooltip update failed', err);
        }
      });
  
    } catch (err) {
      this.errorHandlerLogs('updateChecklistTooltip error', err);
  
    }
  
  }
  checkListEvent(eventName: string, userId: string, checklistStartTime: number, stepsCompleted: number) {
    const totalSteps = 4;
    const completionTime = Date.now();
    const timeToCompletion = completionTime - checklistStartTime;
  
    const progress = Math.round((stepsCompleted / totalSteps) * 100);
  
    this.mixpanelService.checkListEvent(eventName, {
      user_id: userId,
      checklist_state: progress === 100 ? 'completed' : 'in_progress',
      steps_completed: stepsCompleted,
      progress_percentage: progress + "%",
      time_to_completion: timeToCompletion,
      session_count: this.sessionCount
    });
  
  }
  resetTooltipForNewSession() {

    this.getTooltipData()
      .pipe(take(1))
      .subscribe({
  
        next: (tooltip) => {
  
          if (!tooltip) {
            return;
          }
  
          try {
  
            const now = new Date();
  
            const hasRemainingChecklist = parseInt(
              tooltip.tooltip?.match(/\d+/)?.[0] || '0',
              10
            );
  
            const isEligible =
              now < tooltip.expiry_date?.toDate() &&
              hasRemainingChecklist > 0;
  
            if (isEligible) {
  
              this.updateTooltip({
                display_count: 0,
                status: true
              }).subscribe({
                error: (err) => {
                  this.errorHandlerLogs('resetTooltipupdate failed', err);
                }
              });
  
            }
  
          } catch (err) {
            this.errorHandlerLogs('resetTooltip processing error', err);
  
          }
  
        },
  
        error: (err) => {
          this.errorHandlerLogs('resetTooltip fetch error', err);
        }
  
      });
  
  }

}
